import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, ContextMenuControl, DialogControl, FieldControl, FormControl, IconControl, PanelControl, SidePanelFormControl } from "spark-core-dx/components";
import { useOperationDialog } from "spark-core-dx/hooks";
import { SystemTypes } from "spark-core-dx/services";
import { Operations } from "../../../services/ItemServices";
import { dispatchAiPanelCommand } from "../../../components/AIChat/aiPanelCommand";
import CardGridControl from "../components/CardGridControl";
import { ResourceCatalogListControl, ResourceCatalogListTab, ResourceCatalogPanel, ResourceCatalogRow } from "../components/ResourceCatalogPanel";
import ResourceStateControl from "../components/ResourceStateControl";
import EndpointAuthenticationConfiguratorStep from "../components/wizard/steps/EndpointAuthenticationConfiguratorStep";
import {
  buildEndpointEntityRouteCatalog,
  getEndpointEntityCardTitle,
  resolveClientShortName,
  resolveClientShortNameAsync
} from "../components/wizard/steps/EndpointScopeConfiguratorStep";
import { useResourceDomainContext } from "../ResourceDomainProvider";
import {
  DESTINATION_RESOURCE_DEFAULTS,
  getArray,
  getBoolean,
  getDeploymentDisplayName,
  getDeploymentId,
  getDeploymentRenderKey,
  getDeploymentRootName,
  getDeploymentTargetDescription,
  getDeploymentTargetDisplayName,
  getDeploymentTargetValue,
  getDestinationActiveSchemaVersionId,
  getDestinationConnectionSummary,
  getDestinationDeployments,
  getDestinationEntityDeployments,
  getEndpointDeploymentSchemaScopes,
  getDestinationSchemaId,
  getDestinationStateLabel,
  getDisplayName,
  getEntityDeploymentMatchKey,
  getEntityDeploymentRenderKey,
  getFilePackagingModeLabel,
  getId,
  getSchemaDestinationStatusLabel,
  getSchemaVersionLabel,
  getString,
  getValue,
  isSchemaDestinationConfigEditable,
  isSchemaDestinationOnline,
  isRecordActive,
  normalizeCompare
} from "./resourceWorkspace.destinationUtils";
import {
  buildSchemaScopeDraft,
  getSchemaScopeRowKey,
  normalizeSchemaScopeRows,
  stripSchemaScopeUiState,
  validateSchemaScopeRows
} from "./ResourceWorkspaceDestinationPanels";

const SOURCE_CATALOG_STATUS_OPTIONS = [
  { Text: "Active", Value: "active" },
  { Text: "Inactive", Value: "inactive" },
  { Text: "Both", Value: "both" }
];

const SENSITIVE_CONNECTION_FIELD_PATTERN = /(password|passwd|pwd|secret|token|credential|private|clientsecret|apikey|api key|accesskey|access key|auth|authorization)$/i;

const DIRECT_DEPLOYMENT_CONNECTION_FIELDS = [
  "Protocol",
  "Host",
  "Server",
  "Port",
  "Url",
  "BaseUrl",
  "Path",
  "RootPath",
  "RootContainer",
  "RootFolder",
  "Folder",
  "Directory",
  "Container",
  "ContainerName",
  "Share",
  "ShareName",
  "Bucket",
  "BucketName",
  "DBName",
  "DatabaseName",
  "TargetName",
  "Schema",
  "Tenant",
  "Company",
  "User",
  "UserName",
  "Username",
  "Login",
  "UseInheritedConnectionString"
];

const ENDPOINT_AUTH_MODE_PAT = 1;
const ENDPOINT_AUTH_MODE_ANONYMOUS = 3;
const ENDPOINT_AUTH_MODE_BASIC = 4;
const ENDPOINT_AUTH_MODE_PAT_AND_BASIC = 5;

export const EndpointRouteIcon = () => (
  <span className="material-symbols-rounded resource-workspace-endpoint-route-icon" aria-hidden="true">api</span>
);

const getEndpointSurfaceLabel = (deployment) => {
  const mode = Number(getString(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  if (mode === 1) return "Hosted API";
  if (mode === 2) return "MCP";
  if (mode === 3) return "Hosted API + MCP";
  return "Surface disabled";
};

const getEndpointTypeLabel = (deployments = []) => {
  let hasApiSurface = false;
  let hasMcpSurface = false;

  deployments.forEach((deployment) => {
    const mode = Number(getString(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
    if (mode === 1 || mode === 3) hasApiSurface = true;
    if (mode === 2 || mode === 3) hasMcpSurface = true;
    if (!mode) {
      hasApiSurface = hasApiSurface || getBoolean(deployment, "HostedApiEnabled", "hostedApiEnabled");
      hasMcpSurface = hasMcpSurface
        || getBoolean(deployment, "McpEnabled", "mcpEnabled")
        || Boolean(getString(deployment, "McpConnectorUrl", "mcpConnectorUrl"));
    }
  });

  if (hasApiSurface) return "API Endpoint";
  if (hasMcpSurface) return "MCP Endpoint";
  return "Endpoint";
};

const buildWarehouseDeploymentDisplayName = (deployment) => {
  const warehouseName = getString(deployment, "SchemaDestinationName", "schemaDestinationName", "WarehouseName", "warehouseName");
  const targetName = getString(deployment, "DBName", "dbName", "SystemName", "systemName");
  const version = getString(deployment, "CurrentVersion", "currentVersion", "CurrentSchemaVersion", "currentSchemaVersion");
  if (warehouseName && targetName && normalizeCompare(warehouseName) !== normalizeCompare(targetName)) {
    return `${warehouseName} - ${targetName}${version ? ` v${version}` : ""}`;
  }
  return getDisplayName(deployment)
    || targetName
    || warehouseName
    || getString(deployment, "DeploymentName", "deploymentName");
};

const getEndpointWarehouseDeploymentDisplayName = (deployment, destinationState) => {
  const explicitDisplayName = getString(
    deployment,
    "WarehouseDeploymentDisplayName",
    "warehouseDeploymentDisplayName",
    "WarehouseDeploymentName",
    "warehouseDeploymentName"
  );
  if (explicitDisplayName) return explicitDisplayName;

  const warehouseDeploymentId = getString(deployment, "WarehouseDeploymentId", "warehouseDeploymentId", "wdId", "wdid");
  if (!warehouseDeploymentId) return "";

  const warehouseId = getString(deployment, "WarehouseId", "warehouseId", "wId", "wid");
  const warehouses = [
    ...getArray(destinationState, "warehouseDestinations", "warehouses"),
    ...getArray(destinationState?.destination, "WarehouseDestinations", "warehouseDestinations")
  ].filter(Boolean);
  const warehouse = warehouses.find((candidate) =>
    !warehouseId || normalizeCompare(getId(candidate)) === normalizeCompare(warehouseId)
  );
  const warehouseDeployment = getDestinationDeployments(warehouse, DESTINATION_RESOURCE_DEFAULTS.warehouse)
    .find((candidate) => normalizeCompare(getDeploymentId(candidate)) === normalizeCompare(warehouseDeploymentId));

  return warehouseDeployment ? buildWarehouseDeploymentDisplayName(warehouseDeployment) : "";
};

const isMcpOnlyEndpointDeployment = (deployment) => {
  const mode = Number(getString(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  if (mode === 2) return true;
  if (mode === 1 || mode === 3) return false;

  const mcpEnabled = getBoolean(deployment, "McpEnabled", "mcpEnabled");
  const hostedApiEnabled = getBoolean(deployment, "HostedApiEnabled", "hostedApiEnabled");
  return mcpEnabled && !hostedApiEnabled;
};

const hasMcpOnlyEndpointSurface = (deployments = []) =>
  deployments.some(isMcpOnlyEndpointDeployment);

const buildSelectListField = (fieldName, displayName, options, placeHolder = "Please Select", ariaLabel = "") => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SelectList,
  DataJson: options,
  PlaceHolder: placeHolder,
  AriaLabel: ariaLabel,
  ariaLabel
});

const buildFilterTextBoxField = (fieldName, placeHolder, ariaLabel) => ({
  FieldName: fieldName,
  DisplayName: "",
  FieldType: SystemTypes.UIFieldType.TextBox,
  PlaceHolder: placeHolder,
  AriaLabel: ariaLabel,
  ariaLabel
});

const matchesStatusFilter = (item, statusFilter) => {
  if (statusFilter === "both") return true;
  const active = isRecordActive(item);
  return statusFilter === "inactive" ? !active : active;
};

const Fact = ({ label, value }) => (
  <div className="resource-workspace-fact">
    <dt>{label}</dt>
    <dd>{value || "Not set"}</dd>
  </div>
);

const getEndpointDeploymentAuthLabel = (deployment) => {
  const authMode = Number(getValue(deployment, "ApiAuthenticationMode", "apiAuthenticationMode")) || ENDPOINT_AUTH_MODE_PAT;
  if (authMode === ENDPOINT_AUTH_MODE_PAT_AND_BASIC) return "PAT + Basic";
  if (authMode === ENDPOINT_AUTH_MODE_BASIC) return "Basic client supported";
  if (authMode === ENDPOINT_AUTH_MODE_ANONYMOUS) return "Anonymous";
  return "PAT bearer supported";
};

const getEndpointAuthSummary = (deployments = []) => {
  const relevantDeployments = deployments.filter(isRecordActive);
  const sourceDeployments = relevantDeployments.length ? relevantDeployments : deployments;
  const labels = [...new Set(sourceDeployments.map(getEndpointDeploymentAuthLabel).filter(Boolean))];
  if (!labels.length) return "Not configured";
  if (labels.length === 1) return labels[0];
  if (labels.includes("PAT + Basic")) return "PAT + Basic";
  return "Mixed auth modes";
};

const getMcpAuthorizationLabel = (deployment) => {
  const mode = Number(getValue(deployment, "McpAuthorizationMode", "mcpAuthorizationMode")) || 1;
  return mode === 2 ? "All authenticated users" : "Selected profiles";
};

const getMcpAuthorizedProfileCount = (deployment) =>
  getArray(deployment, "McpAuthorizedProfiles", "mcpAuthorizedProfiles")
    .filter((profile) => getValue(profile, "IsDeleted", "isDeleted") !== true && getValue(profile, "IsActive", "isActive") !== false)
    .length;

const getEndpointMcpAccessSummary = (deployments = []) => {
  const mcpDeployments = deployments
    .filter((deployment) => [2, 3].includes(Number(getValue(deployment, "PublicSurfaceMode", "publicSurfaceMode"))));
  if (!mcpDeployments.length) return "Disabled";
  const labels = [...new Set(mcpDeployments.map(getMcpAuthorizationLabel))];
  return labels.length === 1 ? labels[0] : "Mixed MCP access";
};

const formatDisplayLabel = (value) =>
  String(value ?? "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

const getCaseInsensitiveObjectValue = (item, key) => {
  if (!item || typeof item !== "object" || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(item, key)) return item[key];

  const normalizedKey = normalizeCompare(key);
  const matchingKey = Object.keys(item).find((itemKey) => normalizeCompare(itemKey) === normalizedKey);
  return matchingKey ? item[matchingKey] : undefined;
};

const unwrapConnectionFieldValue = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const directValue = getCaseInsensitiveObjectValue(value, "Value");
  if (directValue !== undefined) return directValue;
  const textValue = getCaseInsensitiveObjectValue(value, "Text");
  if (textValue !== undefined) return textValue;
  return value;
};

const isDisplayableConnectionField = (fieldName, value) => {
  const normalizedFieldName = normalizeCompare(fieldName);
  if (!normalizedFieldName || normalizedFieldName.includes("connectionstring")) return false;
  if (normalizedFieldName.startsWith("__")) return false;
  if (normalizedFieldName.includes("securecustomfields")) return false;
  if (normalizedFieldName.endsWith("id") || normalizedFieldName === "_id") return false;
  if (SENSITIVE_CONNECTION_FIELD_PATTERN.test(String(fieldName || ""))) return false;
  const unwrappedValue = unwrapConnectionFieldValue(value);
  if (unwrappedValue === undefined || unwrappedValue === null) return false;
  if (typeof unwrappedValue === "string" && !unwrappedValue.trim()) return false;
  if (typeof unwrappedValue === "object") return false;
  return true;
};

const sanitizeConnectionFieldValue = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return text;

  try {
    const url = new URL(text);
    if (url.password) {
      url.password = "";
      return url.toString();
    }
  } catch {
    // Fall through to lightweight key/value redaction.
  }

  return text.replace(/\b(password|passwd|pwd)\s*=\s*([^;\n&\s]+)/gi, "$1=********");
};

const formatConnectionFieldValue = (value) => {
  const unwrappedValue = unwrapConnectionFieldValue(value);
  if (typeof unwrappedValue === "boolean") return unwrappedValue ? "Yes" : "No";
  return sanitizeConnectionFieldValue(unwrappedValue);
};

const parseConnectionStringDetailRows = (connectionString) => {
  const source = String(connectionString || "").trim();
  if (!source) return [];

  try {
    const parsed = JSON.parse(source);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.entries(parsed)
        .filter(([key, value]) => isDisplayableConnectionField(key, value))
        .map(([key, value]) => ({ label: formatDisplayLabel(key), value: formatConnectionFieldValue(value) }));
    }
  } catch {
    // Fall back to common connection-string formats below.
  }

  try {
    const url = new URL(source);
    const searchRows = Array.from(url.searchParams.entries())
      .filter(([key, value]) => isDisplayableConnectionField(key, value))
      .map(([key, value]) => ({ label: formatDisplayLabel(key), value }));
    return [
      { label: "Protocol", value: url.protocol.replace(":", "") },
      url.username ? { label: "Username", value: decodeURIComponent(url.username) } : null,
      { label: "Host", value: url.hostname },
      url.port ? { label: "Port", value: url.port } : null,
      url.pathname && url.pathname !== "/" ? { label: "Path", value: decodeURIComponent(url.pathname.replace(/^\/+/, "")) } : null,
      ...searchRows
    ].filter((row) => row && isDisplayableConnectionField(row.label, row.value));
  } catch {
    // Fall back to key/value parsing below.
  }

  return source
    .split(/[;\n&]/)
    .map((part) => part.trim())
    .filter((part) => part.includes("="))
    .map((part) => {
      const separatorIndex = part.indexOf("=");
      return {
        key: part.slice(0, separatorIndex).trim(),
        value: part.slice(separatorIndex + 1).trim()
      };
    })
    .filter(({ key, value }) => isDisplayableConnectionField(key, value))
    .map(({ key, value }) => ({ label: formatDisplayLabel(key), value }));
};

const getDeploymentEffectiveConnectionString = (deployment, destination) => {
  const deploymentConnectionString = getString(deployment, "ConnectionString", "connectionString");
  const defaultConnectionString = getString(destination, "DefaultConnectionString", "defaultConnectionString", "ConnectionString", "connectionString");
  const inheritConnectionString = getBoolean(deployment, "UseInheritedConnectionString", "useInheritedConnectionString")
    || !deploymentConnectionString
    || (!!defaultConnectionString && deploymentConnectionString === defaultConnectionString);
  return inheritConnectionString ? defaultConnectionString : deploymentConnectionString;
};

const getDeploymentConnectionDetailRows = (deployment, destination) => {
  const rows = [];
  const seenLabels = new Set();
  const addRow = (label, value) => {
    if (!isDisplayableConnectionField(label, value)) return;
    const formattedLabel = formatDisplayLabel(label);
    const normalizedLabel = normalizeCompare(formattedLabel);
    if (!formattedLabel || seenLabels.has(normalizedLabel)) return;
    rows.push({ label: formattedLabel, value: formatConnectionFieldValue(value) });
    seenLabels.add(normalizedLabel);
  };

  DIRECT_DEPLOYMENT_CONNECTION_FIELDS
    .forEach((fieldName) => addRow(fieldName, getCaseInsensitiveObjectValue(deployment, fieldName)));

  Object.entries(getValue(deployment, "CustomFields", "customFields") || {})
    .forEach(([key, value]) => addRow(key, value));

  parseConnectionStringDetailRows(getDeploymentEffectiveConnectionString(deployment, destination))
    .forEach((row) => addRow(row.label, row.value));

  return rows;
};

const formatConnectionStringKey = (label) =>
  String(label || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const buildConnectionStringClipboardValue = (rows) => {
  const parts = rows
    .map((row) => {
      const key = formatConnectionStringKey(row.label);
      const value = String(row.value ?? "").trim();
      return key && value ? `${key}=${value}` : "";
    })
    .filter(Boolean);

  if (parts.length === 0) return "";

  parts.push("password=<password>");
  return parts.join(";");
};

const copyTextToClipboard = async (text) => {
  if (!text) return;
  const fallbackCopy = () => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(textArea);
    if (!copied) {
      throw new Error("Copy command was blocked.");
    }
  };

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      fallbackCopy();
      return;
    }
  }

  fallbackCopy();
};

const DestinationDeploymentConnectionDetails = ({ deployment, destination, canCheckConnection = false, onCheckConnection }) => {
  const connectionDetailRows = getDeploymentConnectionDetailRows(deployment, destination);
  const connectionStringClipboardValue = buildConnectionStringClipboardValue(connectionDetailRows);
  const [connectionCopyState, setConnectionCopyState] = useState("idle");
  const copyConnectionString = useCallback(async () => {
    if (!connectionStringClipboardValue) return;
    try {
      await copyTextToClipboard(connectionStringClipboardValue);
      setConnectionCopyState("copied");
      window.setTimeout(() => setConnectionCopyState("idle"), 1600);
    } catch {
      setConnectionCopyState("error");
      window.setTimeout(() => setConnectionCopyState("idle"), 2200);
    }
  }, [connectionStringClipboardValue]);

  return (
    <div className="resource-workspace-configuration-connection-details">
      <div className="resource-workspace-configuration-connection-header">
        <span>Connection Fields</span>
        <div className="resource-workspace-configuration-connection-actions">
          {canCheckConnection ? (
            <ButtonControl
              type="standard-small"
              className="resource-workspace-configuration-copy-action"
              title="Check this deployment connection."
              aria-label="Check Connection"
              onClick={() => onCheckConnection?.(deployment)}
            >
              <IconControl iconKey="settings_ethernet" width="1rem" height="1rem" />
            </ButtonControl>
          ) : null}
          <ButtonControl
            type="standard-small"
            className="resource-workspace-configuration-copy-action"
            disabled={!connectionStringClipboardValue}
            title={connectionCopyState === "copied" ? "Connection string copied." : "Copy connection string with password placeholder."}
            aria-label={connectionCopyState === "copied" ? "Connection string copied" : "Copy connection string"}
            onClick={copyConnectionString}
          >
            <IconControl
              iconKey={connectionCopyState === "copied" ? "success" : connectionCopyState === "error" ? "warning" : "contenttab"}
              width="1rem"
              height="1rem"
            />
          </ButtonControl>
        </div>
      </div>
      {connectionDetailRows.length > 0 ? (
        <dl className="resource-workspace-configuration-connection-grid">
          {connectionDetailRows.map((row) => (
            <div key={`${row.label}-${row.value}`}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p>No connection field details are available.</p>
      )}
    </div>
  );
};

const stopActionClick = (event, action) => {
  event?.stopPropagation?.();
  action?.();
};

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const getConfiguredEndpointApiBaseUri = () => {
  const convokeApiUri = window?.configData?.API_URIS?.CONVOKE || "";
  const apiIndex = convokeApiUri.toLowerCase().lastIndexOf("/api/");
  if (apiIndex >= 0) return trimTrailingSlash(convokeApiUri.slice(0, apiIndex));
  return trimTrailingSlash(convokeApiUri);
};

const buildEndpointPathSegment = (value) => encodeURIComponent(String(value || "").trim());

const buildEndpointDeploymentBaseUrl = (destination, deployment, clientShortName = "") => {
  const clientRoute = resolveClientShortName(destination, deployment) || clientShortName;
  const deploymentKey = getString(deployment, "Key", "key", "SystemName", "systemName");
  if (!clientRoute || !deploymentKey) return "";

  const deploymentApiBaseUri = getString(deployment, "APIBaseURI", "apiBaseURI", "ApiBaseUri", "apiBaseUri");
  const baseUri = trimTrailingSlash(deploymentApiBaseUri) || getConfiguredEndpointApiBaseUri();
  const path = `/api/${buildEndpointPathSegment(clientRoute)}/V1/${buildEndpointPathSegment(deploymentKey)}`;
  return baseUri ? `${baseUri}${path}` : path;
};

const buildClientMcpConnectorRoutes = (destination, deployment, clientShortName = "") => {
  const clientRoute = resolveClientShortName(destination, deployment) || clientShortName;
  if (!clientRoute) return [];

  const deploymentApiBaseUri = getString(deployment, "APIBaseURI", "apiBaseURI", "ApiBaseUri", "apiBaseUri");
  const baseUri = trimTrailingSlash(deploymentApiBaseUri) || getConfiguredEndpointApiBaseUri();
  const clientPath = buildEndpointPathSegment(clientRoute);
  const buildConnectorUrl = (providerPath) => {
    const path = `/${providerPath}/${clientPath}`;
    return baseUri ? `${baseUri}${path}` : path;
  };

  return [
    {
      title: "Generic MCP Connector",
      methods: "POST",
      url: buildConnectorUrl("mcp"),
      description: "Unified Client MCP Server connector for generic MCP clients."
    },
    {
      title: "Claude MCP Connector",
      methods: "POST",
      url: buildConnectorUrl("mcp-claude"),
      description: "Unified Client MCP Server connector for Claude."
    },
    {
      title: "ChatGPT MCP Connector",
      methods: "POST",
      url: buildConnectorUrl("mcp-chatgpt"),
      description: "Unified Client MCP Server connector for ChatGPT."
    }
  ];
};

const getEndpointRoutePath = (url) => {
  const value = String(url || "");
  const apiIndex = value.toLowerCase().indexOf("/api/");
  return apiIndex >= 0 ? value.slice(apiIndex) : value;
};

const isHostedApiEnabled = (deployment) => {
  const surfaceMode = Number(getString(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  if (surfaceMode) return surfaceMode === 1 || surfaceMode === 3;
  const hostedApiValue = getValue(deployment, "HostedApiEnabled", "hostedApiEnabled", "IsHostedApiEnabled", "isHostedApiEnabled");
  if (hostedApiValue === undefined || hostedApiValue === null || hostedApiValue === "") return true;
  return getBoolean(deployment, "HostedApiEnabled", "hostedApiEnabled", "IsHostedApiEnabled", "isHostedApiEnabled");
};

const isMcpEnabled = (deployment) => {
  const surfaceMode = Number(getString(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  if (surfaceMode) return surfaceMode === 2 || surfaceMode === 3;
  return getBoolean(deployment, "McpEnabled", "mcpEnabled") === true;
};

const allowsDeploymentWideNativeQuery = (deployment) => {
  const mode = Number(getValue(deployment, "WarehouseNativeQueryMode", "warehouseNativeQueryMode"));
  return mode === 2;
};

const getSchemaRecordId = (schemaRecord) =>
  getId(schemaRecord) || getString(schemaRecord, "SchemaId", "schemaId", "UniqueId", "uniqueId");

const getSchemaRecordLabel = (schemaRecord) =>
  getDisplayName(schemaRecord, getSchemaRecordId(schemaRecord));

const getSchemaVersionRecordId = (version) =>
  getString(version, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId", "_id", "id");

const getSchemaVersionRecordLabel = (version) => {
  const value = getSchemaVersionRecordId(version);
  const label = getString(version, "Title", "title", "DisplayName", "displayName")
    || getString(version, "Version", "version")
    || value;
  const versionNumber = getString(version, "Version", "version");
  return versionNumber && label && !normalizeCompare(label).includes(normalizeCompare(versionNumber))
    ? `${label} (${versionNumber})`
    : label;
};

const getSchemaScopeSchemaRecords = (destinationState, destination) => {
  const records = [
    ...getArray(destinationState, "schemas", "Schemas", "linkedSchemas", "LinkedSchemas"),
    ...getArray(destination, "Schemas", "schemas", "LinkedSchemas", "linkedSchemas")
  ];
  const activeSchema = destinationState?.schema;
  if (activeSchema) records.push(activeSchema);

  const seen = new Set();
  return records.filter((record) => {
    const schemaId = getSchemaRecordId(record);
    if (!schemaId || seen.has(normalizeCompare(schemaId))) return false;
    seen.add(normalizeCompare(schemaId));
    return true;
  });
};

const buildSchemaScopeSchemaOptions = (schemaRecords) =>
  getArray({ schemaRecords }, "schemaRecords")
    .map((schemaRecord) => {
      const value = getSchemaRecordId(schemaRecord);
      const text = getSchemaRecordLabel(schemaRecord);
      return value && text ? { Text: text, Value: value } : null;
    })
    .filter(Boolean);

const buildSchemaScopeVersionOptions = (schemaRecords) =>
  getArray({ schemaRecords }, "schemaRecords")
    .flatMap((schemaRecord) => {
      const schemaId = getSchemaRecordId(schemaRecord);
      if (!schemaId) return [];
      return getArray(schemaRecord, "SchemaVersions", "schemaVersions")
        .map((version) => {
          const value = getSchemaVersionRecordId(version);
          const text = getSchemaVersionRecordLabel(version);
          return value && text ? { Text: text, Value: value, FilterValue: schemaId } : null;
        })
        .filter(Boolean);
    });

const resolveSchemaScopeSchemaLabel = (schemaRecords, schemaId) => {
  if (!schemaId) return "";
  const schemaRecord = getArray({ schemaRecords }, "schemaRecords").find((record) =>
    normalizeCompare(getSchemaRecordId(record)) === normalizeCompare(schemaId)
  );
  return schemaRecord ? getSchemaRecordLabel(schemaRecord) : "";
};

const resolveSchemaScopeVersionLabel = (schemaRecords, schemaVersionId) => {
  if (!schemaVersionId) return "";
  for (const schemaRecord of getArray({ schemaRecords }, "schemaRecords")) {
    const version = getArray(schemaRecord, "SchemaVersions", "schemaVersions").find((candidate) =>
      normalizeCompare(getSchemaVersionRecordId(candidate)) === normalizeCompare(schemaVersionId)
    );
    const label = getSchemaVersionRecordLabel(version);
    if (label) return label;
  }
  return "";
};

const resolveSchemaScopeVersionSelection = (schemaRecords, schemaVersionId) => {
  if (!schemaVersionId) return null;
  for (const schemaRecord of getArray({ schemaRecords }, "schemaRecords")) {
    const version = getArray(schemaRecord, "SchemaVersions", "schemaVersions").find((candidate) =>
      normalizeCompare(getSchemaVersionRecordId(candidate)) === normalizeCompare(schemaVersionId)
    );
    if (version) {
      return {
        schemaId: getSchemaRecordId(schemaRecord),
        schemaName: getSchemaRecordLabel(schemaRecord),
        schemaVersionId: getSchemaVersionRecordId(version),
        schemaVersion: getSchemaVersionRecordLabel(version)
      };
    }
  }
  return null;
};

const decorateSchemaScopeSelection = (scope, schemaRecords) => {
  const schemaVersionId = getString(scope, "SchemaVersionId", "schemaVersionId");
  const versionSelection = resolveSchemaScopeVersionSelection(schemaRecords, schemaVersionId);
  const schemaId = versionSelection?.schemaId || getString(scope, "SchemaId", "schemaId");
  return {
    ...scope,
    SchemaId: schemaId,
    SchemaVersionId: versionSelection?.schemaVersionId || schemaVersionId,
    SchemaName: versionSelection?.schemaName || resolveSchemaScopeSchemaLabel(schemaRecords, schemaId),
    SchemaVersion: versionSelection?.schemaVersion || resolveSchemaScopeVersionLabel(schemaRecords, schemaVersionId)
  };
};

const getStoredSchemaScopeSchemaLabel = (scope) => {
  const value = getString(scope, "SchemaName", "schemaName");
  const normalizedValue = normalizeCompare(value);
  if (!value || normalizedValue === "schema" || /^schema\s+\d+$/.test(normalizedValue)) return "";
  return value;
};

const getSchemaScopeDisplaySchemaLabel = (schemaRecords, scope) => {
  const schemaVersionId = getString(scope, "SchemaVersionId", "schemaVersionId");
  const versionSelection = resolveSchemaScopeVersionSelection(schemaRecords, schemaVersionId);
  return versionSelection?.schemaName
    || resolveSchemaScopeSchemaLabel(schemaRecords, getString(scope, "SchemaId", "schemaId"))
    || getStoredSchemaScopeSchemaLabel(scope);
};

const getSchemaScopeDisplayVersionLabel = (schemaRecords, scope) => {
  const schemaVersionId = getString(scope, "SchemaVersionId", "schemaVersionId");
  const versionSelection = resolveSchemaScopeVersionSelection(schemaRecords, schemaVersionId);
  return versionSelection?.schemaVersion
    || resolveSchemaScopeVersionLabel(schemaRecords, schemaVersionId)
    || getString(scope, "SchemaVersion", "schemaVersion", "Version", "version");
};

const buildSchemaScopeManagerMetadata = ({ schemaOptions, versionOptions, isAddMode }) => ({
  Name: "SchemaAPISchemaScope",
  ItemName: "SchemaAPISchemaScope",
  DisplayName: "Schema Scope",
  SidePanelFormWidth: 42,
  Fields: [
    {
      FieldName: "SchemaId",
      DisplayName: "Schema",
      Description: "Entity Schema used by this deployment scope.",
      FieldType: SystemTypes.UIFieldType.SearchSelectList,
      DataJson: schemaOptions,
      IsRequired: true,
      IsReadOnly: !isAddMode,
      TabId: "Scope|1",
      DisplaySequence: 1
    },
    {
      FieldName: "SchemaVersionId",
      DisplayName: "Schema Version",
      Description: "Schema version used by this deployment scope.",
      FieldType: SystemTypes.UIFieldType.SelectList,
      DataJson: versionOptions,
      DataOperationFilterProperty: "SchemaId",
      IsRequired: true,
      IsReadOnly: !isAddMode,
      TabId: "Scope|1",
      DisplaySequence: 2
    },
    {
      FieldName: "SchemaScopeIdentifier",
      DisplayName: "Scope Identifier",
      Description: "Route-safe schema scope identifier used in API and MCP requests.",
      FieldType: SystemTypes.UIFieldType.TextBox,
      IsRequired: true,
      TabId: "Scope|1",
      DisplaySequence: 3
    },
    {
      FieldName: "Sequence",
      DisplayName: "Sequence",
      FieldType: SystemTypes.UIFieldType.TextBox,
      Format: SystemTypes.UIFormats?.INT,
      TabId: "Scope|1",
      DisplaySequence: 4
    },
    {
      FieldName: "IsActive",
      DisplayName: "Active",
      FieldType: SystemTypes.UIFieldType.CheckBox,
      Format: SystemTypes.UIFormats?.BOOL,
      TabId: "Status|2",
      DisplaySequence: 1
    }
  ]
});

const getEndpointRowSchemaScopeIdentifier = (deployment, row) => {
  const explicit = getString(row, "SchemaScopeIdentifier", "schemaScopeIdentifier");
  if (explicit) return explicit;

  const rowSchemaVersionId = normalizeCompare(getString(row, "EntitySchemaVersionId", "entitySchemaVersionId", "SchemaVersionId", "schemaVersionId"));
  const scopes = getEndpointDeploymentSchemaScopes(deployment);
  const matchingScope = rowSchemaVersionId
    ? scopes.find((scope) => normalizeCompare(getString(scope, "SchemaVersionId", "schemaVersionId")) === rowSchemaVersionId)
    : null;
  return getString(matchingScope, "SchemaScopeIdentifier", "schemaScopeIdentifier")
    || getString(scopes[0], "SchemaScopeIdentifier", "schemaScopeIdentifier");
};

const buildEndpointDeploymentRouteCatalog = (destination, deployment, clientShortName = "") => {
  const deploymentBaseUrl = buildEndpointDeploymentBaseUrl(destination, deployment, clientShortName);
  if (!deploymentBaseUrl) return [];

  const hostedApiEnabled = isHostedApiEnabled(deployment);
  const routes = [{
    title: "Status",
    methods: "GET",
    url: `${deploymentBaseUrl}/status`,
    description: "Configuration and online status for this endpoint deployment."
  }];

  if (hostedApiEnabled) {
    routes.push(
      {
        title: "Secure Status",
        methods: "GET",
        url: `${deploymentBaseUrl}/secure-status`,
        description: "Authenticated online route check for this endpoint deployment."
      },
      {
        title: "OpenAPI",
        methods: "GET",
        url: `${deploymentBaseUrl}/openapi.json`,
        description: "OpenAPI document for this deployment."
      },
      {
        title: "Auth Test",
        methods: "GET, POST",
        url: `${deploymentBaseUrl}/auth-test`,
        description: "Credential test route for PAT or Basic setup."
      },
      {
        title: "Query Languages",
        methods: "GET",
        url: `${deploymentBaseUrl}/query-languages`,
        description: "Query language discovery for this deployment."
      },
      {
        title: "Structured Reference",
        methods: "GET",
        url: `${deploymentBaseUrl}/query-languages/structured`,
        description: "Structured REST query reference."
      },
      {
        title: "ConvokeQL Reference",
        methods: "GET",
        url: `${deploymentBaseUrl}/query-languages/convokeql`,
        description: "ConvokeQL query reference."
      }
    );

    const entityRows = getEndpointEntityEndpointRows(deployment).filter(isRecordActive);
    if (entityRows.length) {
      getEndpointDeploymentSchemaScopes(deployment).forEach((scope) => {
        const schemaScopeIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
        routes.push({
          title: `Scope Query (${schemaScopeIdentifier})`,
          methods: "GET, POST",
          url: `${deploymentBaseUrl}/${buildEndpointPathSegment(schemaScopeIdentifier)}/query`,
          description: "Schema-scoped query route. The entity or view is resolved from the ConvokeQL FROM clause."
        });
      });
    }
    entityRows.forEach((entityRow) => {
      routes.push(...buildEndpointEntityRouteCatalog(destination, deployment, entityRow, clientShortName));
    });

    if (allowsDeploymentWideNativeQuery(deployment)) {
      getEndpointDeploymentSchemaScopes(deployment).forEach((scope) => {
        const schemaScopeIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
        routes.push({
          title: `Warehouse Native Query (${schemaScopeIdentifier})`,
          methods: "POST",
          url: `${deploymentBaseUrl}/${buildEndpointPathSegment(schemaScopeIdentifier)}/warehouse/query`,
          description: "Schema-scoped deployment-wide read-only Native warehouse query route."
        });
      });
    }
  }

  if (isMcpEnabled(deployment)) {
    routes.unshift(...buildClientMcpConnectorRoutes(destination, deployment, clientShortName));
  }

  return routes;
};

const DestinationDeploymentsInlineSection = ({
  destination,
  defaults,
  schema,
  selectedDeploymentId = "",
  selectDeploymentOnly = false,
  onSelectDeployment,
  onSelectedDeploymentActionsChange,
  onAddDeployment,
  onManageDeployment,
  onDeleteDeployment,
  onCheckDeployment,
  onOpenDeploymentSchedule,
  onOpenDeploymentScope,
  onOpenDeploymentViews
}) => {
  const resourceDomainContext = useResourceDomainContext();
  const clientMgr = resourceDomainContext?.managers?.client;
  const hasClientMgr = Boolean(clientMgr);
  const clientMgrRef = useRef(clientMgr);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [openDeploymentMenu, setOpenDeploymentMenu] = useState("");
  const [deploymentMenuStyle, setDeploymentMenuStyle] = useState(null);
  const [resolvedClientShortName, setResolvedClientShortName] = useState("");
  const [routeDialog, setRouteDialog] = useState({ open: false, title: "", routes: [], copiedUrl: "" });
  const schemaVersionLabel = getSchemaVersionLabel(schema, getDestinationActiveSchemaVersionId(destination));
  const deployments = getDestinationDeployments(destination, defaults);
  const selectedDeployment = deployments.find((deployment, index) =>
    normalizeCompare(getDeploymentId(deployment) || getDeploymentRenderKey(deployment, defaults, index)) === normalizeCompare(selectedDeploymentId)
  ) || deployments[0] || null;
  const showDeploymentFilters = deployments.length > 1;
  const routeClientDeployment = deployments[0] || null;
  const routeClientLookupKey = [
    getString(destination, "ClientShortName", "clientShortName"),
    getString(destination, "ClientId", "clientId"),
    getString(routeClientDeployment, "ClientShortName", "clientShortName"),
    getString(routeClientDeployment, "ClientId", "clientId")
  ].join("|");
  const activeDeployments = deployments.filter(isRecordActive).length;
  useEffect(() => {
    clientMgrRef.current = clientMgr;
  }, [clientMgr]);

  useEffect(() => {
    let isCancelled = false;
    const directClientShortName = resolveClientShortName(destination, routeClientDeployment);
    if (directClientShortName || !hasClientMgr) {
      setResolvedClientShortName((previous) => previous === directClientShortName ? previous : directClientShortName);
      return () => {
        isCancelled = true;
      };
    }

    resolveClientShortNameAsync({
      endpoint: destination,
      deployment: routeClientDeployment,
      clientMgr: clientMgrRef.current
    }).then((clientShortName) => {
      if (!isCancelled) {
        setResolvedClientShortName((previous) => previous === (clientShortName || "") ? previous : (clientShortName || ""));
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [hasClientMgr, routeClientLookupKey]);

  const openDeploymentActions = (event, deploymentKey) => {
    const buttonBox = event.currentTarget?.getBoundingClientRect?.();
    setOpenDeploymentMenu((current) => current === deploymentKey ? "" : deploymentKey);
    setDeploymentMenuStyle({
      left: Math.max(8, (buttonBox?.right ?? 0) - 160),
      top: (buttonBox?.bottom ?? 0) + 6
    });
  };
  const runDeploymentMenuAction = (action, deployment) => {
    setOpenDeploymentMenu("");
    action?.(deployment);
  };
  const closeRouteDialog = () => setRouteDialog({ open: false, title: "", routes: [], copiedUrl: "" });
  const openRouteDialog = useCallback((deployment) => {
    const deploymentName = getDeploymentDisplayName(deployment, destination, schemaVersionLabel);
    const clientShortName = resolveClientShortName(destination, deployment) || resolvedClientShortName;
    setRouteDialog({
      open: true,
      title: deploymentName,
      routes: buildEndpointDeploymentRouteCatalog(destination, deployment, clientShortName),
      copiedUrl: ""
    });
  }, [destination, resolvedClientShortName, schemaVersionLabel]);
  const copyRouteUrl = async (url) => {
    await copyTextToClipboard(url);
    setRouteDialog((current) => ({ ...current, copiedUrl: url }));
    window.setTimeout(() => {
      setRouteDialog((current) => current.copiedUrl === url ? { ...current, copiedUrl: "" } : current);
    }, 1600);
  };

  const selectedDeploymentActionId = getDeploymentId(selectedDeployment);
  useEffect(() => {
    if (!onSelectedDeploymentActionsChange) return;
    const currentSelectedDeployment = getDestinationDeployments(destination, defaults).find((deployment) =>
      normalizeCompare(getDeploymentId(deployment)) === normalizeCompare(selectedDeploymentActionId)
    ) || null;
    onSelectedDeploymentActionsChange(currentSelectedDeployment ? {
      selectedDeployment: currentSelectedDeployment,
      selectedDeploymentId: getDeploymentId(currentSelectedDeployment),
      openRoutes: () => openRouteDialog(currentSelectedDeployment)
    } : null);
  }, [defaults, destination, onSelectedDeploymentActionsChange, openRouteDialog, selectedDeploymentActionId]);

  useEffect(() => () => onSelectedDeploymentActionsChange?.(null), [onSelectedDeploymentActionsChange]);

  return (
    <>
      <div className="resource-workspace-section-title">
        <div className="resource-workspace-section-title__row resource-workspace-section-title__row--split resource-workspace-deployments-title-row">
          <div>
            <h2>Deployments</h2>
            <p>{`${activeDeployments.toLocaleString()} active / ${deployments.length.toLocaleString()} total`}</p>
          </div>
          {showDeploymentFilters || onAddDeployment ? (
            <div
              className={`resource-workspace-deployments-title-filters${showDeploymentFilters ? "" : " resource-workspace-deployments-title-filters--action-only"}`}
              aria-label="Destination deployments filters"
            >
              {showDeploymentFilters ? (
                <>
                  <FieldControl
                    fieldMetadata={buildFilterTextBoxField("DestinationDeploymentSearch", "Search deployments...", "Search deployments")}
                    value={searchTerm}
                    onChange={(nextValue) => setSearchTerm(nextValue || "")}
                    disableError
                  />
                  <FieldControl
                    fieldMetadata={buildSelectListField("DestinationDeploymentStatus", "", SOURCE_CATALOG_STATUS_OPTIONS, "Please Select", "Filter deployments")}
                    value={statusFilter}
                    onChange={(nextValue) => setStatusFilter(nextValue || "active")}
                    disableError
                  />
                </>
              ) : null}
              {onAddDeployment ? (
                <ButtonControl
                  type="standard-small"
                  title="Add Deployment"
                  aria-label="Add Deployment"
                  onClick={() => onAddDeployment()}
                >
                  <IconControl iconKey="add" title="Add Deployment" width="1rem" height="1rem" />
                </ButtonControl>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <ResourceCatalogListTab
        items={deployments}
        getItemKey={(deployment, index) => getDeploymentRenderKey(deployment, defaults, index)}
        ariaLabel="Destination deployments"
        emptyMessage="No deployments have been created for this destination yet."
        emptyFilteredMessage="No deployments match the current filters."
        search={showDeploymentFilters ? {
          fieldMetadata: buildFilterTextBoxField("DestinationDeploymentSearch", "Search deployments...", "Search deployments"),
          controlType: "field",
          value: searchTerm,
          onChange: (nextValue) => setSearchTerm(nextValue || ""),
          matchesItem: (deployment, value) => !normalizeCompare(value)
            || normalizeCompare(getDeploymentDisplayName(deployment, destination, schemaVersionLabel)).includes(normalizeCompare(value))
            || normalizeCompare(getDeploymentRootName(deployment, defaults)).includes(normalizeCompare(value))
        } : null}
        statusFilter={showDeploymentFilters ? {
          fieldMetadata: buildSelectListField("DestinationDeploymentStatus", "", SOURCE_CATALOG_STATUS_OPTIONS, "Please Select", "Filter deployments"),
          value: statusFilter,
          onChange: (nextValue) => setStatusFilter(nextValue || "active"),
          matchesItem: (deployment, value) => matchesStatusFilter(deployment, value)
        } : null}
        showFilters={false}
        className="resource-catalog-list-tab--destination-deployments"
        renderItem={(deployment, index) => {
          const deploymentKey = getDeploymentRenderKey(deployment, defaults, index);
          const deploymentName = getDeploymentDisplayName(deployment, destination, schemaVersionLabel);
          const deploymentMenuOpen = openDeploymentMenu === deploymentKey;
          const isEndpoint = defaults?.isEndpoint || normalizeCompare(defaults?.managerKey) === "schemaapi";
          const isWarehouse = normalizeCompare(defaults?.managerKey) === "warehouse";
          const endpointConfigLocked = isEndpoint && !isSchemaDestinationConfigEditable(deployment);
          const metaItems = isEndpoint
            ? [
              { key: "lifecycle", className: "resource-catalog-row__meta-item--status", content: getSchemaDestinationStatusLabel(getValue(deployment, "SchemaDestinationStatus", "schemaDestinationStatus")) },
              { key: "active", className: "resource-catalog-row__meta-item--detail", content: isRecordActive(deployment) ? "Active" : "Inactive" },
              { key: "surface", className: "resource-catalog-row__meta-item--detail", content: getEndpointSurfaceLabel(deployment) }
            ]
            : [
              { key: "status", className: "resource-catalog-row__meta-item--status", content: getDestinationStateLabel(deployment) },
              { key: "sync", className: "resource-catalog-row__meta-item--detail", content: getBoolean(deployment, "EnableSync", "enableSync", "SyncEnable", "syncEnable") ? "Sync enabled" : "Sync disabled" }
            ];
          return (
            <ResourceCatalogRow
              className={selectDeploymentOnly && deployment === selectedDeployment ? "resource-catalog-row--selected" : ""}
              title={deploymentName}
              subtitle={`${getDeploymentTargetDisplayName(defaults)}: ${getDeploymentRootName(deployment, defaults)}`}
              titleAttribute={deploymentName}
              ariaLabel={`Open deployment ${deploymentName}`}
              onClick={() => selectDeploymentOnly ? onSelectDeployment?.(deployment) : onManageDeployment?.(deployment)}
              metaItems={metaItems}
              actions={(
                <>
                  {deployments.length > 1 ? (
                    <ButtonControl
                      type="standard-small"
                      className="resource-workspace-danger-button"
                      disabled={endpointConfigLocked}
                      title={endpointConfigLocked ? "Endpoint deployments are view-only while online." : `Delete ${deploymentName}`}
                      aria-label={`Delete ${deploymentName}`}
                      onClick={(event) => stopActionClick(event, () => onDeleteDeployment?.(deployment))}
                    >
                      <IconControl iconKey="delete" title={`Delete ${deploymentName}`} width="0.95rem" height="0.95rem" />
                    </ButtonControl>
                  ) : null}
                  {isEndpoint && !selectDeploymentOnly ? (
                    <ButtonControl type="standard-small" title={`View routes for ${deploymentName}`} aria-label={`View routes for ${deploymentName}`} onClick={(event) => stopActionClick(event, () => openRouteDialog(deployment))}>
                      <EndpointRouteIcon />
                    </ButtonControl>
                  ) : null}
                  {!selectDeploymentOnly ? (
                    <ButtonControl type="standard-small" title={`Open deployment ${deploymentName}`} aria-label={`Open deployment ${deploymentName}`} onClick={(event) => stopActionClick(event, () => onManageDeployment?.(deployment))}>
                      <IconControl iconKey="settings" title={`Open deployment ${deploymentName}`} width="0.95rem" height="0.95rem" />
                    </ButtonControl>
                  ) : null}
                  {!isEndpoint ? (
                    <span className="resource-catalog-row__operation-menu">
                    <ButtonControl
                      type="standard-small"
                      title={`Open deployment actions for ${deploymentName}`}
                      aria-label={`Open deployment actions for ${deploymentName}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        openDeploymentActions(event, deploymentKey);
                      }}
                    >
                      <IconControl iconKey="viewmore" title={`Open deployment actions for ${deploymentName}`} width="0.95rem" height="0.95rem" />
                    </ButtonControl>
                    {deploymentMenuOpen ? (
                      <ContextMenuControl
                        openContextMenu={deploymentMenuOpen}
                        setOpenContextMenu={(nextOpen) => setOpenDeploymentMenu(nextOpen ? deploymentKey : "")}
                        contextMenuStyle={deploymentMenuStyle}
                      >
                        <div className="resource-workspace-command-bar-menu" role="menu" aria-label={`Deployment actions for ${deploymentName}`}>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(event) => stopActionClick(event, () => runDeploymentMenuAction(onCheckDeployment, deployment))}
                          >
                            Check Connection
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(event) => stopActionClick(event, () => runDeploymentMenuAction(onOpenDeploymentSchedule, deployment))}
                          >
                            Open Schedule
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(event) => stopActionClick(event, () => runDeploymentMenuAction(onOpenDeploymentScope, deployment))}
                          >
                            Open Entity Scope
                          </button>
                          {isWarehouse ? (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={(event) => stopActionClick(event, () => runDeploymentMenuAction(onOpenDeploymentViews, deployment))}
                            >
                              Open Views
                            </button>
                          ) : null}
                        </div>
                      </ContextMenuControl>
                    ) : null}
                    </span>
                  ) : null}
                </>
              )}
            />
          );
        }}
      />
      <DialogControl
        controlOrigin="endpoint-deployment-routes"
        openDialog={routeDialog.open}
        title="MCP URLs"
        subTitle={routeDialog.title}
        okText="Close"
        excludeCancel
        onOk={closeRouteDialog}
        onCancel={closeRouteDialog}
        onX={closeRouteDialog}
      >
        <div className="resource-workspace-endpoint-routes-dialog">
          {routeDialog.routes.length ? routeDialog.routes.map((route) => (
            <article key={`${route.title}-${route.url}`} className="resource-workspace-endpoint-route">
              <div className="resource-workspace-endpoint-route__header">
                <div>
                  <strong>{route.title}</strong>
                  <span>{route.methods}</span>
                </div>
                <ButtonControl
                  type="standard-small"
                  title={`Copy ${route.title} URL`}
                  aria-label={`Copy ${route.title} URL`}
                  onClick={() => copyRouteUrl(route.url)}
                >
                  <IconControl iconKey={routeDialog.copiedUrl === route.url ? "success" : "contenttab"} title={`Copy ${route.title} URL`} width="0.95rem" height="0.95rem" />
                </ButtonControl>
              </div>
              <code>{route.url}</code>
              <p>{route.description}</p>
            </article>
          )) : (
            <p>No generated routes are available for this deployment.</p>
          )}
        </div>
      </DialogControl>
    </>
  );
};

export const ResourceConfigurationDestinationDeploymentsScreen = ({
  destinationRecord,
  destinationState,
  initialDeploymentId = "",
  onAddDeployment,
  onManageDeployment,
  onDeleteDeployment,
  onCheckDeployment,
  onOpenDeploymentSchedule,
  onOpenDeploymentScope,
  onOpenDeploymentViews,
  onEditEntityEndpoint,
  onDeleteEntityEndpoint,
  onSelectedDeploymentActionsChange
}) => {
  const {
    destination,
    schema,
    defaults,
    loading = false,
    error = ""
  } = destinationState || {};
  const deployments = useMemo(() => getDestinationDeployments(destination, defaults), [defaults, destination]);
  const normalizedInitialDeploymentId = initialDeploymentId && initialDeploymentId !== "all" ? initialDeploymentId : "";
  const [selectedDeploymentId, setSelectedDeploymentId] = useState(normalizedInitialDeploymentId);
  const selectedDeployment = deployments.find((deployment, index) =>
    normalizeCompare(getDeploymentId(deployment) || getDeploymentRenderKey(deployment, defaults, index)) === normalizeCompare(selectedDeploymentId)
  ) || deployments[0] || null;
  const effectiveSelectedDeploymentId = selectedDeployment ? getDeploymentId(selectedDeployment) : "";

  useEffect(() => {
    if (!deployments.length) {
      setSelectedDeploymentId("");
      return;
    }
    if (selectedDeploymentId && deployments.some((deployment, index) =>
      normalizeCompare(getDeploymentId(deployment) || getDeploymentRenderKey(deployment, defaults, index)) === normalizeCompare(selectedDeploymentId)
    )) return;
    setSelectedDeploymentId(getDeploymentId(deployments[0]) || getDeploymentRenderKey(deployments[0], defaults, 0));
  }, [defaults, deployments, selectedDeploymentId]);

  useEffect(() => {
    if (!normalizedInitialDeploymentId) return;
    setSelectedDeploymentId(normalizedInitialDeploymentId);
  }, [normalizedInitialDeploymentId]);

  if (loading) return <ResourceStateControl loading loadingText="Loading Deployments..." />;
  if (error && !destination) return <ResourceStateControl tone="warning" title="Deployments unavailable" message={error} />;

  const destinationName = getDisplayName(destination, getDisplayName(destinationRecord) || defaults?.label || "Destination");
  const isEndpoint = Boolean(defaults?.isEndpoint);
  const resourceLabel = isEndpoint ? "Endpoint" : "Destination";

  const selectDeployment = (deployment) => {
    const deploymentId = getDeploymentId(deployment);
    if (deploymentId) setSelectedDeploymentId(deploymentId);
  };

  return (
    <div className={`resource-workspace-linked-item-layout ${isEndpoint ? "resource-workspace-linked-item-layout--deployment-scope-master-detail" : "resource-workspace-linked-item-layout--summary-sidebar"}`} aria-label={`${destinationName} ${resourceLabel.toLowerCase()} deployments`}>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <DestinationDeploymentsInlineSection
          destination={destination}
          defaults={defaults}
          schema={schema}
          selectedDeploymentId={effectiveSelectedDeploymentId}
          selectDeploymentOnly={isEndpoint}
          onSelectDeployment={selectDeployment}
          onSelectedDeploymentActionsChange={isEndpoint ? onSelectedDeploymentActionsChange : undefined}
          onAddDeployment={onAddDeployment}
          onManageDeployment={onManageDeployment}
          onDeleteDeployment={onDeleteDeployment}
          onCheckDeployment={onCheckDeployment}
          onOpenDeploymentSchedule={onOpenDeploymentSchedule}
          onOpenDeploymentScope={onOpenDeploymentScope}
          onOpenDeploymentViews={onOpenDeploymentViews}
        />
      </section>
      {isEndpoint ? (
        <ResourceConfigurationEndpointScopeScreen
          destinationState={destinationState}
          initialDeploymentFilter={effectiveSelectedDeploymentId}
          lockDeploymentFilter
          panelTitle="Endpoint Scope"
          onEditEntityEndpoint={onEditEntityEndpoint}
          onDeleteEntityEndpoint={onDeleteEntityEndpoint}
        />
      ) : null}
    </div>
  );
};

export const ResourceConfigurationDestinationScreen = ({
  destinationRecord,
  destinationState,
  endpointTypeLabel = "",
  showDeploymentsInline = false,
  onManageSchema,
  onManageDeployment,
  onDeleteDeployment,
  onCheckDeployment,
  onOpenDeploymentSchedule,
  onOpenDeploymentScope,
  onOpenDeploymentViews
}) => {
  const {
    destination,
    metadata,
    schema,
    entityObjects = [],
    defaults,
    loading = false,
    error = ""
  } = destinationState || {};
  if (loading) return <ResourceStateControl loading loadingText="Loading Destination..." />;
  if (error && !destination) return <ResourceStateControl tone="warning" title="Destination unavailable" message={error} />;

  const destinationName = getDisplayName(destination, getDisplayName(destinationRecord) || defaults?.label || "Destination");
  const isEndpoint = Boolean(defaults?.isEndpoint);
  const resourceLabel = isEndpoint ? "Endpoint" : "Destination";
  const schemaId = getId(schema) || getDestinationSchemaId(destination);
  const schemaVersionId = getDestinationActiveSchemaVersionId(destination);
  const schemaVersionLabel = getSchemaVersionLabel(schema, schemaVersionId);
  const deployments = getDestinationDeployments(destination, defaults);
  const typeLabel = isEndpoint
    ? endpointTypeLabel || getEndpointTypeLabel(deployments)
    : defaults?.label || getString(destination, "DestinationSystemType", "destinationSystemType") || resourceLabel;
  const entityDeployments = getDestinationEntityDeployments(destination);
  const connectionSummary = getDestinationConnectionSummary(destination);
  const endpointAuthSummary = getEndpointAuthSummary(deployments);
  const endpointMcpAccessSummary = getEndpointMcpAccessSummary(deployments);

  return (
    <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--summary-sidebar" aria-label={`${destinationName} ${resourceLabel.toLowerCase()} details`}>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <h2>Information</h2>
          <p>{resourceLabel} record owned by this Resource Configuration.</p>
        </div>
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <Fact label="Display Name" value={destinationName} />
          <Fact label="Type" value={typeLabel} />
          <Fact label={isEndpoint ? "API Route Key" : "System Name"} value={getString(destination, "APIName", "apiName", "SystemName", "systemName") || "Not set"} />
          <Fact label="Active" value={isRecordActive(destination) ? "Active" : "Inactive"} />
        </dl>
      </section>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <div className="resource-workspace-section-title__row resource-workspace-section-title__row--split">
            <h2>Schema</h2>
            <ButtonControl
              type="standard-small"
              title={schemaId ? "Manage Schema" : "No linked Entity Schema is available"}
              aria-label="Manage Schema"
              disabled={!onManageSchema || !schemaId}
              onClick={() => onManageSchema?.({ SchemaId: schemaId })}
            >
              <IconControl iconKey="opennewwindow" title="Manage Schema" width="1rem" height="1rem" />
            </ButtonControl>
          </div>
          <p>Schema and active version used by this {resourceLabel.toLowerCase()}.</p>
        </div>
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <Fact label="Schema" value={getDisplayName(schema, getDestinationSchemaId(destination) || "Not set")} />
          <Fact label="Active Version" value={schemaVersionLabel || "Not set"} />
          <Fact label="Entity Objects" value={entityObjects.length.toLocaleString()} />
          <Fact label={isEndpoint ? "Endpoint Scope Rows" : "Scope Rows"} value={isEndpoint ? deployments.reduce((count, deployment) =>
            count + getArray(deployment, "EntityEndpoints", "entityEndpoints").length + getArray(deployment, "ModelEndpoints", "modelEndpoints").length, 0).toLocaleString() : entityDeployments.length.toLocaleString()} />
        </dl>
      </section>
      <section className="resource-workspace-panel resource-workspace-linked-item-section">
        <div className="resource-workspace-section-title">
          <h2>Runtime</h2>
          <p>{isEndpoint ? "Route and deployment signals for the endpoint." : "Connection and deployment signals for the destination."}</p>
        </div>
        {metadata ? null : <p className="resource-workspace-inline-warning">{resourceLabel} metadata is not available for editing.</p>}
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <Fact label={isEndpoint ? "Auth" : "Connection"} value={isEndpoint ? endpointAuthSummary : connectionSummary} />
          {isEndpoint ? <Fact label="MCP Access" value={endpointMcpAccessSummary} /> : null}
          <Fact label="Deployments" value={deployments.length.toLocaleString()} />
        </dl>
      </section>
      {showDeploymentsInline ? (
        <section className="resource-workspace-panel resource-workspace-linked-item-section">
          <DestinationDeploymentsInlineSection
            destination={destination}
            defaults={defaults}
            schema={schema}
            onManageDeployment={onManageDeployment}
            onDeleteDeployment={onDeleteDeployment}
            onCheckDeployment={onCheckDeployment}
            onOpenDeploymentSchedule={onOpenDeploymentSchedule}
            onOpenDeploymentScope={onOpenDeploymentScope}
            onOpenDeploymentViews={onOpenDeploymentViews}
          />
        </section>
      ) : null}
    </div>
  );
};

export const ResourceConfigurationDestinationDeploymentScreen = ({
  configuration,
  destinationRecord,
  destinationState,
  deployment,
  resourceEvents = [],
  onCheckDeployment,
  onConfigureEntitySchema,
  onSaveSchemaScopes,
  schemaScopeActionState
}) => {
  const [schemaScopeEditor, setSchemaScopeEditor] = useState(null);
  const [deleteSchemaScopeRequest, setDeleteSchemaScopeRequest] = useState(null);
  const {
    destination,
    schema,
    entityObjects = [],
    defaults,
    loading = false,
    error = ""
  } = destinationState || {};

  if (loading) return <ResourceStateControl loading loadingText="Loading Deployment..." />;
  if (error && !destination) return <ResourceStateControl tone="warning" title="Deployment unavailable" message={error} />;
  if (!deployment) return <ResourceStateControl tone="warning" title="Deployment unavailable" message="The requested destination deployment could not be loaded." />;

  const schemaVersionId = getString(deployment, "SchemaVersionId", "schemaVersionId") || getDestinationActiveSchemaVersionId(destination);
  const schemaVersionLabel = getSchemaVersionLabel(schema, schemaVersionId);
  const deploymentName = getDeploymentDisplayName(deployment, destination, schemaVersionLabel);
  const deploymentId = getDeploymentId(deployment);
  const destinationName = getDisplayName(destination, getDisplayName(destinationRecord) || defaults?.label || "Destination");
  const isEndpoint = Boolean(defaults?.isEndpoint);
  const endpointDeploymentOnline = isEndpoint && isSchemaDestinationOnline(deployment);
  const endpointDeploymentStatusLabel = endpointDeploymentOnline ? "Online" : "Offline";
  const resourceLabel = isEndpoint ? "Endpoint" : "Destination";
  const targetLabel = getDeploymentTargetDisplayName(defaults);
  const targetValue = getDeploymentTargetValue(deployment, defaults) || getDeploymentRootName(deployment, defaults);
  const endpointWarehouseDeploymentName = isEndpoint
    ? getEndpointWarehouseDeploymentDisplayName(deployment, destinationState)
    : "";
  const entityDeployments = getDestinationEntityDeployments(destination)
    .filter((row) => normalizeCompare(getString(row, "DeploymentId", "deploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId")) === normalizeCompare(deploymentId));
  const scheduleRows = [
    ...getArray(destinationState, "destinationSchedules", "schedules"),
    ...getArray(destination, "DestinationSchedules", "destinationSchedules", "Schedules", "schedules")
  ].filter((schedule) =>
    normalizeCompare(getString(schedule, "DeploymentId", "deploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId")) === normalizeCompare(deploymentId)
  );
  const scheduleSummary = `${scheduleRows.length.toLocaleString()} scheduled`;
  const deploymentScopeSummary = `${entityDeployments.length.toLocaleString()} Scoped Object${entityDeployments.length === 1 ? "" : "s"}`;
  const relatedEvents = getArray({ resourceEvents }, "resourceEvents").filter((event) => {
    const eventDeploymentId = getString(
      event,
      "DeploymentId",
      "deploymentId",
      "SchemaDestinationDeploymentId",
      "schemaDestinationDeploymentId",
      "WarehouseDeploymentId",
      "warehouseDeploymentId",
      "StorageDeploymentId",
      "storageDeploymentId",
      "FileStorageDeploymentId",
      "fileStorageDeploymentId",
      "ExternalSystemDeploymentId",
      "externalSystemDeploymentId"
    );
    return eventDeploymentId && normalizeCompare(eventDeploymentId) === normalizeCompare(deploymentId);
  }).slice(0, 5);
  const runtimeSummary = (
    <div className="resource-workspace-deployment-runtime-summary">
      <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
        <h3>Runtime</h3>
        <p>{isEndpoint ? "Current route and surface settings for this deployment." : "Current connection and execution settings for this deployment."}</p>
      </div>
      <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
        <Fact label={isEndpoint ? "Surface" : "Connection"} value={isEndpoint ? getEndpointSurfaceLabel(deployment) : getDestinationConnectionSummary(destination)} />
        <Fact label={isEndpoint ? "Status" : "Deployment State"} value={isEndpoint ? getSchemaDestinationStatusLabel(getValue(deployment, "SchemaDestinationStatus", "schemaDestinationStatus")) : getDestinationStateLabel(deployment)} />
        {isEndpoint ? (
          <>
            <Fact label="OpenAPI" value={getBoolean(deployment, "HostedApiEnabled", "hostedApiEnabled") ? "Enabled" : "Surface dependent"} />
            <Fact label="MCP" value={getString(deployment, "McpConnectorUrl", "mcpConnectorUrl") || "Surface dependent"} />
            <Fact label="MCP Access" value={getMcpAuthorizationLabel(deployment)} />
            <Fact label="MCP Profiles" value={getMcpAuthorizedProfileCount(deployment).toLocaleString()} />
          </>
        ) : (
          <>
            <Fact label="Sync" value={getBoolean(deployment, "EnableSync", "enableSync", "SyncEnable", "syncEnable") ? "Enabled" : "Disabled"} />
            <Fact label="Preview" value={getBoolean(deployment, "EnablePreview", "enablePreview") ? "Enabled" : "Not enabled"} />
            <Fact label="Schedules" value={scheduleSummary} />
          </>
        )}
      </dl>
    </div>
  );
  const connectionDetails = isEndpoint
    ? null
    : (
      <DestinationDeploymentConnectionDetails
        deployment={deployment}
        destination={destination}
        canCheckConnection={Boolean(defaults?.checkOperationName)}
        onCheckConnection={onCheckDeployment}
      />
    );
  const allSchemaScopeRows = isEndpoint ? validateSchemaScopeRows(normalizeSchemaScopeRows(getArray(deployment, "SchemaScopes", "schemaScopes"), deployment)) : [];
  const schemaScopeRows = allSchemaScopeRows.filter((scope) =>
    getValue(scope, "IsDeleted", "isDeleted") !== true && getValue(scope, "IsActive", "isActive") !== false
  );
  const hasMultipleActiveSchemaScopes = schemaScopeRows.length > 1;
  const schemaScopeSchemaRecords = getSchemaScopeSchemaRecords(destinationState, destination);
  const schemaScopeSchemaOptions = buildSchemaScopeSchemaOptions(schemaScopeSchemaRecords);
  const schemaScopeVersionOptions = buildSchemaScopeVersionOptions(schemaScopeSchemaRecords);
  const schemaScopeEditorIsAddMode = schemaScopeEditor?.mode === "add";
  const schemaScopeEditorMetadata = buildSchemaScopeManagerMetadata({
    schemaOptions: schemaScopeSchemaOptions,
    versionOptions: schemaScopeVersionOptions,
    isAddMode: schemaScopeEditorIsAddMode
  });
  const schemaScopeActionBusy = Boolean(schemaScopeActionState?.busyAction?.startsWith("destination-deployment-schema-scopes-save:"));
  const canManageSchemaScopes = Boolean(onSaveSchemaScopes) && isSchemaDestinationConfigEditable(deployment);
  const canAddSchemaScope = canManageSchemaScopes && schemaScopeSchemaOptions.length > 0 && schemaScopeVersionOptions.length > 0;
  const endpointRows = isEndpoint
    ? [
      ...getArray(deployment, "EntityEndpoints", "entityEndpoints"),
      ...getArray(deployment, "ViewEndpoints", "viewEndpoints")
    ].filter((row) => getValue(row, "IsDeleted", "isDeleted") !== true)
    : [];
  const getSchemaScopeEndpointRowCount = (scope) => {
    const scopeIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    if (!scopeIdentifier) return 0;
    return endpointRows.filter((row) =>
      normalizeCompare(getEndpointRowSchemaScopeIdentifier(deployment, row)) === normalizeCompare(scopeIdentifier)
    ).length;
  };
  const getSchemaScopeEntityObjectCount = (scope) => {
    const explicitCount = getValue(scope, "EntityObjectCount", "entityObjectCount");
    if (explicitCount !== undefined && explicitCount !== null && explicitCount !== "") return Number(explicitCount).toLocaleString();
    const scopeVersionId = getString(scope, "SchemaVersionId", "schemaVersionId");
    if (!scopeVersionId || schemaScopeRows.length === 1 || normalizeCompare(scopeVersionId) === normalizeCompare(schemaVersionId)) {
      return entityObjects.length.toLocaleString();
    }
    return "Not loaded";
  };
  const openAddSchemaScopeEditor = () => {
    if (!canAddSchemaScope) return;
    const currentSchemaId = getString(deployment, "SchemaId", "schemaId") || getDestinationSchemaId(destination);
    const currentVersionId = getString(deployment, "CurrentSchemaVersionId", "currentSchemaVersionId", "SchemaVersionId", "schemaVersionId")
      || getDestinationActiveSchemaVersionId(destination);
    const currentVersionIsValid = schemaScopeVersionOptions.some((option) =>
      normalizeCompare(option.Value) === normalizeCompare(currentVersionId)
      && (!currentSchemaId || normalizeCompare(option.FilterValue) === normalizeCompare(currentSchemaId))
    );
    const currentVersionSelection = currentVersionIsValid
      ? resolveSchemaScopeVersionSelection(schemaScopeSchemaRecords, currentVersionId)
      : null;
    const currentSchemaIdToUse = currentVersionSelection?.schemaId || currentSchemaId;
    const schemaName = currentVersionSelection?.schemaName || resolveSchemaScopeSchemaLabel(schemaScopeSchemaRecords, currentSchemaIdToUse);
    const schemaVersion = currentVersionIsValid
      ? currentVersionSelection?.schemaVersion || resolveSchemaScopeVersionLabel(schemaScopeSchemaRecords, currentVersionId)
      : "";
    setSchemaScopeEditor({
      mode: "add",
      scope: buildSchemaScopeDraft({
        ...deployment,
        SchemaId: currentSchemaIdToUse,
        SchemaName: schemaName,
        CurrentSchemaVersionId: currentVersionIsValid ? currentVersionId : "",
        CurrentSchemaVersion: schemaVersion
      }, allSchemaScopeRows),
      error: ""
    });
  };
  const openEditSchemaScopeEditor = (scope) => {
    if (!canManageSchemaScopes || !scope) return;
    setSchemaScopeEditor({
      mode: "edit",
      scope: decorateSchemaScopeSelection(scope, schemaScopeSchemaRecords),
      error: ""
    });
  };
  const closeSchemaScopeEditor = () => {
    setSchemaScopeEditor(null);
  };
  const updateSchemaScopeEditorData = (nextScope) => {
    setSchemaScopeEditor((current) => current ? { ...current, scope: nextScope, error: "" } : current);
  };
  const saveSchemaScopeEditor = async (updatedScope) => {
    if (!onSaveSchemaScopes || !updatedScope) return undefined;
    const decoratedScope = decorateSchemaScopeSelection(updatedScope, schemaScopeSchemaRecords);
    const schemaIdToSave = getString(decoratedScope, "SchemaId", "schemaId");
    const schemaVersionIdToSave = getString(decoratedScope, "SchemaVersionId", "schemaVersionId");
    const identifierToSave = getString(decoratedScope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
    if (!schemaIdToSave || !schemaVersionIdToSave || !identifierToSave) {
      setSchemaScopeEditor((current) => current ? {
        ...current,
        error: "Schema, Schema Version, and Scope Identifier are required."
      } : current);
      return undefined;
    }
    const updatedKey = getSchemaScopeRowKey(updatedScope);
    const existingRow = allSchemaScopeRows.some((scope, index) => String(getSchemaScopeRowKey(scope, index)) === String(updatedKey));
    const nextRows = existingRow
      ? allSchemaScopeRows.map((scope, index) =>
        String(getSchemaScopeRowKey(scope, index)) === String(updatedKey)
          ? { ...scope, ...decoratedScope }
          : scope
      )
      : [...allSchemaScopeRows, decoratedScope];
    const response = await onSaveSchemaScopes(deployment, nextRows.map(stripSchemaScopeUiState));
    if (response !== undefined) closeSchemaScopeEditor();
    return response;
  };
  const requestDeleteSchemaScope = (scope) => {
    if (!canManageSchemaScopes || !scope) return;
    const activeScopes = allSchemaScopeRows.filter((candidate) =>
      getValue(candidate, "IsDeleted", "isDeleted") !== true && getValue(candidate, "IsActive", "isActive") !== false
    );
    setDeleteSchemaScopeRequest({
      scope,
      blocked: activeScopes.length <= 1
    });
  };
  const closeDeleteSchemaScopeDialog = () => {
    setDeleteSchemaScopeRequest(null);
  };
  const confirmDeleteSchemaScope = async () => {
    if (!deleteSchemaScopeRequest?.scope) return;
    if (deleteSchemaScopeRequest.blocked) {
      closeDeleteSchemaScopeDialog();
      return;
    }
    const deleteKey = getSchemaScopeRowKey(deleteSchemaScopeRequest.scope);
    const nextRows = allSchemaScopeRows.filter((scope, index) => String(getSchemaScopeRowKey(scope, index)) !== String(deleteKey));
    const response = await onSaveSchemaScopes?.(deployment, nextRows.map(stripSchemaScopeUiState));
    if (response !== undefined) closeDeleteSchemaScopeDialog();
  };

  return (
    <>
    <div className="resource-workspace-linked-item-layout resource-workspace-linked-item-layout--summary-sidebar resource-workspace-linked-item-layout--deployment-detail" aria-label={`${deploymentName} deployment details`}>
      <section className="resource-workspace-panel resource-workspace-linked-item-section resource-workspace-linked-item-section--deployment-information">
        <div className="resource-workspace-section-title">
          <div className="resource-workspace-section-title__row resource-workspace-section-title__row--split">
            <h2>Information</h2>
            {isEndpoint ? (
              <span className={`resource-workspace-status-pill resource-workspace-status-pill--${endpointDeploymentOnline ? "online" : "offline"}`}>
                {endpointDeploymentStatusLabel}
              </span>
            ) : null}
          </div>
          <p>{getDeploymentTargetDescription(defaults)}</p>
        </div>
        <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
          <Fact label="Deployment" value={deploymentName} />
          <Fact label="Type" value={defaults?.deploymentLabel || "Deployment"} />
          <Fact label={targetLabel} value={targetValue} />
          <Fact label="Active" value={isRecordActive(deployment) ? "Active" : "Inactive"} />
          <Fact label={isEndpoint ? "Endpoint Scope" : "Deployment Scope"} value={isEndpoint
            ? `${(getArray(deployment, "EntityEndpoints", "entityEndpoints").length + getArray(deployment, "ModelEndpoints", "modelEndpoints").length).toLocaleString()} endpoint rows`
            : deploymentScopeSummary} />
          {endpointWarehouseDeploymentName ? <Fact label="Warehouse Deployment" value={endpointWarehouseDeploymentName} /> : null}
          <Fact label={resourceLabel} value={destinationName} />
          <Fact label={`${resourceLabel} Type`} value={defaults?.label || resourceLabel} />
          <Fact label={isEndpoint ? "API Route Key" : "System Name"} value={getString(destination, "APIName", "apiName", "SystemName", "systemName")} />
          <Fact label="Resource Configuration" value={getDisplayName(configuration, "Resource configuration")} />
        </dl>
        <div className="resource-workspace-linked-item-section__divider" />
        {runtimeSummary}
        {connectionDetails ? (
          connectionDetails
        ) : null}
      </section>
      {!isEndpoint ? (
        <section className="resource-workspace-panel resource-workspace-linked-item-section resource-workspace-linked-item-section--deployment-events">
          <div className="resource-workspace-section-title">
            <h2>Events</h2>
            <p>Recent deployment-related event activity.</p>
          </div>
          {relatedEvents.length > 0 ? (
            <div className="resource-workspace-record-list">
              {relatedEvents.map((event, index) => (
                <article key={getString(event, "_id", "Id", "id", "UniqueId", "uniqueId") || index} className="resource-workspace-record-row">
                  <strong>{getDisplayName(event, getString(event, "OperationName", "operationName") || "Event")}</strong>
                  <small>{getString(event, "EventStatus", "eventStatus", "Status", "status") || "Status unavailable"}</small>
                </article>
              ))}
            </div>
          ) : (
            <ResourceStateControl compact message="No deployment events are available yet." />
          )}
        </section>
      ) : null}
      {isEndpoint ? (
        <section className="resource-workspace-panel resource-workspace-linked-item-section resource-workspace-linked-item-section--deployment-schema">
          <div className="resource-workspace-section-title">
            <div className="resource-workspace-section-title__row resource-workspace-section-title__row--split">
              <h2>Schema Scopes</h2>
              <ButtonControl
                type="standard-small"
                title={canAddSchemaScope ? "Add Schema Scope" : "No linked Entity Schema versions are available"}
                aria-label="Add Schema Scope"
                disabled={!canAddSchemaScope || schemaScopeActionBusy}
                onClick={openAddSchemaScopeEditor}
              >
                <IconControl iconKey="add" title="Add Schema Scope" width="1.5rem" height="1.5rem" />
              </ButtonControl>
            </div>
            <p>Schema versions and endpoint rows used by this deployment.</p>
            {canManageSchemaScopes && !canAddSchemaScope ? (
              <div className="resource-workspace-access-note resource-workspace-access-note--warning" role="alert">
                Linked Entity Schema version metadata is required before adding a schema scope.
              </div>
            ) : null}
          </div>
          <div className="resource-workspace-record-list">
            {schemaScopeRows.map((scope, index) => {
              const scopeIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier") || `Scope ${index + 1}`;
              const scopeSchemaName = getSchemaScopeDisplaySchemaLabel(schemaScopeSchemaRecords, scope)
                || "Schema metadata missing";
              const scopeVersionLabel = getSchemaScopeDisplayVersionLabel(schemaScopeSchemaRecords, scope)
                || "Schema version metadata missing";
              return (
                <article key={getString(scope, "__ScopeKey") || scopeIdentifier || index} className="resource-workspace-record-row resource-workspace-record-row--schema-scope">
                  <div>
                    <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
                      <Fact label="Scope" value={scopeIdentifier} />
                      <Fact label="Schema" value={scopeSchemaName} />
                      <Fact label="Active Version" value={scopeVersionLabel} />
                      <Fact label="Entity Objects" value={getSchemaScopeEntityObjectCount(scope)} />
                      <Fact label="Endpoint Rows" value={getSchemaScopeEndpointRowCount(scope).toLocaleString()} />
                    </dl>
                  </div>
                  <span className="resource-workspace-record-row-actions">
                    <ButtonControl
                      type="standard-small"
                      title="Configure Entity Schema"
                      aria-label="Configure Entity Schema"
                      disabled={!onConfigureEntitySchema}
                      onClick={() => onConfigureEntitySchema?.(scope)}
                    >
                      <IconControl iconKey="schema" title="Configure Entity Schema" width="1.5rem" height="1.5rem" />
                    </ButtonControl>
                    <ButtonControl
                      type="standard-small"
                      title={`Edit Schema Scope ${scopeIdentifier}`}
                      aria-label={`Edit Schema Scope ${scopeIdentifier}`}
                      disabled={!canManageSchemaScopes || schemaScopeActionBusy}
                      onClick={() => openEditSchemaScopeEditor(scope)}
                    >
                      <IconControl iconKey="edit" title={`Edit Schema Scope ${scopeIdentifier}`} width="1.5rem" height="1.5rem" />
                    </ButtonControl>
                    <ButtonControl
                      type="standard-small"
                      className="resource-workspace-danger-button"
                      title={hasMultipleActiveSchemaScopes
                        ? `Delete Schema Scope ${scopeIdentifier}`
                        : "At least one active schema scope is required."}
                      aria-label={`Delete Schema Scope ${scopeIdentifier}`}
                      disabled={!canManageSchemaScopes || schemaScopeActionBusy || !hasMultipleActiveSchemaScopes}
                      onClick={() => requestDeleteSchemaScope(scope)}
                    >
                      <IconControl iconKey="delete" title={`Delete Schema Scope ${scopeIdentifier}`} width="1.5rem" height="1.5rem" />
                    </ButtonControl>
                  </span>
                </article>
              );
            })}
          </div>
        </section>
      ) : (
        <section className="resource-workspace-panel resource-workspace-linked-item-section resource-workspace-linked-item-section--deployment-schema">
          <div className="resource-workspace-section-title">
            <h2>Schema</h2>
            <p>Schema version and Entity Objects used by this deployment.</p>
          </div>
          <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
            <Fact label="Schema" value={getDisplayName(schema, getDestinationSchemaId(destination) || "Not set")} />
            <Fact label="Active Version" value={schemaVersionLabel || "Not set"} />
            <Fact label="Entity Objects" value={entityObjects.length.toLocaleString()} />
            <Fact label="Scope Rows" value={entityDeployments.length.toLocaleString()} />
          </dl>
        </section>
      )}
    </div>
    <SidePanelFormControl
      metadata={schemaScopeEditorMetadata}
      title={schemaScopeEditor?.mode === "add"
        ? "Add Schema Scope"
        : `Edit ${getString(schemaScopeEditor?.scope, "SchemaScopeIdentifier", "schemaScopeIdentifier") || "Schema Scope"}`}
      formFields={schemaScopeEditorMetadata.Fields}
      open={Boolean(schemaScopeEditor)}
      itemData={schemaScopeEditor?.scope}
      setItemData={updateSchemaScopeEditorData}
      setOpenEditSidePanel={(isOpen) => {
        if (!isOpen) closeSchemaScopeEditor();
      }}
      onUpdateSidePanel={() => {}}
      onCancel={closeSchemaScopeEditor}
      onSave={saveSchemaScopeEditor}
      saveButtonText={schemaScopeActionBusy ? "Saving..." : "Save"}
      viewOnly={!canManageSchemaScopes}
    />
    {schemaScopeEditor?.error ? (
      <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">
        {schemaScopeEditor.error}
      </div>
    ) : null}
    <DialogControl
      controlOrigin="schema-scope-delete"
      title={deleteSchemaScopeRequest?.blocked ? "Schema Scope Required" : "Delete Schema Scope"}
      openDialog={Boolean(deleteSchemaScopeRequest)}
      okText={deleteSchemaScopeRequest?.blocked ? "OK" : schemaScopeActionBusy ? "Deleting..." : "Delete"}
      cancelText={deleteSchemaScopeRequest?.blocked ? "" : "Cancel"}
      onOk={deleteSchemaScopeRequest?.blocked ? closeDeleteSchemaScopeDialog : confirmDeleteSchemaScope}
      onCancel={closeDeleteSchemaScopeDialog}
      onX={closeDeleteSchemaScopeDialog}
      disableOk={schemaScopeActionBusy}
      disableCancel={schemaScopeActionBusy}
    >
      <div className="resource-workspace-linked-record-add-dialog">
        <p>
          {deleteSchemaScopeRequest?.blocked
            ? "At least one active schema scope is required for this deployment."
            : `Delete "${getString(deleteSchemaScopeRequest?.scope, "SchemaScopeIdentifier", "schemaScopeIdentifier") || "Schema Scope"}" from schema scopes? This cannot be undone.`}
        </p>
        {schemaScopeActionState?.error ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{schemaScopeActionState.error}</div> : null}
      </div>
    </DialogControl>
    </>
  );
};

const WAREHOUSE_VIEW_DEFAULT_METADATA = {
  Name: "",
  ItemName: "WarehouseView",
  DisplayName: "Warehouse View",
  Fields: [
    {
      FieldName: "DisplayName",
      DisplayName: "Display Name",
      Description: "Friendly name shown in designer lists, destination pickers, and AI context.",
      FieldType: SystemTypes.UIFieldType.TextBox,
      TabId: "Identity|2",
      DisplaySequence: 1,
      IsRequired: false
    },
    {
      FieldName: "Description",
      DisplayName: "Description",
      Description: "Short business description of what this view returns and when to use it.",
      FieldType: SystemTypes.UIFieldType.TextArea || SystemTypes.UIFieldType.TextBox,
      TabId: "Identity|2",
      DisplaySequence: 2,
      IsRequired: false
    },
    {
      FieldName: "ViewName",
      DisplayName: "View Name",
      Description: "Physical view name parsed from the SQL view header.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Identity|2",
      DisplaySequence: 10,
      IsRequired: true
    },
    {
      FieldName: "SchemaName",
      DisplayName: "Schema Name",
      Description: "Database schema parsed from the SQL view header. Defaults to the deployment or destination schema when the SQL header does not include one.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Identity|2",
      DisplaySequence: 20,
      IsRequired: false
    },
    {
      FieldName: "QualifiedViewName",
      DisplayName: "Qualified View Name",
      Description: "Resolved schema-qualified view name used by deploy and search operations.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Identity|2",
      DisplaySequence: 30,
      IsRequired: false
    },
    {
      FieldName: "DatabaseName",
      DisplayName: "Database Name",
      Description: "Deployment database for this warehouse view.",
      FieldType: SystemTypes.UIFieldType.TextBox,
      TabId: "Identity|2",
      DisplaySequence: 90,
      IsReadOnly: true,
      IsRequired: false
    },
    {
      FieldName: "SqlDefinition",
      DisplayName: "SQL Definition",
      Description: "Create-or-replace SQL definition for the managed warehouse view.",
      FieldType: SystemTypes.UIFieldType.SQL || SystemTypes.UIFieldType.TextArea || SystemTypes.UIFieldType.TextBox,
      Format: SystemTypes.UIFormats?.SQL,
      TabId: "Definition|1",
      GroupId: "SQL|1",
      DisplaySequence: 1,
      IsRequired: true
    },
    {
      FieldName: "Sequence",
      DisplayName: "Deploy Sequence",
      Description: "Execution order when multiple managed views are deployed for the same warehouse deployment and schema version.",
      FieldType: SystemTypes.UIFieldType.TextBox,
      Format: SystemTypes.UIFormats?.INT,
      TabId: "Definition|1",
      GroupId: "Deployment|2",
      DisplaySequence: 21,
      IsRequired: true
    },
    {
      FieldName: "ValidationState",
      DisplayName: "Validation State",
      Description: "Last known SQL validation state for this view.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Status|4",
      DisplaySequence: 1,
      IsRequired: false
    },
    {
      FieldName: "LastValidationMessage",
      DisplayName: "Validation Message",
      Description: "Last validation message returned by the warehouse provider.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Status|4",
      DisplaySequence: 2,
      IsRequired: false
    },
    {
      FieldName: "DeployState",
      DisplayName: "Deploy State",
      Description: "Last known deployment state for this view.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Status|4",
      DisplaySequence: 10,
      IsRequired: false
    },
    {
      FieldName: "LastDeployMessage",
      DisplayName: "Deploy Message",
      Description: "Last deploy message returned by the warehouse provider.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Status|4",
      DisplaySequence: 11,
      IsRequired: false
    },
    {
      FieldName: "AIPromptContext",
      DisplayName: "AI Context",
      Description: "Additional business rules, naming conventions, or SQL guidance the AI should use when helping build this view.",
      FieldType: SystemTypes.UIFieldType.TextArea || SystemTypes.UIFieldType.TextBox,
      TabId: "AI|3",
      GroupId: "Prompt Context|1",
      DisplaySequence: 1,
      IsRequired: false
    },
    {
      FieldName: "Fields",
      DisplayName: "Fields",
      Description: "Output field metadata returned by this managed warehouse view.",
      FieldType: SystemTypes.UIFieldType.SubGrid,
      Format: SystemTypes.UIFormats?.GRID,
      TabId: "Fields|5",
      GroupId: "Output Fields|1",
      DisplaySequence: 1,
      IsRequired: false
    }
  ]
};

const WAREHOUSE_VIEW_FIELD_FORM_METADATA = {
  Name: "WarehouseViewField",
  ItemName: "WarehouseViewField",
  DisplayName: "Warehouse View Field",
  SidePanelFormWidth: 44,
  Fields: [
    {
      FieldName: "FieldName",
      DisplayName: "Field Name",
      Description: "Column name returned by the SQL view.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Field|1",
      DisplaySequence: 1,
      IsRequired: true
    },
    {
      FieldName: "DisplayName",
      DisplayName: "Display Name",
      Description: "Friendly label used for grids, destination selection, and AI schema context.",
      FieldType: SystemTypes.UIFieldType.TextBox,
      TabId: "Field|1",
      DisplaySequence: 2,
      IsRequired: true
    },
    {
      FieldName: "Description",
      DisplayName: "Description",
      Description: "Business meaning or usage notes for this output column.",
      FieldType: SystemTypes.UIFieldType.TextArea || SystemTypes.UIFieldType.TextBox,
      TabId: "Field|1",
      DisplaySequence: 3,
      IsRequired: false
    },
    {
      FieldName: "DataTypeName",
      DisplayName: "Data Type",
      Description: "Provider or CLR type name detected for the view column.",
      FieldType: SystemTypes.UIFieldType.Label,
      TabId: "Field|1",
      DisplaySequence: 4,
      IsRequired: false
    },
    {
      FieldName: "PrimaryField",
      DisplayName: "Primary Display Field",
      Description: "Marks this column as the main label or identity field when the view is shown as a selectable destination item.",
      FieldType: SystemTypes.UIFieldType.CheckBox,
      Format: SystemTypes.UIFormats?.BOOL,
      TabId: "Display|2",
      DisplaySequence: 1,
      IsRequired: false
    },
    {
      FieldName: "CanFilter",
      DisplayName: "Filterable",
      Description: "Allow filtering on this field in search and grid surfaces.",
      FieldType: SystemTypes.UIFieldType.CheckBox,
      Format: SystemTypes.UIFormats?.BOOL,
      TabId: "Display|2",
      DisplaySequence: 2,
      IsRequired: false
    },
    {
      FieldName: "CanSort",
      DisplayName: "Sortable",
      Description: "Allow sorting on this field in grid surfaces.",
      FieldType: SystemTypes.UIFieldType.CheckBox,
      Format: SystemTypes.UIFormats?.BOOL,
      TabId: "Display|2",
      DisplaySequence: 3,
      IsRequired: false
    },
    {
      FieldName: "ShowOnGrid",
      DisplayName: "Show in Grid",
      Description: "Show this field as a grid column.",
      FieldType: SystemTypes.UIFieldType.CheckBox,
      Format: SystemTypes.UIFormats?.BOOL,
      TabId: "Display|2",
      DisplaySequence: 4,
      IsRequired: false
    },
    {
      FieldName: "ShowOnCard",
      DisplayName: "Show in Card",
      Description: "Show this field on compact cards.",
      FieldType: SystemTypes.UIFieldType.CheckBox,
      Format: SystemTypes.UIFormats?.BOOL,
      TabId: "Display|2",
      DisplaySequence: 5,
      IsRequired: false
    },
    {
      FieldName: "IsAIAnalytics",
      DisplayName: "Include in AI",
      Description: "When enabled, this field is included in AI schema metadata for warehouse view authoring.",
      FieldType: SystemTypes.UIFieldType.CheckBox,
      Format: SystemTypes.UIFormats?.BOOL,
      TabId: "AI|3",
      DisplaySequence: 1,
      IsRequired: false
    },
    {
      FieldName: "AIPromptContext",
      DisplayName: "AI Context",
      Description: "Additional context or constraints for AI when using this field.",
      FieldType: SystemTypes.UIFieldType.TextArea || SystemTypes.UIFieldType.TextBox,
      TabId: "AI|3",
      DisplaySequence: 2,
      IsRequired: false
    }
  ]
};

const WAREHOUSE_VIEW_FIELD_CARD_FIELDS = [
  { FieldName: "DataTypeName", DisplayName: "Data Type", ShowOnCard: true },
  { FieldName: "DisplayName", DisplayName: "Display Name", ShowOnCard: true },
  { FieldName: "Description", DisplayName: "Description", ShowOnCard: true }
];

const getWarehouseViewFieldKey = (field, index = 0) =>
  getString(field, "_id", "Id", "id", "UniqueId", "uniqueId", "WarehouseViewFieldId", "warehouseViewFieldId")
  || getString(field, "FieldName", "fieldName")
  || index;

const stripWarehouseViewFieldUiState = (field) => {
  const { __fieldKey, ...cleanField } = field || {};
  return cleanField;
};

const WarehouseViewFieldsTab = ({
  fields,
  disabled,
  busy,
  onRefreshFields,
  onFieldsChange
}) => {
  const [editingField, setEditingField] = useState(null);
  const fieldRows = useMemo(() => getArray({ Fields: fields }, "Fields").map((field, index) => ({
    ...field,
    __fieldKey: getWarehouseViewFieldKey(field, index),
    FieldName: getString(field, "FieldName", "fieldName") || `Field ${index + 1}`,
    DisplayName: getString(field, "DisplayName", "displayName") || getString(field, "FieldName", "fieldName") || `Field ${index + 1}`,
    DataTypeName: getString(field, "DataTypeName", "dataTypeName") || "System.String",
    IsActive: getValue(field, "IsActive", "isActive") !== false
  })), [fields]);

  const updateField = useCallback((updatedField) => {
    if (!updatedField) return;
    const updatedKey = getWarehouseViewFieldKey(updatedField);
    const nextFields = fieldRows.map((field) =>
      String(getWarehouseViewFieldKey(field)) === String(updatedKey)
        ? { ...field, ...updatedField }
        : field).map(stripWarehouseViewFieldUiState);
    onFieldsChange?.(nextFields);
    setEditingField(null);
  }, [fieldRows, onFieldsChange]);

  const setFieldActive = useCallback((fieldToUpdate, isActive) => {
    if (!fieldToUpdate) return;
    const updatedKey = getWarehouseViewFieldKey(fieldToUpdate);
    const nextFields = fieldRows.map((field) =>
      String(getWarehouseViewFieldKey(field)) === String(updatedKey)
        ? { ...field, IsActive: isActive }
        : field).map(stripWarehouseViewFieldUiState);
    onFieldsChange?.(nextFields);
  }, [fieldRows, onFieldsChange]);

  return (
    <div className="resource-workspace-warehouse-view-fields-tab">
      <CardGridControl
        className="resource-workspace-warehouse-view-fields-grid"
        rows={fieldRows}
        cardFields={WAREHOUSE_VIEW_FIELD_CARD_FIELDS}
        rowKey={(field) => field.__fieldKey}
        cardTitleResolver={(field) => getString(field, "FieldName", "fieldName")}
        readOnly={disabled}
        disableInlineEdit
        disableCardClickEdit
        searchable
        activeFilterEnabled
        emptyContent="No fields have been loaded for this view."
        toolbarLeadingContent={<span>{fieldRows.length.toLocaleString()} fields</span>}
        toolbarActions={[
          {
            key: "refresh-fields",
            title: busy ? "Refreshing fields..." : "Refresh Fields",
            label: busy ? "Refreshing..." : "Refresh Fields",
            iconKey: "refresh",
            disabled: disabled || busy,
            onClick: onRefreshFields
          }
        ]}
        headerIconActions={[
          {
            key: "disable-field",
            title: "Disable field",
            iconKey: "undo",
            visible: (field) => getValue(field, "IsActive", "isActive") !== false,
            disabled: disabled,
            onClick: (field) => setFieldActive(field, false)
          },
          {
            key: "enable-field",
            title: "Enable field",
            iconKey: "success",
            visible: (field) => getValue(field, "IsActive", "isActive") === false,
            disabled: disabled,
            onClick: (field) => setFieldActive(field, true)
          },
          {
            key: "edit-field",
            title: "Edit field",
            iconKey: "edit",
            disabled: disabled,
            onClick: (field) => setEditingField(field)
          }
        ]}
      />
      <SidePanelFormControl
        metadata={WAREHOUSE_VIEW_FIELD_FORM_METADATA}
        title={editingField ? `Edit ${getString(editingField, "FieldName", "fieldName")}` : "Edit Field"}
        formFields={WAREHOUSE_VIEW_FIELD_FORM_METADATA.Fields}
        open={Boolean(editingField)}
        itemData={editingField}
        setItemData={setEditingField}
        setOpenEditSidePanel={(open) => {
          if (!open) setEditingField(null);
        }}
        onUpdateSidePanel={() => {}}
        onCancel={() => setEditingField(null)}
        onSave={updateField}
        saveButtonText="Save"
      />
    </div>
  );
};

const getWarehouseViewMetadataFields = (metadata) => getArray(metadata, "Fields", "fields");

const buildWarehouseViewFormItem = (view, databaseName = "") => ({
  ...(view || {}),
  DisplayName: getString(view, "DisplayName", "displayName"),
  Description: getString(view, "Description", "description"),
  DatabaseName: databaseName || getString(view, "DatabaseName", "databaseName"),
  SchemaName: getString(view, "SchemaName", "schemaName"),
  ViewName: getString(view, "ViewName", "viewName"),
  QualifiedViewName: getString(view, "QualifiedViewName", "qualifiedViewName"),
  SqlDefinition: getString(view, "SqlDefinition", "sqlDefinition", "DatabaseView", "databaseView"),
  Sequence: getValue(view, "Sequence", "sequence") ?? 999,
  ValidationState: getString(view, "ValidationState", "validationState") || "Unknown",
  LastValidationMessage: getString(view, "LastValidationMessage", "lastValidationMessage"),
  DeployState: getString(view, "DeployState", "deployState") || "Not Deployed",
  LastDeployMessage: getString(view, "LastDeployMessage", "lastDeployMessage"),
  AIPromptContext: getString(view, "AIPromptContext", "aiPromptContext"),
  Fields: getArray(view, "Fields", "fields")
});

const getWarehouseViewId = (view) => getString(view, "_id", "Id", "id", "UniqueId", "uniqueId", "WarehouseViewId", "warehouseViewId", "__draftId");
const getWarehouseViewSessionReferenceId = (view) => getString(view, "_id", "Id", "id", "UniqueId", "uniqueId", "WarehouseViewId", "warehouseViewId");
const getWarehouseViewName = (view) =>
  getString(view, "DisplayName", "displayName")
  || getString(view, "QualifiedViewName", "qualifiedViewName")
  || getString(view, "ViewName", "viewName")
  || "Warehouse View";
const getWarehouseViewDiscoveryKey = (view, index = 0, defaultSchemaName = "dbo") =>
  `${getString(view, "DatabaseName", "databaseName") || ""}.${getString(view, "SchemaName", "schemaName") || defaultSchemaName}.${getString(view, "ViewName", "viewName") || "View"}.${index}`;
const isDefaultNewWarehouseView = (view) =>
  normalizeCompare(getString(view, "ViewName", "viewName")) === "newwarehouseview"
  || normalizeCompare(getString(view, "DisplayName", "displayName")) === "newwarehouseview"
  || normalizeCompare(getString(view, "DisplayName", "displayName")) === "new warehouse view";

const WarehouseViewAIPanel = React.lazy(() => import("../../../components/AIChat/AIPanel"));
const EntityScopeAIPanel = React.lazy(() => import("../../../components/AIChat/AIPanel"));

const WAREHOUSE_VIEW_DISCOVERY_CARD_FIELDS = [
  { FieldName: "DisplayName", DisplayName: "View", CardDisplayName: "", ShowOnCard: true },
  { FieldName: "DatabaseName", DisplayName: "Database", ShowOnCard: true },
  { FieldName: "SchemaName", DisplayName: "Schema", ShowOnCard: true },
  { FieldName: "ViewName", DisplayName: "DB View", ShowOnCard: true },
  { FieldName: "Fields", DisplayName: "Columns", ShowOnCard: true },
  { FieldName: "ManagedState", DisplayName: "Status", ShowOnCard: true }
];

const WAREHOUSE_VIEW_DEPLOY_CARD_FIELDS = [
  { FieldName: "DisplayName", DisplayName: "View", CardDisplayName: "", ShowOnCard: true },
  { FieldName: "DatabaseName", DisplayName: "Database", ShowOnCard: true },
  { FieldName: "SchemaName", DisplayName: "Schema", ShowOnCard: true },
  { FieldName: "ViewName", DisplayName: "DB View", ShowOnCard: true },
  { FieldName: "DeployState", DisplayName: "Deploy State", ShowOnCard: true },
  { FieldName: "Fields", DisplayName: "Fields", ShowOnCard: true }
];

const getOperationItemsFromResponse = (response) => {
  const candidates = [
    response?.Item,
    response?.item,
    response?.Result?.Item,
    response?.Result?.item,
    response?.result?.Item,
    response?.result?.item,
    ...getArray(response, "Items", "items"),
    ...getArray(response?.Result, "Items", "items"),
    ...getArray(response?.result, "Items", "items")
  ];
  return candidates.filter(Boolean);
};

const getOperationMessageDetailsFromResponse = (response) => [
  ...getArray(response, "MessageDetails", "messageDetails"),
  ...getArray(response?.Result, "MessageDetails", "messageDetails"),
  ...getArray(response?.result, "MessageDetails", "messageDetails")
].filter(Boolean);

const getOperationMessageFromResponse = (response) =>
  getString(response, "DisplayMessage", "displayMessage", "Message", "message")
  || getString(response?.Result, "DisplayMessage", "displayMessage", "Message", "message")
  || getString(response?.result, "DisplayMessage", "displayMessage", "Message", "message")
  || getString(getArray(response, "BaseResults", "baseResults")[0], "DisplayMessage", "displayMessage", "Message", "message")
  || getString(getArray(response?.Result, "BaseResults", "baseResults")[0], "DisplayMessage", "displayMessage", "Message", "message")
  || getString(getArray(response?.result, "BaseResults", "baseResults")[0], "DisplayMessage", "displayMessage", "Message", "message");

const dispatchAiPanelCommandFromWorkspaceOperationResponse = (response) => {
  const candidates = [
    response,
    response?.Result,
    response?.result,
    ...(Array.isArray(response) ? response : []),
    ...getArray(response, "Items", "items"),
    ...getArray(response?.Result, "Items", "items"),
    ...getArray(response?.result, "Items", "items")
  ].filter(Boolean);

  const commandResult = candidates.find((candidate) => candidate?.UICommands || candidate?.uiCommands);
  if (!commandResult) return false;
  return dispatchAiPanelCommand(commandResult.UICommands || commandResult.uiCommands, commandResult);
};

const isOperationSuccessful = (response) => {
  const result = response?.Result || response?.result || response;
  return !(result?.Success === false || result?.success === false);
};

const getOperationIdentity = (operation) =>
  getString(operation, "Name", "OperationTypeId", "OpeationTypeId", "Value", "_id", "Id", "id");

const isManagedWarehouseViewDiscoveryRow = (view) =>
  getBoolean(view?.CustomFields, "IsAlreadyManaged", "isAlreadyManaged")
  || getBoolean(view, "IsAlreadyManaged", "isAlreadyManaged");
const WAREHOUSE_VIEW_AI_REFERENCE_ITEM_NAME = "WarehouseView";

export const ResourceConfigurationWarehouseViewDesignerScreen = ({
  destinationState,
  deployment,
  onWarehouseViewActionsChange
}) => {
  const resourceDomainContext = useResourceDomainContext() || {};
  const { managers, coreContext, itemServiceEnum } = resourceDomainContext;
  const warehouseViewMgr = managers?.warehouseView;
  const aiChatSessionMgr = managers?.aiChatSession;
  const warehouseViewOperationDialog = useOperationDialog(itemServiceEnum?.WarehouseView || "WarehouseView");
  const { destination, defaults, loading = false, error = "" } = destinationState || {};
  const [views, setViews] = useState([]);
  const [viewSearchTerm, setViewSearchTerm] = useState("");
  const [selectedViewId, setSelectedViewId] = useState("");
  const [draft, setDraft] = useState(null);
  const [aiSession, setAiSession] = useState(null);
  const [aiSessionLoading, setAiSessionLoading] = useState(false);
  const [aiSessionLookupReferenceId, setAiSessionLookupReferenceId] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [screenError, setScreenError] = useState("");
  const [validationResultDialog, setValidationResultDialog] = useState(null);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployResultDialog, setDeployResultDialog] = useState(null);
  const [deployError, setDeployError] = useState("");
  const [selectedDeployViewKeys, setSelectedDeployViewKeys] = useState([]);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [discoveredViews, setDiscoveredViews] = useState([]);
  const [selectedDiscoveredViewKeys, setSelectedDiscoveredViewKeys] = useState([]);
  const [deleteViewRequest, setDeleteViewRequest] = useState(null);
  const [pendingViewSelection, setPendingViewSelection] = useState(null);
  const [pendingDiscardAction, setPendingDiscardAction] = useState(null);
  const [discoverError, setDiscoverError] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const selectedValidationViewIdRef = useRef("");
  const selectedDraftViewIdRef = useRef("");
  const draftDirtyRef = useRef(false);
  const draftRef = useRef(null);
  const pendingDiscardActionRef = useRef(null);

  const deploymentId = getDeploymentId(deployment);
  const warehouseId = getId(destination);
  const schemaVersionId = getString(deployment, "CurrentSchemaVersionId", "currentSchemaVersionId", "SchemaVersionId", "schemaVersionId")
    || getDestinationActiveSchemaVersionId(destination);
  const databaseName = getString(deployment, "DBName", "dbName", "SystemName", "systemName")
    || getString(destination, "DefaultDBName", "defaultDBName", "SystemName", "systemName");
  const defaultSchemaName = getString(deployment, "SchemaName", "schemaName", "DefaultSchemaName", "defaultSchemaName")
    || getString(destination, "DefaultSchemaName", "defaultSchemaName", "SchemaName", "schemaName")
    || "dbo";
  const deploymentName = getDeploymentDisplayName(deployment, destination);
  const queryParams = useMemo(() => ({
    wid: warehouseId,
    wId: warehouseId,
    warehouseId,
    wdid: deploymentId,
    wdId: deploymentId,
    warehouseDeploymentId: deploymentId,
    referenceid: deploymentId
  }), [deploymentId, warehouseId]);
  const warehouseViewFormMetadata = WAREHOUSE_VIEW_DEFAULT_METADATA;
  const warehouseViewFormFields = useMemo(
    () => getWarehouseViewMetadataFields(warehouseViewFormMetadata),
    [warehouseViewFormMetadata]
  );
  const getWarehouseViewOperationDefinition = useCallback((operationName) => {
    const operationKey = normalizeCompare(operationName);
    return (Array.isArray(coreContext?.Operations) ? coreContext.Operations : []).find((operation) =>
      normalizeCompare(getOperationIdentity(operation)) === operationKey
    ) || null;
  }, [coreContext?.Operations]);
  const draftFormItem = useMemo(() => buildWarehouseViewFormItem(draft, databaseName), [databaseName, draft]);
  const canManageWarehouseViews = Boolean(deployment && normalizeCompare(defaults?.managerKey) === "warehouse");
  const discoveredViewRows = useMemo(() => discoveredViews.map((view, index) => ({
    ...view,
    __discoveryKey: getWarehouseViewDiscoveryKey(view, index, defaultSchemaName),
    DatabaseName: getString(view, "DatabaseName", "databaseName") || databaseName || "Database",
    SchemaName: getString(view, "SchemaName", "schemaName") || defaultSchemaName,
    ViewName: getString(view, "ViewName", "viewName") || "View",
    DisplayName: getWarehouseViewName(view),
    ManagedState: isManagedWarehouseViewDiscoveryRow(view) ? "Managed" : "Available"
  })), [databaseName, defaultSchemaName, discoveredViews]);
  const availableDiscoveredViewKeys = useMemo(
    () => discoveredViewRows.map((view) => view.__discoveryKey),
    [discoveredViewRows]
  );
  const selectedDiscoveredViewKeySet = useMemo(
    () => new Set(selectedDiscoveredViewKeys.map((key) => String(key))),
    [selectedDiscoveredViewKeys]
  );
  const allDiscoveredViewsSelected = availableDiscoveredViewKeys.length > 0
    && availableDiscoveredViewKeys.every((key) => selectedDiscoveredViewKeySet.has(String(key)));
  const toggleAllDiscoveredViews = useCallback(() => {
    setSelectedDiscoveredViewKeys(allDiscoveredViewsSelected ? [] : availableDiscoveredViewKeys);
  }, [allDiscoveredViewsSelected, availableDiscoveredViewKeys]);
  const discoveredViewToolbarLeadingContent = useMemo(() => (
    <div className="resource-wizard__entity-scope-selector-toolbar">
      <span>{selectedDiscoveredViewKeys.length} selected</span>
      <div className="resource-wizard__entity-scope-selector-actions">
        <ButtonControl
          type="cancel-small"
          onClick={toggleAllDiscoveredViews}
          disabled={discovering || availableDiscoveredViewKeys.length === 0}
        >
          {allDiscoveredViewsSelected ? "Unselect All" : "Select All"}
        </ButtonControl>
      </div>
    </div>
  ), [
    allDiscoveredViewsSelected,
    availableDiscoveredViewKeys.length,
    discovering,
    selectedDiscoveredViewKeys.length,
    toggleAllDiscoveredViews
  ]);
  const deployableViews = useMemo(() => views
    .filter((view) => isRecordActive(view) && getValue(view, "IsDeleted", "isDeleted") !== true)
    .map((view) => ({
      ...view,
      __deployKey: getWarehouseViewId(view)
    }))
    .filter((view) => view.__deployKey), [views]);
  const draftHasSearchableFields = useMemo(() => getArray(draft, "Fields", "fields")
    .some((field) => getValue(field, "IsActive", "isActive") !== false
      && getValue(field, "IsDeleted", "isDeleted") !== true
      && getString(field, "FieldName", "fieldName")), [draft]);
  const draftIsDeployed = normalizeCompare(getString(draft, "DeployState", "deployState")) === "deployed";
  const draftSearchDisabledReason = !draft
    ? "Select or add a warehouse view to search or export."
    : draftDirtyRef.current
      ? "Save the warehouse view before using Search/Export."
      : isRecordActive(draft) === false
        ? "Enable the warehouse view before using Search/Export."
        : !draftIsDeployed
          ? "Deploy the warehouse view before using Search/Export."
          : !draftHasSearchableFields
            ? "Refresh Fields and save the warehouse view before using Search/Export."
            : "";
  const selectedDeployViewKeySet = useMemo(
    () => new Set(selectedDeployViewKeys.map((key) => String(key))),
    [selectedDeployViewKeys]
  );
  const allDeployViewsSelected = deployableViews.length > 0
    && deployableViews.every((view) => selectedDeployViewKeySet.has(String(view.__deployKey)));
  const toggleAllDeployViews = useCallback(() => {
    setSelectedDeployViewKeys(allDeployViewsSelected ? [] : deployableViews.map((view) => view.__deployKey));
    setDeployError("");
  }, [allDeployViewsSelected, deployableViews]);
  const deployViewToolbarLeadingContent = useMemo(() => (
    <div className="resource-wizard__entity-scope-selector-toolbar">
      <span>{selectedDeployViewKeys.length} selected</span>
      <div className="resource-wizard__entity-scope-selector-actions">
        <ButtonControl
          type="cancel-small"
          onClick={toggleAllDeployViews}
          disabled={busyAction === "deploy" || deployableViews.length === 0}
        >
          {allDeployViewsSelected ? "Unselect All" : "Select All"}
        </ButtonControl>
      </div>
    </div>
  ), [
    allDeployViewsSelected,
    busyAction,
    deployableViews.length,
    selectedDeployViewKeys.length,
    toggleAllDeployViews
  ]);
  const aiReferenceId = getWarehouseViewSessionReferenceId(draft);
  const aiOperationReady = Boolean(aiReferenceId) && aiSessionLookupReferenceId === aiReferenceId && !aiSessionLoading;
  const aiOperationName = Operations.AIChatSessionItem?.AIQuestionWarehouseView || "AIQuestionWarehouseView";

  const runWarehouseViewOperation = useCallback(async ({ operationName, item = null, params = {}, action = operationName, allowFailure = false }) => {
    if (!warehouseViewMgr?.RunOperation) throw new Error("Warehouse view service is not available.");
    setBusyAction(action);
    setScreenError("");
    try {
      const response = await warehouseViewMgr.RunOperation({
        operationName,
        item: item || {},
        queryParams: { ...queryParams, ...params },
        isItem: Boolean(item),
        isWarning: true,
        supressAllErrors: true
      });
      if (!isOperationSuccessful(response) && !allowFailure) {
        const message = getOperationMessageFromResponse(response)
          || `${operationName} failed.`;
        throw new Error(message);
      }
      return response;
    } finally {
      setBusyAction("");
    }
  }, [queryParams, warehouseViewMgr]);

  const applyCleanDraft = useCallback((nextDraft) => {
    draftDirtyRef.current = false;
    draftRef.current = nextDraft || null;
    setDraft(nextDraft || null);
  }, []);

  const applyWorkingDraft = useCallback((nextDraft) => {
    draftDirtyRef.current = true;
    draftRef.current = nextDraft || null;
    setDraft(nextDraft || null);
  }, []);

  const applyDraftPreservingDirtyState = useCallback((nextDraft) => {
    const wasDirty = draftDirtyRef.current;
    draftRef.current = nextDraft || null;
    setDraft(nextDraft || null);
    draftDirtyRef.current = wasDirty;
  }, []);

  const restoreSelectedSavedDraft = useCallback(() => {
    const selected = views.find((view) => normalizeCompare(getWarehouseViewId(view)) === normalizeCompare(selectedViewId));
    if (selected) {
      selectedDraftViewIdRef.current = selectedViewId;
      applyCleanDraft(selected);
      return;
    }
    draftDirtyRef.current = false;
    draftRef.current = draft || null;
  }, [applyCleanDraft, draft, selectedViewId, views]);

  const requestDiscardChanges = useCallback((action, label = "continue") => {
    if (!draftDirtyRef.current) {
      action?.();
      return;
    }
    pendingDiscardActionRef.current = action || null;
    setPendingDiscardAction({ label });
  }, []);

  const runPendingDiscardAction = useCallback(() => {
    const action = pendingDiscardActionRef.current;
    pendingDiscardActionRef.current = null;
    setPendingDiscardAction(null);
    restoreSelectedSavedDraft();
    action?.();
  }, [restoreSelectedSavedDraft]);

  const cancelPendingDiscardAction = useCallback(() => {
    pendingDiscardActionRef.current = null;
    setPendingDiscardAction(null);
  }, []);

  const loadViews = useCallback(async () => {
    if (!deploymentId || !warehouseViewMgr?.RunOperation) return;
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.ListWarehouseViews || "ListWarehouseViews",
        item: null,
        action: "load"
      });
      const nextViews = getOperationItemsFromResponse(response);
      setViews(nextViews);
      setSelectedViewId((current) => current || getWarehouseViewId(nextViews[0]) || "");
      if (nextViews[0]) {
        const hasCurrentDraft = Boolean(draftRef.current);
        if (!hasCurrentDraft) applyCleanDraft(nextViews[0]);
      }
    } catch (loadError) {
      setScreenError(loadError?.message || "Warehouse views could not be loaded.");
    }
  }, [applyCleanDraft, deploymentId, runWarehouseViewOperation, warehouseViewMgr?.RunOperation]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  useEffect(() => {
    if (!selectedViewId) return;
    if (normalizeCompare(selectedValidationViewIdRef.current) !== normalizeCompare(selectedViewId)) {
      selectedValidationViewIdRef.current = selectedViewId;
      setScreenError("");
      setValidationResultDialog(null);
    }
    const selected = views.find((view) => normalizeCompare(getWarehouseViewId(view)) === normalizeCompare(selectedViewId));
    const selectedDraftChanged = normalizeCompare(selectedDraftViewIdRef.current) !== normalizeCompare(selectedViewId);
    const draftSelectionChanged = normalizeCompare(getWarehouseViewId(draftRef.current)) !== normalizeCompare(selectedViewId);
    if (selected && (selectedDraftChanged || draftSelectionChanged) && !draftDirtyRef.current) {
      selectedDraftViewIdRef.current = selectedViewId;
      applyCleanDraft(selected);
    }
  }, [applyCleanDraft, selectedViewId, views]);

  useEffect(() => {
    if (!draftDirtyRef.current) draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    if (!aiReferenceId || !aiChatSessionMgr?.Query) {
      setAiSession(null);
      setAiSessionLoading(false);
      setAiSessionLookupReferenceId("");
      return undefined;
    }

    let cancelled = false;
    setAiSession(null);
    setAiSessionLoading(true);
    setAiSessionLookupReferenceId("");

    const loadAiSession = async () => {
      try {
        const result = await aiChatSessionMgr.Query({
          model: "x => x.ReferenceId == referenceId && x.ReferenceItemName == referenceItemName && x.IsDeleted == false && x.IsActive == true",
          params: {
            referenceId: aiReferenceId,
            referenceItemName: WAREHOUSE_VIEW_AI_REFERENCE_ITEM_NAME
          }
        });
        if (cancelled) return;
        const firstSession = result?.Items?.first?.()
          ?? (Array.isArray(result?.Items) ? result.Items[0] : result?.Items)
          ?? null;
        setAiSession(result?.Success && firstSession ? firstSession : null);
      } catch {
        if (!cancelled) setAiSession(null);
      } finally {
        if (!cancelled) {
          setAiSessionLookupReferenceId(aiReferenceId);
          setAiSessionLoading(false);
        }
      }
    };

    loadAiSession();
    return () => {
      cancelled = true;
    };
  }, [aiChatSessionMgr, aiReferenceId]);

  const handleWarehouseViewFormChange = useCallback((value, fieldName, updatedItem) => {
    draftDirtyRef.current = true;
    draftRef.current = {
      ...(draftRef.current || draft || {}),
      ...(updatedItem || {}),
      ...(fieldName ? { [fieldName]: fieldName === "Sequence" ? Number(value || 999) : value } : {}),
      DatabaseName: databaseName
    };
  }, [databaseName, draft]);

  const createBlankDraft = useCallback(async () => {
    if (views.some(isDefaultNewWarehouseView)) return;
    const nextViewName = "NewWarehouseView";
    const nextDisplayName = "New Warehouse View";
    const nextDraft = {
      WarehouseId: warehouseId,
      WarehouseDeploymentId: deploymentId,
      SchemaVersionId: schemaVersionId,
      DatabaseName: databaseName,
      SchemaName: "",
      ViewName: nextViewName,
      QualifiedViewName: `${defaultSchemaName}.${nextViewName}`,
      DisplayName: nextDisplayName,
      Description: "",
      Sequence: 999,
      SqlDefinition: `CREATE OR ALTER VIEW [${defaultSchemaName}].[${nextViewName}] AS\nSELECT 1 AS PlaceholderValue`,
      AIPromptContext: "",
      Fields: [],
      IsActive: false
    };
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.SaveWarehouseView || "SaveWarehouseView",
        item: nextDraft,
        action: "add"
      });
      const saved = getOperationItemsFromResponse(response)[0] || nextDraft;
      const savedId = getWarehouseViewId(saved);
      selectedDraftViewIdRef.current = savedId || "";
      setViews((currentViews) => {
        const replaceKeys = new Set([
          normalizeCompare(savedId),
          normalizeCompare(getString(saved, "QualifiedViewName", "qualifiedViewName"))
        ].filter(Boolean));
        const remainingViews = currentViews.filter((view) => ![
          normalizeCompare(getWarehouseViewId(view)),
          normalizeCompare(getString(view, "QualifiedViewName", "qualifiedViewName"))
        ].some((key) => replaceKeys.has(key)));
        return [...remainingViews, saved];
      });
      applyCleanDraft(saved);
      setSelectedViewId(savedId || "");
    } catch (addError) {
      setScreenError(addError?.message || "Warehouse view could not be added.");
    }
  }, [applyCleanDraft, databaseName, defaultSchemaName, deploymentId, runWarehouseViewOperation, schemaVersionId, views, warehouseId]);

  const discoverViews = useCallback(async () => {
    setDiscovering(true);
    setDiscoverError("");
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.DiscoverWarehouseDatabaseViews || "DiscoverWarehouseDatabaseViews",
        item: null,
        action: "discover"
      });
      setDiscoveredViews(getOperationItemsFromResponse(response).filter((view) => !isManagedWarehouseViewDiscoveryRow(view)));
      setSelectedDiscoveredViewKeys([]);
    } catch (discoverLoadError) {
      setDiscoverError(discoverLoadError?.message || "Existing database views could not be loaded.");
      setDiscoveredViews([]);
      setSelectedDiscoveredViewKeys([]);
    } finally {
      setDiscovering(false);
    }
  }, [runWarehouseViewOperation]);

  const openLoadDialog = useCallback(() => {
    setSelectedDiscoveredViewKeys([]);
    setLoadDialogOpen(true);
    discoverViews();
  }, [discoverViews]);

  const openDeployDialog = useCallback(() => {
    const currentDraftId = getWarehouseViewId(draft);
    const currentDeployable = deployableViews.find((view) => normalizeCompare(view.__deployKey) === normalizeCompare(currentDraftId));
    setDeployError("");
    setSelectedDeployViewKeys(currentDeployable ? [currentDeployable.__deployKey] : []);
    setDeployDialogOpen(true);
  }, [deployableViews, draft]);

  const openDeployDialogGuarded = useCallback(() => {
    requestDiscardChanges(openDeployDialog, "deploy warehouse views");
  }, [openDeployDialog, requestDiscardChanges]);

  const buildDiscoveredViewDraft = useCallback((view, index = 0) => {
    const schemaName = getString(view, "SchemaName", "schemaName") || defaultSchemaName;
    const viewName = getString(view, "ViewName", "viewName") || "WarehouseView";
    return {
      ...view,
      __draftId: `warehouse-view-draft:${view.__discoveryKey || getWarehouseViewDiscoveryKey(view, index, defaultSchemaName)}`,
      WarehouseId: warehouseId,
      WarehouseDeploymentId: deploymentId,
      DatabaseName: databaseName,
      SchemaName: schemaName,
      ViewName: viewName,
      QualifiedViewName: getString(view, "QualifiedViewName", "qualifiedViewName") || `${schemaName}.${viewName}`,
      DisplayName: getWarehouseViewName(view),
      Sequence: getValue(view, "Sequence", "sequence") ?? 999,
      SchemaVersionId: schemaVersionId,
      Fields: getArray(view, "Fields", "fields")
    };
  }, [databaseName, defaultSchemaName, deploymentId, schemaVersionId, warehouseId]);

  const addSelectedDiscoveredViews = useCallback(() => {
    const selectedKeySet = new Set(selectedDiscoveredViewKeys.map((key) => String(key)));
    const selectedRows = discoveredViewRows.filter((view) => selectedKeySet.has(String(view.__discoveryKey)));
    if (!selectedRows.length) return;

    const importSelectedViews = async () => {
      const nextDrafts = selectedRows.map((view, index) => buildDiscoveredViewDraft(view, index));
      try {
        const savedViews = [];
        for (const draftToSave of nextDrafts) {
          const response = await runWarehouseViewOperation({
            operationName: Operations.WarehouseViewItem?.SaveWarehouseView || "SaveWarehouseView",
            item: {
              ...draftToSave,
              WarehouseId: warehouseId,
              WarehouseDeploymentId: deploymentId,
              DatabaseName: databaseName,
              SchemaVersionId: draftToSave.SchemaVersionId || schemaVersionId,
              IsActive: false
            },
            action: "import"
          });
          savedViews.push(getOperationItemsFromResponse(response)[0] || { ...draftToSave, IsActive: false });
        }

        const firstSavedView = savedViews[0];
        const firstSavedViewId = getWarehouseViewId(firstSavedView) || "";
        setViews((currentViews) => {
          const importedKeys = new Set([...nextDrafts, ...savedViews].flatMap((view) => [
            normalizeCompare(getWarehouseViewId(view)),
            normalizeCompare(getString(view, "QualifiedViewName", "qualifiedViewName")),
            normalizeCompare(`${getString(view, "DatabaseName", "databaseName")}.${getString(view, "SchemaName", "schemaName")}.${getString(view, "ViewName", "viewName")}`)
          ]).filter(Boolean));
          const remainingViews = currentViews.filter((view) => {
            const viewKeys = [
              normalizeCompare(getWarehouseViewId(view)),
              normalizeCompare(getString(view, "QualifiedViewName", "qualifiedViewName")),
              normalizeCompare(`${getString(view, "DatabaseName", "databaseName")}.${getString(view, "SchemaName", "schemaName")}.${getString(view, "ViewName", "viewName")}`)
            ];
            return !viewKeys.some((key) => importedKeys.has(key));
          });
          return [...remainingViews, ...savedViews];
        });
        selectedDraftViewIdRef.current = firstSavedViewId;
        applyCleanDraft(firstSavedView);
        setSelectedViewId(firstSavedViewId);
        setSelectedDiscoveredViewKeys([]);
        setLoadDialogOpen(false);
      } catch (importError) {
        setScreenError(importError?.message || "Warehouse views could not be imported.");
      }
    };

    importSelectedViews();
  }, [applyCleanDraft, buildDiscoveredViewDraft, databaseName, deploymentId, discoveredViewRows, runWarehouseViewOperation, schemaVersionId, selectedDiscoveredViewKeys, warehouseId]);

  const createBlankDraftGuarded = useCallback(() => {
    requestDiscardChanges(createBlankDraft, "add a warehouse view");
  }, [createBlankDraft, requestDiscardChanges]);

  const openLoadDialogGuarded = useCallback(() => {
    requestDiscardChanges(openLoadDialog, "import database views");
  }, [openLoadDialog, requestDiscardChanges]);

  const saveDraft = useCallback(async () => {
    const draftToSave = draftRef.current || draft;
    if (!draftToSave) return;
    const currentDraftId = getWarehouseViewId(draftToSave);
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.SaveWarehouseView || "SaveWarehouseView",
        item: {
          ...draftToSave,
          WarehouseId: warehouseId,
          WarehouseDeploymentId: deploymentId,
          DatabaseName: databaseName,
          SchemaVersionId: draftToSave.SchemaVersionId || schemaVersionId
        },
        action: "save"
      });
      const saved = getOperationItemsFromResponse(response)[0] || draftToSave;
      const savedId = getWarehouseViewId(saved);
      selectedDraftViewIdRef.current = savedId || "";
      applyCleanDraft(saved);
      setSelectedViewId(savedId || "");
      setViews((currentViews) => {
        const replaceKeys = new Set([normalizeCompare(currentDraftId), normalizeCompare(savedId)].filter(Boolean));
        const remainingViews = currentViews.filter((view) => !replaceKeys.has(normalizeCompare(getWarehouseViewId(view))));
        return [...remainingViews, saved];
      });
    } catch (saveError) {
      setScreenError(saveError?.message || "Warehouse view could not be saved.");
    }
  }, [applyCleanDraft, databaseName, deploymentId, draft, runWarehouseViewOperation, schemaVersionId, warehouseId]);

  const setDraftActive = useCallback(async (isActive) => {
    const draftToSave = draftRef.current || draft;
    if (!draftToSave) return;
    const currentDraftId = getWarehouseViewId(draftToSave);
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.SaveWarehouseView || "SaveWarehouseView",
        item: {
          ...draftToSave,
          WarehouseId: warehouseId,
          WarehouseDeploymentId: deploymentId,
          DatabaseName: databaseName,
          SchemaVersionId: draftToSave.SchemaVersionId || schemaVersionId,
          IsActive: isActive
        },
        action: isActive ? "enable" : "disable"
      });
      const saved = getOperationItemsFromResponse(response)[0] || { ...draftToSave, IsActive: isActive };
      const savedId = getWarehouseViewId(saved);
      selectedDraftViewIdRef.current = savedId || currentDraftId || "";
      applyCleanDraft(saved);
      setSelectedViewId(savedId || currentDraftId || "");
      setViews((currentViews) => {
        const replaceKeys = new Set([normalizeCompare(currentDraftId), normalizeCompare(savedId)].filter(Boolean));
        const remainingViews = currentViews.filter((view) => !replaceKeys.has(normalizeCompare(getWarehouseViewId(view))));
        return [...remainingViews, saved];
      });
    } catch (activeError) {
      setScreenError(activeError?.message || `Warehouse view could not be ${isActive ? "enabled" : "disabled"}.`);
    }
  }, [applyCleanDraft, databaseName, deploymentId, draft, runWarehouseViewOperation, schemaVersionId, warehouseId]);

  const deleteWarehouseView = useCallback(async () => {
    if (!deleteViewRequest) return;
    const deleteViewId = getWarehouseViewId(deleteViewRequest);
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.SaveWarehouseView || "SaveWarehouseView",
        item: {
          ...deleteViewRequest,
          WarehouseId: warehouseId,
          WarehouseDeploymentId: deploymentId,
          DatabaseName: databaseName,
          SchemaVersionId: deleteViewRequest.SchemaVersionId || schemaVersionId,
          IsDeleted: true
        },
        action: "delete"
      });
      const deleted = getOperationItemsFromResponse(response)[0] || deleteViewRequest;
      const deletedId = getWarehouseViewId(deleted) || deleteViewId;
      const remainingViews = views.filter((view) =>
        normalizeCompare(getWarehouseViewId(view)) !== normalizeCompare(deletedId));
      setViews(remainingViews);
      if (normalizeCompare(selectedViewId) === normalizeCompare(deletedId)) {
        const nextView = remainingViews[0] || null;
        selectedDraftViewIdRef.current = getWarehouseViewId(nextView) || "";
        applyCleanDraft(nextView);
        setSelectedViewId(getWarehouseViewId(nextView) || "");
      }
      setDeleteViewRequest(null);
    } catch (deleteError) {
      setScreenError(deleteError?.message || "Warehouse view could not be deleted.");
    }
  }, [applyCleanDraft, databaseName, deleteViewRequest, deploymentId, runWarehouseViewOperation, schemaVersionId, selectedViewId, views, warehouseId]);

  const deploySelectedViews = useCallback(async () => {
    if (!selectedDeployViewKeys.length) {
      setDeployError("Select at least one warehouse view to deploy.");
      return;
    }

    setDeployError("");
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.DeployWarehouseViews || "DeployWarehouseViews",
        item: null,
        params: {
          warehouseViewIds: selectedDeployViewKeys.join(","),
          requireSelection: "true"
        },
        action: "deploy",
        allowFailure: true
      });
      const operationMessage = getOperationMessageFromResponse(response);
      const operationDetails = getOperationMessageDetailsFromResponse(response);
      const deployedViews = getOperationItemsFromResponse(response);
      if (deployedViews.length) {
        const deployedKeySet = new Set(deployedViews.map((view) => normalizeCompare(getWarehouseViewId(view))).filter(Boolean));
        setViews((currentViews) => {
          const remainingViews = currentViews.filter((view) => !deployedKeySet.has(normalizeCompare(getWarehouseViewId(view))));
          return [...remainingViews, ...deployedViews];
        });
        const firstDeployedView = deployedViews[0];
        const firstDeployedViewId = getWarehouseViewId(firstDeployedView) || selectedDeployViewKeys[0] || "";
        selectedDraftViewIdRef.current = firstDeployedViewId;
        setSelectedViewId(firstDeployedViewId);
        applyCleanDraft(firstDeployedView);
      }

      if (!isOperationSuccessful(response)) {
        setDeployDialogOpen(false);
        setDeployResultDialog({
          tone: "error",
          message: operationMessage || "Warehouse view deployment failed.",
          details: operationDetails
        });
        return;
      }

      setDeployDialogOpen(false);
      setSelectedDeployViewKeys([]);
      setDeployResultDialog({
        tone: "success",
        message: operationMessage || "Warehouse views deployed.",
        details: operationDetails
      });
    } catch (deployFailure) {
      setDeployDialogOpen(false);
      setDeployResultDialog({
        tone: "error",
        message: deployFailure?.message || "Warehouse view deployment failed.",
        details: []
      });
    }
  }, [applyCleanDraft, runWarehouseViewOperation, selectedDeployViewKeys]);

  const refreshDraftFields = useCallback(async () => {
    const draftToRefresh = draftRef.current || draft;
    if (!draftToRefresh) return;
    const currentDraftId = getWarehouseViewId(draftToRefresh);
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.RefreshWarehouseViewFields || "RefreshWarehouseViewFields",
        item: {
          ...draftToRefresh,
          WarehouseId: warehouseId,
          WarehouseDeploymentId: deploymentId,
          DatabaseName: databaseName,
          SchemaVersionId: draftToRefresh.SchemaVersionId || schemaVersionId
        },
        action: "refresh-fields",
        allowFailure: true
      });
      if (!isOperationSuccessful(response)) {
        const message = getOperationMessageFromResponse(response) || "Warehouse view fields could not be refreshed.";
        throw new Error(message);
      }
      const refreshed = getOperationItemsFromResponse(response)[0] || draftToRefresh;
      const refreshedId = getWarehouseViewId(refreshed);
      selectedDraftViewIdRef.current = refreshedId || currentDraftId || "";
      applyWorkingDraft(refreshed);
      setSelectedViewId(refreshedId || currentDraftId || "");
      setViews((currentViews) => {
        const replaceKeys = new Set([normalizeCompare(currentDraftId), normalizeCompare(refreshedId)].filter(Boolean));
        const remainingViews = currentViews.filter((view) => !replaceKeys.has(normalizeCompare(getWarehouseViewId(view))));
        return [...remainingViews, refreshed];
      });
    } catch (refreshError) {
      setScreenError(refreshError?.message || "Warehouse view fields could not be refreshed.");
    }
  }, [applyWorkingDraft, databaseName, deploymentId, draft, runWarehouseViewOperation, schemaVersionId, warehouseId]);

  const validateDraft = useCallback(async () => {
    const draftToValidate = draftRef.current || draft;
    if (!draftToValidate) return;
    const currentDraftId = getWarehouseViewId(draftToValidate);
    try {
      const response = await runWarehouseViewOperation({
        operationName: Operations.WarehouseViewItem?.ValidateWarehouseView || "ValidateWarehouseView",
        item: {
          ...draftToValidate,
          DatabaseName: databaseName
        },
        action: "validate",
        allowFailure: true
      });
      const validated = getOperationItemsFromResponse(response)[0] || draftToValidate;
      const validatedId = getWarehouseViewId(validated);
      const operationMessage = getOperationMessageFromResponse(response);
      const isValidationError = !isOperationSuccessful(response)
        || normalizeCompare(getString(validated, "ValidationState", "validationState")) === "error";
      const isValidationWarning = normalizeCompare(getString(validated, "ValidationState", "validationState")) === "warning";
      const validationState = getString(validated, "ValidationState", "validationState") || (isValidationError ? "Error" : "Valid");
      const validationMessage = getString(validated, "LastValidationMessage", "lastValidationMessage") || operationMessage;
      selectedDraftViewIdRef.current = validatedId || currentDraftId || "";
      applyDraftPreservingDirtyState(validated);
      setSelectedViewId(validatedId || currentDraftId || "");
      setViews((currentViews) => {
        const replaceKeys = new Set([normalizeCompare(currentDraftId), normalizeCompare(validatedId)].filter(Boolean));
        const remainingViews = currentViews.filter((view) => !replaceKeys.has(normalizeCompare(getWarehouseViewId(view))));
        return [...remainingViews, validated];
      });
      setValidationResultDialog({
        view: validated,
        tone: isValidationError ? "error" : isValidationWarning ? "warning" : "success",
        state: validationState,
        message: validationMessage,
        checkedAt: new Date().toLocaleString()
      });
    } catch (validateError) {
      setValidationResultDialog({
        view: draftToValidate,
        tone: "error",
        state: "Error",
        message: validateError?.message || "Warehouse view validation failed.",
        checkedAt: new Date().toLocaleString()
      });
    }
  }, [applyDraftPreservingDirtyState, databaseName, draft, runWarehouseViewOperation]);

  const searchExportDraft = useCallback(async () => {
    const draftToSearch = draftRef.current || draft;
    if (!draftToSearch) return;

    if (draftDirtyRef.current) {
      setScreenError("Save the warehouse view before using Search/Export.");
      return;
    }

    const searchableFields = getArray(draftToSearch, "Fields", "fields")
      .filter((field) => getValue(field, "IsActive", "isActive") !== false
        && getValue(field, "IsDeleted", "isDeleted") !== true
        && getString(field, "FieldName", "fieldName"));
    if (isRecordActive(draftToSearch) === false) {
      setScreenError("Enable the warehouse view before using Search/Export.");
      return;
    }
    if (normalizeCompare(getString(draftToSearch, "DeployState", "deployState")) !== "deployed") {
      setScreenError("Deploy the warehouse view before using Search/Export.");
      return;
    }
    if (!searchableFields.length) {
      setScreenError("Refresh Fields and save the warehouse view before using Search/Export.");
      return;
    }

    const operationName = Operations.WarehouseViewItem?.SearchWarehouseView || "SearchWarehouseView";
    const operation = getWarehouseViewOperationDefinition(operationName);
    if (!operation) {
      setScreenError(`Warehouse View operation metadata '${operationName}' was not found.`);
      return;
    }
    if (!getOperationIdentity(operation)) {
      setScreenError(`Warehouse View operation metadata '${operationName}' is missing its operation Name.`);
      return;
    }
    if (!warehouseViewOperationDialog?.handleOperationDialog) {
      setScreenError("Warehouse View operation dialog service is not available.");
      return;
    }

    try {
      const warehouseViewId = getWarehouseViewId(draftToSearch);
      setBusyAction("search-export");
      setScreenError("");
      await warehouseViewOperationDialog.handleOperationDialog(
        {
          ...operation,
          itemData: draftToSearch,
          queryParams: {
            ...queryParams,
            warehouseViewId,
            id: warehouseViewId,
            wId: warehouseId,
            wdId: deploymentId
          }
        },
        {
          closeOnComplete: false,
          resultsCallback: async (response) => {
            if (!isOperationSuccessful(response)) {
              const message = getOperationMessageFromResponse(response) || "Warehouse view Search/Export failed.";
              const details = getOperationMessageDetailsFromResponse(response);
              throw new Error([message, ...details].filter(Boolean).join(" "));
            }
            dispatchAiPanelCommandFromWorkspaceOperationResponse(response);
          }
        }
      );
    } catch (searchError) {
      setScreenError(searchError?.message || "Warehouse view Search/Export could not be opened.");
    } finally {
      setBusyAction("");
    }
  }, [coreContext?.Operations, deploymentId, draft, getWarehouseViewOperationDefinition, queryParams, warehouseId, warehouseViewOperationDialog]);

  const selectManagedWarehouseView = useCallback((view) => {
    const nextViewId = getWarehouseViewId(view);
    if (!nextViewId || normalizeCompare(nextViewId) === normalizeCompare(selectedViewId)) return;
    if (draftDirtyRef.current) {
      setPendingViewSelection(view);
      return;
    }
    selectedDraftViewIdRef.current = nextViewId;
    applyCleanDraft(view);
    setSelectedViewId(nextViewId);
  }, [applyCleanDraft, selectedViewId]);

  const discardChangesAndSelectPendingView = useCallback(() => {
    const nextView = pendingViewSelection;
    const nextViewId = getWarehouseViewId(nextView);
    setPendingViewSelection(null);
    if (!nextView || !nextViewId) return;
    selectedDraftViewIdRef.current = nextViewId;
    applyCleanDraft(nextView);
    setSelectedViewId(nextViewId);
  }, [applyCleanDraft, pendingViewSelection]);

  const renderWarehouseViewFieldsTab = useCallback(({ value, onChange, disabled }) => (
    <WarehouseViewFieldsTab
      fields={value}
      disabled={disabled}
      busy={busyAction === "refresh-fields"}
      onRefreshFields={refreshDraftFields}
      onFieldsChange={onChange}
    />
  ), [busyAction, refreshDraftFields]);
  const warehouseViewSubGridRenderers = useMemo(() => ({
    Fields: renderWarehouseViewFieldsTab
  }), [renderWarehouseViewFieldsTab]);

  useEffect(() => {
    if (!onWarehouseViewActionsChange) return undefined;
    if (!canManageWarehouseViews) {
      onWarehouseViewActionsChange(null);
      return () => onWarehouseViewActionsChange(null);
    }

    onWarehouseViewActionsChange({
      busyAction,
      confirmDiscardChanges: requestDiscardChanges,
      actions: [
        {
          key: "warehouse-view-deploy",
          label: "Deploy",
          iconKey: "publish",
          loading: busyAction === "deploy",
          separatorAfter: true,
          disabled: Boolean(busyAction) || deployableViews.length === 0,
          title: deployableViews.length > 0
            ? "Deploy selected managed warehouse views."
            : "No active warehouse views are available to deploy.",
          onClick: openDeployDialogGuarded
        },
        {
          key: "warehouse-view-save",
          label: "Save",
          iconKey: "save",
          disabled: Boolean(busyAction) || !draft,
          title: draft ? "Save the selected warehouse view." : "Select or add a warehouse view to save.",
          onClick: saveDraft
        },
        {
          key: isRecordActive(draft) ? "warehouse-view-disable" : "warehouse-view-enable",
          label: isRecordActive(draft) ? "Disable" : "Enable",
          iconKey: isRecordActive(draft) ? "undo" : "success",
          disabled: Boolean(busyAction) || !draft,
          title: draft
            ? isRecordActive(draft)
              ? "Disable this warehouse view for runtime use."
              : "Enable this warehouse view for runtime use."
            : "Select or add a warehouse view to enable or disable.",
          onClick: () => setDraftActive(!isRecordActive(draft))
        },
        {
          key: "warehouse-view-validate",
          label: "Validate",
          iconKey: "validate",
          loading: busyAction === "validate",
          disabled: Boolean(busyAction) || !draft,
          title: draft ? "Validate the selected warehouse view SQL." : "Select or add a warehouse view to validate.",
          onClick: validateDraft
        },
        {
          key: "warehouse-view-search-export",
          label: "Search/Export",
          iconKey: "search",
          loading: busyAction === "search-export",
          disabled: Boolean(busyAction) || Boolean(draftSearchDisabledReason),
          title: draftSearchDisabledReason || "Search or export the selected warehouse view.",
          onClick: searchExportDraft
        }
      ]
    });

    return () => onWarehouseViewActionsChange(null);
  }, [busyAction, canManageWarehouseViews, deployableViews.length, draft, draftSearchDisabledReason, onWarehouseViewActionsChange, openDeployDialogGuarded, requestDiscardChanges, saveDraft, searchExportDraft, setDraftActive, validateDraft]);

  if (loading) return <ResourceStateControl loading loadingText="Loading Warehouse Views..." />;
  if (error && !destination) return <ResourceStateControl tone="warning" title="Warehouse views unavailable" message={error} />;
  if (!canManageWarehouseViews) {
    return <ResourceStateControl tone="warning" title="Warehouse deployment required" message="Warehouse views require a selected warehouse deployment." />;
  }

  const isViewMutationBusy = ["add", "save", "validate", "import", "deploy", "enable", "disable", "delete", "refresh-fields", "search-export"].includes(busyAction);
  const hasDefaultNewWarehouseView = views.some(isDefaultNewWarehouseView);
  const addWarehouseViewTitle = isViewMutationBusy
    ? "Finish the current warehouse view action before adding another view."
    : hasDefaultNewWarehouseView
      ? "Rename or delete the existing New Warehouse View before adding another."
      : "Add warehouse view";
  const validationDialogView = validationResultDialog?.view || draft;
  const validationDialogSchema = getString(validationDialogView, "SchemaName", "schemaName") || defaultSchemaName;
  const validationDialogViewName = getString(validationDialogView, "ViewName", "viewName") || getWarehouseViewName(validationDialogView);
  const validationDialogDatabase = getString(validationDialogView, "DatabaseName", "databaseName") || databaseName;
  const validationDialogQualifiedName = getString(validationDialogView, "QualifiedViewName", "qualifiedViewName")
    || `${validationDialogSchema}.${validationDialogViewName}`;
  const validationDialogMessage = validationResultDialog?.message
    && normalizeCompare(validationResultDialog.message) !== "success"
    ? validationResultDialog.message
    : "The warehouse provider accepted the SQL definition for this managed view.";
  const validationDialogTone = validationResultDialog?.tone === "error" ? "error" : validationResultDialog?.tone === "warning" ? "warning" : "success";

  const leftPane = (
    <ResourceCatalogListControl
      className="resource-workspace-warehouse-view-list"
      summary={`${views.length.toLocaleString()} managed`}
      items={views}
      getItemKey={(view, index) => getWarehouseViewId(view) || index}
      ariaLabel="Managed warehouse views"
      emptyMessage="No warehouse views have been managed yet."
      emptyFilteredMessage="No warehouse views match the current search."
      search={{
        fieldMetadata: buildFilterTextBoxField("WarehouseViewSearch", "Search warehouse views...", "Search warehouse views"),
        value: viewSearchTerm,
        onChange: (nextValue) => setViewSearchTerm(nextValue || ""),
        matchesItem: (view, value) => !normalizeCompare(value)
          || normalizeCompare(getWarehouseViewName(view)).includes(normalizeCompare(value))
          || normalizeCompare(getString(view, "ViewName", "viewName")).includes(normalizeCompare(value))
          || normalizeCompare(getString(view, "QualifiedViewName", "qualifiedViewName")).includes(normalizeCompare(value))
          || normalizeCompare(getString(view, "DatabaseName", "databaseName")).includes(normalizeCompare(value))
          || normalizeCompare(getString(view, "SchemaName", "schemaName")).includes(normalizeCompare(value))
          || normalizeCompare(getString(view, "DeployState", "deployState")).includes(normalizeCompare(value))
      }}
      actions={(
        <>
          <span className="resource-workspace-warehouse-view-list__toolbar-action" title={addWarehouseViewTitle}>
            <ButtonControl
              type="standard-small"
              title={addWarehouseViewTitle}
              aria-label="Add warehouse view"
              onClick={createBlankDraftGuarded}
              disabled={isViewMutationBusy || hasDefaultNewWarehouseView}
            >
              <IconControl iconKey="add" title={addWarehouseViewTitle} width="1rem" height="1rem" />
            </ButtonControl>
          </span>
          <ButtonControl type="standard-small" title="Load existing database views" aria-label="Load existing database views" onClick={openLoadDialogGuarded} disabled={isViewMutationBusy || discovering}>
            <IconControl iconKey="database" title="Load existing database views" width="1rem" height="1rem" />
          </ButtonControl>
        </>
      )}
      renderItem={(view) => {
        const viewId = getWarehouseViewId(view);
        const selected = normalizeCompare(viewId) === normalizeCompare(selectedViewId);
        return (
          <ResourceCatalogRow
            title={getWarehouseViewName(view)}
            subtitle={`${getString(view, "DatabaseName", "databaseName") || databaseName || "Database"} / ${getString(view, "SchemaName", "schemaName") || defaultSchemaName}`}
            titleAttribute={getWarehouseViewName(view)}
            className={`resource-workspace-warehouse-view-row${selected ? " resource-catalog-row--selected" : ""}`}
            onClick={() => selectManagedWarehouseView(view)}
            actions={(
              <span
                className="resource-workspace-warehouse-view-row__delete"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <ButtonControl
                  type="standard-small"
                  title={`Delete ${getWarehouseViewName(view)}`}
                  aria-label={`Delete ${getWarehouseViewName(view)}`}
                  disabled={isViewMutationBusy}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    setDeleteViewRequest(view);
                  }}
                >
                  <IconControl iconKey="delete" title="Delete warehouse view" width="1rem" height="1rem" />
                </ButtonControl>
              </span>
            )}
            metaItems={[
              { key: "name", content: getString(view, "ViewName", "viewName") || "View" },
              { key: "state", className: "resource-catalog-row__meta-item--status", content: getString(view, "DeployState", "deployState") || "Not Deployed" },
              { key: "fields", content: `${getArray(view, "Fields", "fields").length.toLocaleString()} fields` }
            ]}
          />
        );
      }}
    />
  );

  const middlePane = draft ? (
    <div className="resource-workspace-warehouse-view-editor">
      <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
        <h3>{getWarehouseViewName(draft)}</h3>
        <p>{deploymentName}</p>
      </div>
      {screenError ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{screenError}</div> : null}
      <FormControl
        className="resource-workspace-warehouse-view-editor__form"
        metadata={warehouseViewFormMetadata}
        formFields={warehouseViewFormFields}
        itemData={draftFormItem}
        disableFooter={true}
        disableMinWidth={true}
        tabLayout="top"
        subGridRenderers={warehouseViewSubGridRenderers}
        onChange={handleWarehouseViewFormChange}
        onSave={() => {}}
      />
    </div>
  ) : (
    <ResourceStateControl message="Select or add a warehouse view to edit SQL." />
  );

  const aiParams = draft ? {
    warehouseViewId: aiReferenceId,
    viewId: aiReferenceId,
    warehouseId,
    wid: warehouseId,
    warehouseDeploymentId: deploymentId,
    wdid: deploymentId,
    referenceid: aiReferenceId
  } : {};

  return (
    <>
      <React.Suspense fallback={<ResourceStateControl loading loadingText="Loading AI panel..." />}>
        <WarehouseViewAIPanel
          key={`warehouse-view-ai:${aiReferenceId || "none"}`}
          open
          presentation="inline"
          title="Warehouse View Designer"
          className="resource-workspace-warehouse-view-designer"
          chatPane="right"
          splitPaneStorageKey={`warehouse-view-designer:no-resize-overlay:v3:${deploymentId || warehouseId || "default"}`}
          initialLeftPct={0.33}
          initialRightPct={0.28}
          enableContentDragHiding={false}
          minLeft={280}
          minMiddle={460}
          minRight={360}
          referenceId={aiReferenceId}
          referenceName={WAREHOUSE_VIEW_AI_REFERENCE_ITEM_NAME}
          operationName={aiOperationName}
          singleSessionMode={aiOperationReady}
          session={aiSession}
          params={aiParams}
          payload={{ selectedView: draft, deployment, destination }}
          left={leftPane}
          middle={middlePane}
          introMessage={draft
            ? aiReferenceId
              ? `Build or revise ${getWarehouseViewName(draft)} using the warehouse schema metadata.`
              : "Save this warehouse view before starting AI assistance."
            : "Select a warehouse view to start AI assistance."}
        />
      </React.Suspense>
      <DialogControl
        controlOrigin="warehouse-view-execute-operation"
        openDialog={warehouseViewOperationDialog?.openDialog}
        title={warehouseViewOperationDialog?.activeOperation?.DisplayName ?? warehouseViewOperationDialog?.activeOperation?.Name ?? "Operation Completed"}
        subTitle={warehouseViewOperationDialog?.activeOperation?.Details}
        onCancel={warehouseViewOperationDialog?.cancelOperation}
        onOk={async () => warehouseViewOperationDialog?.executeOperation?.()}
        excludeCancel={!warehouseViewOperationDialog?.activeOperation}
        okText={(warehouseViewOperationDialog?.activeOperation && warehouseViewOperationDialog?.operationErrors) ? "Retry" : "OK"}
        disableCancel={warehouseViewOperationDialog?.operationSuccessMessage}
      >
        {warehouseViewOperationDialog?.renderOperationContent?.()}
      </DialogControl>
      <DialogControl
        controlOrigin="warehouse-view-deploy"
        title="Deploy Warehouse Views"
        openDialog={deployDialogOpen}
        okText={busyAction === "deploy" ? "Deploying..." : "Deploy"}
        cancelText="Close"
        onOk={deploySelectedViews}
        onCancel={() => setDeployDialogOpen(false)}
        onX={() => setDeployDialogOpen(false)}
        disableOk={busyAction === "deploy" || !selectedDeployViewKeys.length}
        disableCancel={busyAction === "deploy"}
      >
        <div className="resource-workspace-linked-record-add-dialog resource-workspace-warehouse-view-deploy-dialog">
          <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
            <div>
              <dt>Active Views</dt>
              <dd>{deployableViews.length.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Database</dt>
              <dd>{databaseName || "Current database"}</dd>
            </div>
          </dl>
          {deployError ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{deployError}</div> : null}
          <CardGridControl
            className="resource-workspace-warehouse-view-card-grid"
            rows={deployableViews}
            cardFields={WAREHOUSE_VIEW_DEPLOY_CARD_FIELDS}
            rowKey={(view) => view.__deployKey}
            selectable
            multiSelect
            selectedRowKeys={selectedDeployViewKeys}
            onSelectedRowKeysChange={(keys) => {
              setSelectedDeployViewKeys(keys);
              setDeployError("");
            }}
            toolbarLeadingContent={deployViewToolbarLeadingContent}
            valueDisplayResolvers={{
              Fields: (value, row) => `${getArray(row, "Fields", "fields").length.toLocaleString()} fields`
            }}
            searchable
            activeFilterEnabled={false}
            emptyContent="No active warehouse views are available to deploy."
            minCardWidth="11rem"
            maxColumns={2}
          />
        </div>
      </DialogControl>
      <DialogControl
        controlOrigin="warehouse-view-deploy-result"
        title="Warehouse View Deployment"
        openDialog={Boolean(deployResultDialog)}
        okText="Close"
        hideCancel
        onOk={() => setDeployResultDialog(null)}
        onX={() => setDeployResultDialog(null)}
      >
        <div className="resource-workspace-warehouse-view-validation-dialog">
          <div className={`resource-workspace-access-note resource-workspace-access-note--${deployResultDialog?.tone === "error" ? "error" : "success"}`} role={deployResultDialog?.tone === "error" ? "alert" : "status"}>
            {deployResultDialog?.message || "Warehouse view deployment completed."}
          </div>
          {getArray(deployResultDialog, "details").length ? (
            <ul className="resource-workspace-warehouse-view-deploy-details">
              {getArray(deployResultDialog, "details").map((detail, index) => (
                <li key={`${index}-${detail}`}>{detail}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </DialogControl>
      <DialogControl
        controlOrigin="warehouse-view-validation-result"
        title="Warehouse View Validation"
        openDialog={Boolean(validationResultDialog)}
        okText="Close"
        hideCancel
        onOk={() => setValidationResultDialog(null)}
        onX={() => setValidationResultDialog(null)}
      >
        <div className="resource-workspace-warehouse-view-validation-dialog">
          <div className={`resource-workspace-access-note resource-workspace-access-note--${validationDialogTone}`} role={validationDialogTone === "error" ? "alert" : "status"}>
            {validationDialogMessage}
          </div>
          <p>
            Validated the SQL definition for <strong>{validationDialogQualifiedName}</strong> against the selected warehouse deployment.
            This check does not deploy or replace the database view.
          </p>
          <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
            <div>
              <dt>State</dt>
              <dd>{validationResultDialog?.state || "Unknown"}</dd>
            </div>
            <div>
              <dt>View</dt>
              <dd>{validationDialogQualifiedName}</dd>
            </div>
            <div>
              <dt>Database</dt>
              <dd>{validationDialogDatabase || "Current database"}</dd>
            </div>
            <div>
              <dt>Schema</dt>
              <dd>{validationDialogSchema || "Deployment default"}</dd>
            </div>
            <div>
              <dt>Checked</dt>
              <dd>{validationResultDialog?.checkedAt || "Just now"}</dd>
            </div>
          </dl>
        </div>
      </DialogControl>
      <DialogControl
        controlOrigin="warehouse-view-load"
        title="Import View"
        openDialog={loadDialogOpen}
        okText="Add"
        cancelText="Close"
        onOk={addSelectedDiscoveredViews}
        onCancel={() => setLoadDialogOpen(false)}
        onX={() => setLoadDialogOpen(false)}
        disableOk={discovering || busyAction === "import" || !selectedDiscoveredViewKeys.length}
        disableCancel={discovering || busyAction === "import"}
      >
        <div className="resource-workspace-linked-record-add-dialog resource-workspace-warehouse-view-load-dialog">
          <dl className="resource-workspace-facts resource-workspace-linked-item-metrics">
            <div>
              <dt>Database Views</dt>
              <dd>{discovering ? "Loading" : discoveredViews.length.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Database</dt>
              <dd>{databaseName || "Current database"}</dd>
            </div>
          </dl>
          {discoverError ? (
            <ResourceStateControl tone="warning" title="Existing views unavailable" message={discoverError} />
          ) : (
            <CardGridControl
              className="resource-workspace-warehouse-view-card-grid"
              rows={discoveredViewRows}
              cardFields={WAREHOUSE_VIEW_DISCOVERY_CARD_FIELDS}
              rowKey={(view) => view.__discoveryKey}
              selectable
              multiSelect
              selectedRowKeys={selectedDiscoveredViewKeys}
              onSelectedRowKeysChange={setSelectedDiscoveredViewKeys}
              toolbarLeadingContent={discoveredViewToolbarLeadingContent}
              valueDisplayResolvers={{
                Fields: (value, row) => `${getArray(row, "Fields", "fields").length.toLocaleString()} columns`
              }}
              searchable
              activeFilterEnabled={false}
              emptyContent={discovering ? "Loading existing database views..." : "No database views were found."}
              minCardWidth="11rem"
              maxColumns={2}
            />
          )}
        </div>
      </DialogControl>
      <DialogControl
        controlOrigin="warehouse-view-delete"
        title="Delete Warehouse View"
        openDialog={Boolean(deleteViewRequest)}
        okText={busyAction === "delete" ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onOk={deleteWarehouseView}
        onCancel={() => setDeleteViewRequest(null)}
        onX={() => setDeleteViewRequest(null)}
        disableOk={busyAction === "delete"}
        disableCancel={busyAction === "delete"}
      >
        <div className="resource-workspace-delete-dialog">
          <p>
            Delete <strong>{getWarehouseViewName(deleteViewRequest)}</strong> from managed warehouse views?
          </p>
          <p>This removes the view from Convoke management. It does not drop the physical database view.</p>
        </div>
      </DialogControl>
      <DialogControl
        controlOrigin="warehouse-view-discard-unsaved"
        title="Discard Unsaved Changes"
        openDialog={Boolean(pendingViewSelection || pendingDiscardAction)}
        okText="Discard Changes"
        cancelText="Stay"
        onOk={pendingViewSelection ? discardChangesAndSelectPendingView : runPendingDiscardAction}
        onCancel={() => {
          setPendingViewSelection(null);
          cancelPendingDiscardAction();
        }}
        onX={() => {
          setPendingViewSelection(null);
          cancelPendingDiscardAction();
        }}
      >
        <div className="resource-workspace-delete-dialog">
          <p>
            You have unsaved changes to <strong>{getWarehouseViewName(draftRef.current || draft)}</strong>.
          </p>
          <p>
            {pendingViewSelection
              ? "Changing warehouse views will discard those changes."
              : `${pendingDiscardAction?.label || "Continuing"} will discard those changes.`}
          </p>
        </div>
      </DialogControl>
    </>
  );
};

export const ResourceConfigurationDestinationEntityDeploymentsScreen = ({
  destinationState,
  initialDeploymentFilter = "all",
  onSaveEntityDeployment,
  onDeleteEntityDeployment,
  onOpenEntityDeploymentSchedule,
  onManageEntityDeploymentEntity,
  onMapEntityDeploymentEntity,
  onSearchEntityDeploymentEntity,
  onOpenEntityDeploymentPicker,
  onEntityScopeActionsChange
}) => {
  const { destination, defaults, entityObjects = [], loading = false, error = "" } = destinationState || {};
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntityDeploymentKey, setSelectedEntityDeploymentKey] = useState("");
  const [draftEntityDeployment, setDraftEntityDeployment] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingEntityDeploymentSelection, setPendingEntityDeploymentSelection] = useState(null);
  const [pendingDiscardAction, setPendingDiscardAction] = useState(null);
  const draftEntityDeploymentDirtyRef = useRef(false);
  const draftEntityDeploymentRef = useRef(null);
  const pendingDiscardActionRef = useRef(null);
  const normalizedInitialDeploymentFilter = initialDeploymentFilter || "all";
  const isWarehouse = normalizeCompare(defaults?.managerKey) === "warehouse";
  const rows = useMemo(() => getDestinationEntityDeployments(destination)
    .filter((row) => {
      if (!normalizedInitialDeploymentFilter || normalizedInitialDeploymentFilter === "all") return true;
      const rowDeploymentId = getString(row, "DeploymentId", "deploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId");
      return normalizeCompare(rowDeploymentId) === normalizeCompare(normalizedInitialDeploymentFilter);
    }), [destination, normalizedInitialDeploymentFilter]);
  const deployments = useMemo(() => getDestinationDeployments(destination, defaults), [defaults, destination]);
  const deploymentById = useMemo(
    () => new Map(deployments.map((deployment) => [normalizeCompare(getDeploymentId(deployment)), deployment])),
    [deployments]
  );
  const entityObjectById = useMemo(() => {
    const nextMap = new Map();
    (Array.isArray(entityObjects) ? entityObjects : []).forEach((entityObject) => {
      [
        getId(entityObject),
        getString(entityObject, "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId")
      ].filter(Boolean).forEach((id) => {
        nextMap.set(normalizeCompare(id), entityObject);
      });
    });
    return nextMap;
  }, [entityObjects]);
  const getEntityDeploymentDisplayName = useCallback((row) => {
    const entityObjectId = getString(row, "EntityObjectId", "entityObjectId");
    const linkedEntityObject = entityObjectById.get(normalizeCompare(entityObjectId));
    return getDisplayName(
      linkedEntityObject,
      getString(linkedEntityObject, "ObjectName", "objectName")
    )
      || getString(row, "EntityObjectDisplayName", "entityObjectDisplayName", "EntityDisplayName", "entityDisplayName")
      || getString(row, "EntityObjectName", "entityObjectName", "ObjectName", "objectName")
      || "Entity Object";
  }, [entityObjectById]);
  const getEntityDeploymentStatusLabel = useCallback((row) => {
    const destinationStateLabel = getDestinationStateLabel(row);
    const activeStateLabel = isRecordActive(row) ? "Active" : "Disabled";
    return `${destinationStateLabel} / ${activeStateLabel}`;
  }, []);
  const getEntityDeploymentSelectionKey = useCallback((row, index = 0) =>
    getEntityDeploymentMatchKey(row) || getEntityDeploymentRenderKey(row, index), []);
  const activeRows = rows.filter(isRecordActive).length;
  const selectedEntityDeployment = rows.find((row, index) =>
    getEntityDeploymentSelectionKey(row, index) === selectedEntityDeploymentKey
  ) || rows[0] || null;
  const selectedDeployment = selectedEntityDeployment
    ? deploymentById.get(normalizeCompare(getString(selectedEntityDeployment, "DeploymentId", "deploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId")))
    : null;
  const selectedEntityName = selectedEntityDeployment ? getEntityDeploymentDisplayName(selectedEntityDeployment) : "";
  const entityDeploymentMetadata = destinationState?.entityDeploymentMetadata || {};
  const entityDeploymentFormFields = useMemo(
    () => getArray(entityDeploymentMetadata, "Fields", "fields")
      .filter((field) => !isWarehouse || normalizeCompare(field?.FieldName) !== "filepackagingmode"),
    [entityDeploymentMetadata, isWarehouse]
  );
  const entityDeploymentEditorFields = useMemo(
    () => entityDeploymentFormFields.map((field) => {
      const fieldName = normalizeCompare(field?.FieldName);
      if (fieldName === "entityobjectname") return { ...field, IsReadOnly: true, disabled: true };
      if (
        fieldName === "destinationstate"
        || fieldName === "isactive"
        || fieldName === "customfields"
        || fieldName === "securecustomfields"
      ) return { ...field, IsHidden: true };
      return field;
    }),
    [entityDeploymentFormFields]
  );
  const entityDeploymentVisibleEditorFields = useMemo(
    () => entityDeploymentEditorFields.filter((field) => !getBoolean(field, "IsHidden", "isHidden", "Hidden", "hidden")),
    [entityDeploymentEditorFields]
  );
  const entityDeploymentFormMetadata = useMemo(() => ({
    ...entityDeploymentMetadata,
    Name: getString(entityDeploymentMetadata, "Name", "name") || "EntityObjectDeployment",
    DisplayName: "Entity Scope",
    Fields: entityDeploymentEditorFields
  }), [entityDeploymentEditorFields, entityDeploymentMetadata]);
  const applyCleanEntityDeploymentDraft = useCallback((row) => {
    const nextDraft = row ? { ...row, IsNew: false } : null;
    draftEntityDeploymentDirtyRef.current = false;
    draftEntityDeploymentRef.current = nextDraft;
    setDraftEntityDeployment(nextDraft);
  }, []);
  const restoreSelectedEntityDeploymentDraft = useCallback(() => {
    const currentRow = rows.find((row, index) =>
      getEntityDeploymentSelectionKey(row, index) === selectedEntityDeploymentKey
    ) || selectedEntityDeployment || null;
    applyCleanEntityDeploymentDraft(currentRow);
  }, [applyCleanEntityDeploymentDraft, getEntityDeploymentSelectionKey, rows, selectedEntityDeployment, selectedEntityDeploymentKey]);
  const requestDiscardEntityScopeChanges = useCallback((action, label = "continue") => {
    if (!draftEntityDeploymentDirtyRef.current) {
      action?.();
      return;
    }
    pendingDiscardActionRef.current = action || null;
    setPendingDiscardAction({ label });
  }, []);
  const runPendingDiscardAction = useCallback(() => {
    const action = pendingDiscardActionRef.current;
    pendingDiscardActionRef.current = null;
    setPendingDiscardAction(null);
    restoreSelectedEntityDeploymentDraft();
    action?.();
  }, [restoreSelectedEntityDeploymentDraft]);
  const cancelPendingDiscardAction = useCallback(() => {
    pendingDiscardActionRef.current = null;
    setPendingDiscardAction(null);
  }, []);

  useEffect(() => {
    if (!rows.length) {
      setSelectedEntityDeploymentKey("");
      applyCleanEntityDeploymentDraft(null);
      return;
    }

    const currentRow = rows.find((row, index) =>
      getEntityDeploymentSelectionKey(row, index) === selectedEntityDeploymentKey
    );
    const nextRow = currentRow || rows[0];
    const nextKey = rows.reduce((match, row, index) =>
      row === nextRow ? getEntityDeploymentSelectionKey(row, index) : match
    , "");
    setSelectedEntityDeploymentKey(nextKey);
    const draftSelectionKey = getEntityDeploymentMatchKey(draftEntityDeploymentRef.current);
    if (!draftEntityDeploymentDirtyRef.current || normalizeCompare(draftSelectionKey) !== normalizeCompare(nextKey)) {
      applyCleanEntityDeploymentDraft(nextRow);
    }
  }, [applyCleanEntityDeploymentDraft, destination, getEntityDeploymentSelectionKey, normalizedInitialDeploymentFilter, rows, selectedEntityDeploymentKey]);

  const selectEntityDeployment = useCallback((row, index) => {
    const nextKey = getEntityDeploymentSelectionKey(row, index);
    if (nextKey === selectedEntityDeploymentKey) return;
    if (draftEntityDeploymentDirtyRef.current) {
      setPendingEntityDeploymentSelection({ row, index });
      return;
    }
    setSelectedEntityDeploymentKey(nextKey);
    applyCleanEntityDeploymentDraft(row);
    setSaveError("");
  }, [applyCleanEntityDeploymentDraft, getEntityDeploymentSelectionKey, selectedEntityDeploymentKey]);

  const discardChangesAndSelectPendingEntityDeployment = useCallback(() => {
    const pendingSelection = pendingEntityDeploymentSelection;
    setPendingEntityDeploymentSelection(null);
    if (!pendingSelection?.row) return;
    setSelectedEntityDeploymentKey(getEntityDeploymentSelectionKey(pendingSelection.row, pendingSelection.index));
    applyCleanEntityDeploymentDraft(pendingSelection.row);
    setSaveError("");
  }, [applyCleanEntityDeploymentDraft, getEntityDeploymentSelectionKey, pendingEntityDeploymentSelection]);

  const updateDraftEntityDeployment = useCallback((value, fieldName, updatedItem) => {
    draftEntityDeploymentDirtyRef.current = true;
    const nextDraft = {
      ...(draftEntityDeploymentRef.current || draftEntityDeployment || {}),
      ...(updatedItem || {}),
      ...(fieldName ? { [fieldName]: value } : {})
    };
    draftEntityDeploymentRef.current = nextDraft;
    setDraftEntityDeployment((current) => ({
      ...(current || {}),
      ...(updatedItem || {}),
      ...(fieldName ? { [fieldName]: value } : {})
    }));
  }, [draftEntityDeployment]);

  useEffect(() => {
    if (!draftEntityDeploymentDirtyRef.current) draftEntityDeploymentRef.current = draftEntityDeployment;
  }, [draftEntityDeployment]);

  const openEntityDeploymentPickerGuarded = useCallback(() => {
    requestDiscardEntityScopeChanges(() => onOpenEntityDeploymentPicker?.(), "add Entity Scope rows");
  }, [onOpenEntityDeploymentPicker, requestDiscardEntityScopeChanges]);

  const saveSelectedEntityDeployment = useCallback(async (updatedItem = null) => {
    const nextDraft = {
      ...(draftEntityDeploymentRef.current || draftEntityDeployment || selectedEntityDeployment || {}),
      ...(updatedItem || {})
    };
    if (!nextDraft || !getEntityDeploymentRenderKey(nextDraft)) return;
    setSaving(true);
    setSaveError("");
    try {
      const response = await onSaveEntityDeployment?.(nextDraft);
      if (response !== undefined) {
        applyCleanEntityDeploymentDraft(nextDraft);
      } else {
        setSaveError("Entity scope could not be saved.");
      }
    } catch (saveFailure) {
      setSaveError(saveFailure?.message || "Entity scope could not be saved.");
    } finally {
      setSaving(false);
    }
  }, [applyCleanEntityDeploymentDraft, draftEntityDeployment, onSaveEntityDeployment, selectedEntityDeployment]);

  const selectedActionDisabled = !draftEntityDeployment || saving;
  const selectedEntityScopeDisabled = draftEntityDeployment ? !isRecordActive(draftEntityDeployment) : false;
  const toggleSelectedEntityScopeEnabled = useCallback(() => {
    const currentDraft = draftEntityDeploymentRef.current || draftEntityDeployment;
    if (!currentDraft) return;
    saveSelectedEntityDeployment({
      ...currentDraft,
      IsActive: selectedEntityScopeDisabled
    });
  }, [draftEntityDeployment, saveSelectedEntityDeployment, selectedEntityScopeDisabled]);
  useEffect(() => {
    if (!onEntityScopeActionsChange) return undefined;

    onEntityScopeActionsChange({
      busyAction: saving ? "entity-scope-save" : "",
      confirmDiscardChanges: requestDiscardEntityScopeChanges,
      actions: [
        {
          key: "entity-scope-save",
          label: saving ? "Saving..." : "Save",
          iconKey: "save",
          loading: saving,
          disabled: selectedActionDisabled,
          title: draftEntityDeployment ? "Save the selected Entity Scope row." : "Select an Entity Scope row to save.",
          onClick: () => saveSelectedEntityDeployment(draftEntityDeployment)
        },
        {
          key: selectedEntityScopeDisabled ? "entity-scope-enable" : "entity-scope-disable",
          label: selectedEntityScopeDisabled ? "Enable" : "Disable",
          iconKey: selectedEntityScopeDisabled ? "success" : "undo",
          disabled: selectedActionDisabled,
          title: draftEntityDeployment
            ? selectedEntityScopeDisabled
              ? "Enable this Entity Scope row for runtime use."
              : "Disable this Entity Scope row for runtime use."
            : "Select an Entity Scope row to enable or disable.",
          onClick: toggleSelectedEntityScopeEnabled
        },
        {
          key: "entity-scope-search-export",
          label: "Search/Export",
          iconKey: "search",
          disabled: selectedActionDisabled,
          title: draftEntityDeployment ? "Search or export the selected Entity Object." : "Select an Entity Scope row to search or export.",
          onClick: () => requestDiscardEntityScopeChanges(() => onSearchEntityDeploymentEntity?.(draftEntityDeployment), "open Search/Export")
        },
        {
          key: "entity-scope-schedule",
          label: "Schedule",
          iconKey: "clock",
          disabled: selectedActionDisabled,
          title: draftEntityDeployment ? "Open Schedule for the selected Entity Scope row." : "Select an Entity Scope row to schedule.",
          onClick: () => requestDiscardEntityScopeChanges(() => onOpenEntityDeploymentSchedule?.(draftEntityDeployment), "open Schedule")
        },
        {
          key: "entity-scope-manage",
          label: "Manage",
          iconKey: "settings",
          disabled: selectedActionDisabled,
          title: draftEntityDeployment ? "Manage the selected Entity Object." : "Select an Entity Scope row to manage.",
          onClick: () => requestDiscardEntityScopeChanges(() => onManageEntityDeploymentEntity?.(draftEntityDeployment), "open Manage")
        },
        {
          key: "entity-scope-map",
          label: "Map",
          iconKey: "mapping",
          disabled: selectedActionDisabled,
          title: draftEntityDeployment ? "Map the selected Entity Object." : "Select an Entity Scope row to map.",
          onClick: () => requestDiscardEntityScopeChanges(() => onMapEntityDeploymentEntity?.(draftEntityDeployment), "open Map")
        }
      ]
    });

    return () => onEntityScopeActionsChange(null);
  }, [
    draftEntityDeployment,
    onEntityScopeActionsChange,
    onManageEntityDeploymentEntity,
    onMapEntityDeploymentEntity,
    onOpenEntityDeploymentSchedule,
    requestDiscardEntityScopeChanges,
    onSearchEntityDeploymentEntity,
    saveSelectedEntityDeployment,
    saving,
    selectedActionDisabled,
    selectedEntityScopeDisabled,
    toggleSelectedEntityScopeEnabled
  ]);

  if (loading) return <ResourceStateControl loading loadingText="Loading Entity Deployment Scope..." />;
  if (error && !destination) return <ResourceStateControl tone="warning" title="Entity Deployment Scope unavailable" message={error} />;

  const leftPane = (
    <ResourceCatalogListControl
      className="resource-workspace-entity-scope-list"
      summary={`${activeRows.toLocaleString()} active / ${rows.length.toLocaleString()} total`}
      items={rows}
      getItemKey={(row, index) => getEntityDeploymentSelectionKey(row, index)}
      ariaLabel="Entity Scope rows"
      emptyMessage="No Entity Scope rows have been generated yet."
      emptyFilteredMessage="No Entity Scope rows match the current search."
      search={{
        fieldMetadata: buildFilterTextBoxField("DestinationEntityScopeSearch", "Search entity scope...", "Search entity scope"),
        value: searchTerm,
        onChange: (nextValue) => setSearchTerm(nextValue || ""),
        matchesItem: (row, value) => !normalizeCompare(value)
          || normalizeCompare(getEntityDeploymentDisplayName(row)).includes(normalizeCompare(value))
          || normalizeCompare(getString(row, "EntityObjectName", "entityObjectName")).includes(normalizeCompare(value))
          || normalizeCompare(getString(row, "ObjectName", "objectName")).includes(normalizeCompare(value))
      }}
      actions={(
        <ButtonControl
          type="standard-small"
          title={destination ? "Generate Entity Deployment Scope rows." : "No linked Destination is available."}
          aria-label="Add Entity Deployments"
          onClick={openEntityDeploymentPickerGuarded}
          disabled={saving || !destination}
        >
          <IconControl iconKey="add" title="Add Entity Deployments" width="1rem" height="1rem" />
        </ButtonControl>
      )}
      renderItem={(row, index) => {
        const entityName = getEntityDeploymentDisplayName(row);
        const deployment = deploymentById.get(normalizeCompare(getString(row, "DeploymentId", "deploymentId", "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId")));
        const entityDeploymentKey = getEntityDeploymentSelectionKey(row, index);
        const selected = entityDeploymentKey === selectedEntityDeploymentKey;
        const disabled = !isRecordActive(row);
        return (
          <ResourceCatalogRow
            title={entityName}
            subtitle={`Deployment: ${deployment ? getDeploymentDisplayName(deployment, destination) : "Not set"}`}
            titleAttribute={entityName}
            className={`resource-workspace-entity-scope-row${selected ? " resource-catalog-row--selected" : ""}${disabled ? " resource-workspace-entity-scope-row--disabled" : ""}`}
            onClick={() => selectEntityDeployment(row, index)}
            metaItems={[
              { key: "status", className: "resource-catalog-row__meta-item--status", content: getEntityDeploymentStatusLabel(row) },
              !isWarehouse
                ? { key: "packaging", className: "resource-catalog-row__meta-item--detail", content: getFilePackagingModeLabel(getString(row, "FilePackagingMode", "filePackagingMode")) || "Default packaging" }
                : null
            ].filter(Boolean)}
            actions={(
              <span
                className="resource-workspace-entity-scope-row__delete"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <ButtonControl
                  type="standard-small"
                  title={`Delete ${entityName}`}
                  aria-label={`Delete ${entityName}`}
                  disabled={saving}
                  onClick={(event) => {
                    event?.stopPropagation?.();
                    onDeleteEntityDeployment?.(row);
                  }}
                >
                  <IconControl iconKey="delete" title={`Delete ${entityName}`} width="0.95rem" height="0.95rem" />
                </ButtonControl>
              </span>
            )}
          />
        );
      }}
    />
  );

  const middlePane = draftEntityDeployment ? (
    <div className="resource-workspace-entity-scope-editor">
      <div className="resource-workspace-entity-scope-editor__header resource-workspace-section-title resource-workspace-section-title--embedded">
        <h3>{selectedEntityName}</h3>
        <p>{selectedDeployment ? getDeploymentDisplayName(selectedDeployment, destination) : "Deployment not set"}</p>
      </div>
      {saveError ? <div className="resource-workspace-access-note resource-workspace-access-note--error" role="alert">{saveError}</div> : null}
      <div
        className="resource-workspace-entity-scope-editor__form"
        data-testid="entity-scope-editor-form"
        data-metadata-name={entityDeploymentFormMetadata?.Name || ""}
        data-field-names={entityDeploymentVisibleEditorFields.map((field) => field.FieldName).join(",")}
      >
        <FormControl
          className="resource-workspace-entity-scope-editor__metadata-form"
          metadata={entityDeploymentFormMetadata}
          formFields={entityDeploymentEditorFields}
          itemData={draftEntityDeployment}
          disabled={saving}
          disableFooter={true}
          disableMinWidth={true}
          tabLayout="top"
          onChange={updateDraftEntityDeployment}
        />
      </div>
    </div>
  ) : (
    <ResourceStateControl message="Select or add an Entity Scope row." />
  );

  return (
    <>
      <React.Suspense fallback={<ResourceStateControl loading loadingText="Loading Entity Scope..." />}>
        <EntityScopeAIPanel
          open
          presentation="inline"
          title="Entity Scope"
          className="resource-workspace-entity-scope-designer"
          chatEnabled={false}
          chatPaneToggleEnabled={false}
          chatPane="right"
          chatPaneCollapseMode="closed"
          chatPaneStartOpen={false}
          splitPaneStorageKey={`entity-scope-designer:no-operation:${normalizedInitialDeploymentFilter || getId(destination) || "default"}`}
          initialRightPct={0.76}
          enableContentDragHiding={false}
          minLeft={280}
          minRight={460}
          left={leftPane}
          right={middlePane}
        />
      </React.Suspense>
      <DialogControl
        controlOrigin="entity-scope-discard-unsaved"
        title="Discard Unsaved Changes"
        openDialog={Boolean(pendingEntityDeploymentSelection || pendingDiscardAction)}
        okText="Discard Changes"
        cancelText="Stay"
        onOk={pendingEntityDeploymentSelection ? discardChangesAndSelectPendingEntityDeployment : runPendingDiscardAction}
        onCancel={() => {
          setPendingEntityDeploymentSelection(null);
          cancelPendingDiscardAction();
        }}
        onX={() => {
          setPendingEntityDeploymentSelection(null);
          cancelPendingDiscardAction();
        }}
      >
        <div className="resource-workspace-delete-dialog">
          <p>
            You have unsaved changes to <strong>{getEntityDeploymentDisplayName(draftEntityDeploymentRef.current || draftEntityDeployment)}</strong>.
          </p>
          <p>
            {pendingEntityDeploymentSelection
              ? "Changing Entity Scope rows will discard those changes."
              : `${pendingDiscardAction?.label || "Continuing"} will discard those changes.`}
          </p>
        </div>
      </DialogControl>
    </>
  );
};

const getEndpointDeploymentRows = (destination, defaults) =>
  getDestinationDeployments(destination, defaults);

const getEndpointEntityEndpointRows = (deployment) =>
  getArray(deployment, "EntityEndpoints", "entityEndpoints")
    .map((row) => ({
      ...row,
      __DeploymentName: getString(deployment, "Key", "key", "SystemName", "systemName", "DeploymentName", "deploymentName"),
      __DeploymentId: getDeploymentId(deployment)
    }));

const hasDeploymentWideNativeQuery = (deployment) => {
  const mode = Number(getValue(deployment, "WarehouseNativeQueryMode", "warehouseNativeQueryMode"));
  return mode === 2;
};

const getEndpointRowKey = (row, index) =>
  getString(row, "UniqueId", "uniqueId", "_id", "id") || `${getString(row, "__DeploymentId") || "deployment"}-${index}`;

const appendVersionLabel = (name, version) => {
  if (!name || !version) return name || "";
  const normalizedName = normalizeCompare(name);
  const normalizedVersion = normalizeCompare(version);
  return normalizedVersion && normalizedName.includes(normalizedVersion) ? name : `${name} (${version})`;
};

const getEndpointScopeModeLabel = (row, typeLabel) => {
  const isNativeQueryRow = getString(row, "__EndpointScopeRowType") === "native-query";
  if (isNativeQueryRow) return typeLabel;
  const executionMode = String(getString(row, "ExecutionMode", "executionMode") || "");
  if (executionMode === "1") return "Source";
  if (executionMode === "2") return "Warehouse";
  return getString(row, "ExecutionMode", "executionMode") || "Configured";
};

export const ResourceConfigurationEndpointScopeScreen = ({
  destinationState,
  initialDeploymentFilter = "all",
  lockDeploymentFilter = false,
  panelTitle = "Endpoint Scope",
  onEditEntityEndpoint,
  onDeleteEntityEndpoint
}) => {
  const { destination, defaults, entityObjects = [], loading = false, error = "" } = destinationState || {};
  const resourceDomainContext = useResourceDomainContext();
  const clientMgr = resourceDomainContext?.managers?.client;
  const hasClientMgr = Boolean(clientMgr);
  const clientMgrRef = useRef(clientMgr);
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedInitialDeploymentFilter = initialDeploymentFilter || "all";
  const [deploymentFilter, setDeploymentFilter] = useState(normalizedInitialDeploymentFilter);
  const [schemaScopeFilter, setSchemaScopeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [resolvedClientShortName, setResolvedClientShortName] = useState("");
  const [routeDialog, setRouteDialog] = useState({
    open: false,
    title: "",
    routes: [],
    copiedUrl: ""
  });
  const deployments = getEndpointDeploymentRows(destination, defaults);
  const routeClientDeployment = deployments[0] || null;
  const routeClientLookupKey = [
    getString(destination, "ClientShortName", "clientShortName"),
    getString(destination, "ClientId", "clientId"),
    getString(routeClientDeployment, "ClientShortName", "clientShortName"),
    getString(routeClientDeployment, "ClientId", "clientId")
  ].join("|");

  useEffect(() => {
    setDeploymentFilter(normalizedInitialDeploymentFilter);
  }, [normalizedInitialDeploymentFilter]);

  useEffect(() => {
    clientMgrRef.current = clientMgr;
  }, [clientMgr]);

  useEffect(() => {
    let isCancelled = false;
    const directClientShortName = resolveClientShortName(destination, routeClientDeployment);
    if (directClientShortName || !hasClientMgr) {
      setResolvedClientShortName((previous) => previous === directClientShortName ? previous : directClientShortName);
      return () => {
        isCancelled = true;
      };
    }

    resolveClientShortNameAsync({
      endpoint: destination,
      deployment: routeClientDeployment,
      clientMgr: clientMgrRef.current
    }).then((clientShortName) => {
      if (!isCancelled) {
        setResolvedClientShortName((previous) => previous === (clientShortName || "") ? previous : (clientShortName || ""));
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [hasClientMgr, routeClientLookupKey]);

  if (loading) return <ResourceStateControl loading loadingText="Loading Endpoint Scope..." />;
  if (error && !destination) return <ResourceStateControl tone="warning" title="Endpoint Scope unavailable" message={error} />;

  const schemaScopeSchemaRecords = getSchemaScopeSchemaRecords(destinationState, destination);
  const allEntityRows = deployments.flatMap((deployment) =>
    getEndpointEntityEndpointRows(deployment).map((row) => {
      const schemaScopeIdentifier = getEndpointRowSchemaScopeIdentifier(deployment, row);
      const scope = getEndpointDeploymentSchemaScopes(deployment)
        .find((entry) => normalizeCompare(getString(entry, "SchemaScopeIdentifier", "schemaScopeIdentifier")) === normalizeCompare(schemaScopeIdentifier));
      const scopeSchemaName = getSchemaScopeDisplaySchemaLabel(schemaScopeSchemaRecords, scope)
        || "Schema metadata missing";
      return {
        ...row,
        SchemaScopeIdentifier: getString(row, "SchemaScopeIdentifier", "schemaScopeIdentifier") || schemaScopeIdentifier,
        __SchemaScopeIdentifier: schemaScopeIdentifier,
        __SchemaScopeName: scopeSchemaName
      };
    })
  ).sort((left, right) =>
    normalizeCompare(getString(left, "__SchemaScopeIdentifier")).localeCompare(normalizeCompare(getString(right, "__SchemaScopeIdentifier")))
    || getString(left, "EntityName", "entityName", "DisplayName", "displayName").localeCompare(getString(right, "EntityName", "entityName", "DisplayName", "displayName"))
  );
  const entityRows = lockDeploymentFilter && normalizedInitialDeploymentFilter !== "all"
    ? allEntityRows.filter((row) =>
      normalizeCompare(getString(row, "__DeploymentId")) === normalizeCompare(normalizedInitialDeploymentFilter)
    )
    : allEntityRows;
  const hideNativeQueryTab = hasMcpOnlyEndpointSurface(deployments);
  const entityObjectById = new Map();
  (Array.isArray(entityObjects) ? entityObjects : []).forEach((entityObject) => {
    [
      getId(entityObject),
      getString(entityObject, "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId")
    ].filter(Boolean).forEach((id) => {
      entityObjectById.set(normalizeCompare(id), entityObject);
    });
  });
  const allNativeRows = hideNativeQueryTab ? [] : deployments
    .filter(hasDeploymentWideNativeQuery)
    .flatMap((deployment, deploymentIndex) =>
      getEndpointDeploymentSchemaScopes(deployment).map((scope, scopeIndex) => {
        const schemaScopeIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
        const scopeSchemaName = getSchemaScopeDisplaySchemaLabel(schemaScopeSchemaRecords, scope)
          || "Schema metadata missing";
        return {
          UniqueId: `${getDeploymentId(deployment) || `native-${deploymentIndex}`}:${schemaScopeIdentifier || scopeIndex}`,
          Name: getString(deployment, "Key", "key", "SystemName", "systemName") || "Deployment",
          WarehouseNativeQueryMode: getString(deployment, "WarehouseNativeQueryMode", "warehouseNativeQueryMode"),
          SchemaScopeIdentifier: schemaScopeIdentifier,
          __SchemaScopeIdentifier: schemaScopeIdentifier,
          __SchemaScopeName: scopeSchemaName,
          __DeploymentName: getString(deployment, "Key", "key", "SystemName", "systemName"),
          __DeploymentId: getDeploymentId(deployment),
          IsActive: isRecordActive(deployment),
          IsDeleted: false
        };
      })
    );
  const nativeRows = lockDeploymentFilter && normalizedInitialDeploymentFilter !== "all"
    ? allNativeRows.filter((row) =>
      normalizeCompare(getString(row, "__DeploymentId")) === normalizeCompare(normalizedInitialDeploymentFilter)
    )
    : allNativeRows;
  const deploymentOptions = [
    { Text: "All Deployments", Value: "all" },
    ...deployments.map((deployment, index) => ({
      Text: getDeploymentDisplayName(deployment, destination),
      Value: getDeploymentId(deployment) || getDeploymentRenderKey(deployment, defaults, index)
    })).filter((option) => option.Value)
  ];
  const schemaScopeOptions = [
    { Text: "All Schema Scopes", Value: "all" },
    ...deployments
      .flatMap((deployment) => getEndpointDeploymentSchemaScopes(deployment))
      .reduce((options, scope) => {
        const scopeIdentifier = getString(scope, "SchemaScopeIdentifier", "schemaScopeIdentifier");
        if (!scopeIdentifier || options.some((option) => normalizeCompare(option.Value) === normalizeCompare(scopeIdentifier))) {
          return options;
        }
        options.push({ Text: scopeIdentifier, Value: scopeIdentifier });
        return options;
      }, [])
  ];
  const matchesDeploymentFilter = (row, value) => {
    if (!value || value === "all") return true;
    return normalizeCompare(getString(row, "__DeploymentId")) === normalizeCompare(value);
  };
  const matchesSchemaScopeFilter = (row, value) => {
    if (!value || value === "all") return true;
    return normalizeCompare(getString(row, "SchemaScopeIdentifier", "schemaScopeIdentifier", "__SchemaScopeIdentifier")) === normalizeCompare(value);
  };
  const matchesSearch = (row, value) => {
    const normalized = normalizeCompare(value);
    if (!normalized) return true;
    return normalizeCompare(getEndpointScopeRowTitle(row, "")).includes(normalized)
      || normalizeCompare(getString(row, "EntityName", "entityName", "ModelName", "modelName", "Name", "name")).includes(normalized)
      || normalizeCompare(getString(row, "__DeploymentName")).includes(normalized)
      || normalizeCompare(getString(row, "SchemaScopeIdentifier", "schemaScopeIdentifier", "__SchemaScopeIdentifier")).includes(normalized)
      || normalizeCompare(getString(row, "ExecutionMode", "executionMode")).includes(normalized);
  };
  const filters = !lockDeploymentFilter ? [{
    key: "deployment",
    fieldMetadata: buildSelectListField("EndpointScopeDeployment", "", deploymentOptions, "All Deployments", "Filter by deployment"),
    value: deploymentFilter,
    onChange: (nextValue) => setDeploymentFilter(nextValue || "all"),
    matchesItem: matchesDeploymentFilter
  }, {
    key: "schema-scope",
    fieldMetadata: buildSelectListField("EndpointScopeSchemaScope", "", schemaScopeOptions, "All Schema Scopes", "Filter by Schema Scope"),
    value: schemaScopeFilter,
    onChange: (nextValue) => setSchemaScopeFilter(nextValue || "all"),
    matchesItem: matchesSchemaScopeFilter
  }] : [];
  const statusFilterConfig = {
    fieldMetadata: buildSelectListField("EndpointScopeStatus", "", SOURCE_CATALOG_STATUS_OPTIONS, "Please Select", "Filter Endpoint Scope"),
    value: statusFilter,
    onChange: (nextValue) => setStatusFilter(nextValue || "active"),
    matchesItem: (row, value) => matchesStatusFilter(row, value)
  };
  const search = {
    fieldMetadata: buildFilterTextBoxField("EndpointScopeSearch", "Search endpoint scope...", "Search endpoint scope"),
    controlType: "field",
    value: searchTerm,
    onChange: (nextValue) => setSearchTerm(nextValue || ""),
    matchesItem: matchesSearch
  };
  const getDeploymentForEndpointRow = (row) =>
    deployments.find((deployment, index) =>
      normalizeCompare(getDeploymentId(deployment)) === normalizeCompare(getString(row, "__DeploymentId"))
      || normalizeCompare(getDeploymentRenderKey(deployment, defaults, index)) === normalizeCompare(getString(row, "__DeploymentId"))
    ) || null;

  const getEndpointScopeRowTitle = (row, typeLabel) => {
    const isNativeQueryRow = getString(row, "__EndpointScopeRowType") === "native-query";
    if (isNativeQueryRow) {
      return getString(row, "Name", "name") || getString(row, "__DeploymentName") || typeLabel;
    }

    const entityObjectId = getString(row, "EntityObjectId", "entityObjectId");
    const linkedEntityObject = entityObjectById.get(normalizeCompare(entityObjectId));
    const linkedTitle = getDisplayName(
      linkedEntityObject,
      getString(linkedEntityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name")
    );
    const linkedVersion = getString(linkedEntityObject, "Version", "version", "EntityObjectVersion", "entityObjectVersion");
    const rowTitle = getEndpointEntityCardTitle(row)
      || getString(row, "EntityObjectDisplayName", "entityObjectDisplayName", "EntityDisplayName", "entityDisplayName")
      || getString(row, "EntityObjectName", "entityObjectName", "ObjectName", "objectName")
      || getString(row, "EntityName", "entityName", "ModelName", "modelName", "Name", "name");
    const rowVersion = getString(row, "EntityObjectVersion", "entityObjectVersion", "Version", "version");
    return appendVersionLabel(linkedTitle || rowTitle, linkedVersion || rowVersion)
      || getString(row, "EntityObjectId", "entityObjectId", "DataModelId", "dataModelId")
      || typeLabel;
  };

  const openRouteDialog = (row) => {
    const deployment = getDeploymentForEndpointRow(row);
    const clientShortName = resolveClientShortName(destination, deployment) || resolvedClientShortName;
    const deploymentBaseUrl = buildEndpointDeploymentBaseUrl(destination, deployment, clientShortName);
    const isNativeQueryRow = getString(row, "__EndpointScopeRowType") === "native-query";
    const schemaScopeIdentifier = getString(row, "SchemaScopeIdentifier", "schemaScopeIdentifier", "__SchemaScopeIdentifier");
    const routes = isNativeQueryRow && deploymentBaseUrl
      ? [{
        title: "Warehouse Native Query",
        methods: "POST",
        url: `${deploymentBaseUrl}/${buildEndpointPathSegment(schemaScopeIdentifier)}/warehouse/query`,
        description: "Schema-scoped deployment-wide read-only Native warehouse query route."
      }]
      : buildEndpointEntityRouteCatalog(destination, deployment, row, clientShortName);
    setRouteDialog({
      open: true,
      title: getEndpointScopeRowTitle(row, getString(row, "Name", "name")),
      routes,
      copiedUrl: ""
    });
  };

  const closeRouteDialog = () => {
    setRouteDialog({
      open: false,
      title: "",
      routes: [],
      copiedUrl: ""
    });
  };

  const copyRouteUrl = async (url) => {
    await copyTextToClipboard(url);
    setRouteDialog((current) => ({ ...current, copiedUrl: url }));
    window.setTimeout(() => {
      setRouteDialog((current) => current.copiedUrl === url ? { ...current, copiedUrl: "" } : current);
    }, 1600);
  };

  const renderScopeRow = (row, typeLabel, editable = false) => {
    const title = getEndpointScopeRowTitle(row, typeLabel);
    const deployment = getDeploymentForEndpointRow(row);
    const endpointConfigLocked = deployment && !isSchemaDestinationConfigEditable(deployment);
    const isNativeQueryRow = getString(row, "__EndpointScopeRowType") === "native-query";
    const showRouteUi = !isMcpOnlyEndpointDeployment(deployment);
    const rowSchemaScopeIdentifier = getString(row, "SchemaScopeIdentifier", "schemaScopeIdentifier", "__SchemaScopeIdentifier");
    const routeCount = editable && showRouteUi
      ? (
        isNativeQueryRow
          ? (buildEndpointDeploymentBaseUrl(destination, deployment, resolveClientShortName(destination, deployment) || resolvedClientShortName) && rowSchemaScopeIdentifier ? 1 : 0)
          : buildEndpointEntityRouteCatalog(destination, deployment, row, resolveClientShortName(destination, deployment) || resolvedClientShortName).length
      )
      : 0;
    const deploymentBaseUrl = buildEndpointDeploymentBaseUrl(destination, deployment, resolveClientShortName(destination, deployment) || resolvedClientShortName);
    const routePreviewPath = showRouteUi && isNativeQueryRow
      ? (deploymentBaseUrl && rowSchemaScopeIdentifier ? getEndpointRoutePath(`${deploymentBaseUrl}/${buildEndpointPathSegment(rowSchemaScopeIdentifier)}/warehouse/query`) : "")
      : showRouteUi
        ? getEndpointRoutePath(buildEndpointEntityRouteCatalog(destination, deployment, row, resolveClientShortName(destination, deployment) || resolvedClientShortName)[0]?.url)
        : "";
    const rowActive = isRecordActive(row);
    const rowOnline = rowActive && isSchemaDestinationOnline(deployment);
    const statusLabel = !rowActive ? "Inactive" : rowOnline ? "Online" : "Active";
    const scopeEditLabel = endpointConfigLocked ? "View" : "Edit";
    const rowScopeLabel = rowSchemaScopeIdentifier || "Scope metadata missing";
    const metaItems = [
      { key: "type", className: "resource-catalog-row__meta-item--detail", content: typeLabel },
      ...(!isNativeQueryRow
        ? [{ key: "mode", className: "resource-catalog-row__meta-item--status", content: getEndpointScopeModeLabel(row, typeLabel) }]
        : []),
      { key: "scope", className: "resource-catalog-row__meta-item--detail", content: `Scope: ${rowScopeLabel}` },
      { key: "status", className: rowOnline ? "resource-catalog-row__meta-item--status" : "resource-catalog-row__meta-item--detail", content: statusLabel }
    ];
    return (
      <ResourceCatalogRow
        title={title}
        subtitle={routePreviewPath || `Scope: ${rowScopeLabel} / Deployment: ${getString(row, "__DeploymentName") || "Not set"}`}
        titleAttribute={title}
        metaItems={metaItems}
        actions={editable ? (
          <>
            {showRouteUi ? (
              <ButtonControl
                type="standard-small"
                disabled={!routeCount}
                title={routeCount ? "View generated route URLs." : "Routes require a client route, deployment key, and entity route."}
                aria-label={`View routes for ${title}`}
                onClick={() => openRouteDialog(row)}
              >
                <EndpointRouteIcon />
              </ButtonControl>
            ) : null}
            {!isNativeQueryRow ? (
              <>
                <ButtonControl
                  type="standard-small"
                  title={`${scopeEditLabel} ${title}`}
                  aria-label={`${scopeEditLabel} ${title}`}
                  onClick={() => onEditEntityEndpoint?.(endpointConfigLocked ? { ...row, __EndpointScopeViewOnly: true } : row)}
                >
                  {endpointConfigLocked ? (
                    <IconControl iconKey="view" title={`${scopeEditLabel} ${title}`} width="1.5rem" height="1.5rem" />
                  ) : (
                    <IconControl iconKey="edit" title={`${scopeEditLabel} ${title}`} width="1.5rem" height="1.5rem" />
                  )}
                </ButtonControl>
                <ButtonControl
                  type="standard-small"
                  disabled={endpointConfigLocked}
                  title={endpointConfigLocked ? "Endpoint scope settings are view-only while online." : `Delete ${title}`}
                  aria-label={`Delete ${title}`}
                  onClick={() => onDeleteEntityEndpoint?.(row)}
                >
                  <IconControl iconKey="delete" title={`Delete ${title}`} width="1.5rem" height="1.5rem" />
                </ButtonControl>
              </>
            ) : null}
          </>
        ) : null}
      />
    );
  };

  return (
    <>
      <ResourceCatalogPanel
        title={panelTitle}
        summary={`${entityRows.length.toLocaleString()} entity / ${nativeRows.length.toLocaleString()} native`}
        ariaLabel={panelTitle}
        idPrefix="resource-endpoint-scope"
        tabs={[
          {
            key: "entity-endpoints",
            title: "Entity Endpoints",
            content: (
              <ResourceCatalogListTab
                items={entityRows}
                getItemKey={getEndpointRowKey}
                ariaLabel="Endpoint entity rows"
                emptyMessage="No entity endpoints are configured."
                emptyFilteredMessage="No entity endpoints match the current filters."
                search={search}
                filters={filters}
                statusFilter={statusFilterConfig}
                className="resource-catalog-list-tab--endpoint-scope"
                renderItem={(row) => renderScopeRow(row, "Entity Endpoint", true)}
              />
            )
          },
          ...(!hideNativeQueryTab ? [{
            key: "native-query",
            title: "Native Query",
            content: (
              <ResourceCatalogListTab
                items={nativeRows}
                getItemKey={getEndpointRowKey}
                ariaLabel="Endpoint native query rows"
                emptyMessage="No deployment-wide native query capability is enabled."
                emptyFilteredMessage="No native query rows match the current filters."
                search={search}
                filters={filters}
                statusFilter={statusFilterConfig}
                className="resource-catalog-list-tab--endpoint-scope"
                renderItem={(row) => renderScopeRow({ ...row, __EndpointScopeRowType: "native-query" }, "Native Query", true)}
              />
            )
          }] : [])
        ]}
      />
      <DialogControl
        controlOrigin="endpoint-entity-routes"
        openDialog={routeDialog.open}
        title="Endpoint Routes"
        subTitle={routeDialog.title}
        okText="Close"
        excludeCancel
        onOk={closeRouteDialog}
        onCancel={closeRouteDialog}
        onX={closeRouteDialog}
      >
        <div className="resource-workspace-linked-record-add-dialog">
          {routeDialog.routes.length ? routeDialog.routes.map((route) => (
            <div key={`${route.title}-${route.url}`} className="resource-workspace-endpoint-route">
              <div className="resource-workspace-endpoint-route__header">
                <div>
                  <strong>{route.title}</strong>
                  <span>{route.methods}</span>
                </div>
                <ButtonControl
                  type="standard-small"
                  title={`Copy ${route.title} URL`}
                  aria-label={`Copy ${route.title} URL`}
                  onClick={() => copyRouteUrl(route.url)}
                >
                  <IconControl iconKey={routeDialog.copiedUrl === route.url ? "success" : "contenttab"} title={`Copy ${route.title} URL`} width="0.95rem" height="0.95rem" />
                </ButtonControl>
              </div>
              <code>{route.url}</code>
              <p>{route.description}</p>
            </div>
          )) : (
            <p>No generated routes are available for this entity endpoint.</p>
          )}
        </div>
      </DialogControl>
    </>
  );
};

export const ResourceConfigurationEndpointAuthenticationScreen = ({
  destinationRecord,
  destinationState,
  configuration,
  selectedResource,
  fixedDeploymentId = "",
  saving = false,
  registerStepController,
  onSaved
}) => {
  const {
    destination,
    defaults,
    loading = false,
    error = ""
  } = destinationState || {};

  const endpointDefaults = useMemo(() => ({
    ...DESTINATION_RESOURCE_DEFAULTS.schemaAPI,
    ...(defaults || {}),
    managerKey: "schemaAPI",
    itemName: "SchemaAPI",
    destinationKind: "SchemaAPI",
    destinationItemName: "SchemaAPI",
    destinationLabel: "Endpoint",
    destinationStepTitle: "Endpoint",
    deploymentItemName: "SchemaAPIDeployment",
    deploymentCollectionField: "Deployments",
    deploymentCollection: "Deployments",
    label: "Endpoint",
    isEndpoint: true
  }), [defaults]);
  const destinationId = getId(destination) || getString(destinationRecord, "ItemId", "itemId");
  const configurationId = getId(configuration);
  const clientResourceId = getString(configuration, "ClientResourceId", "clientResourceId")
    || selectedResource?.id
    || getString(selectedResource, "ClientResourceId", "clientResourceId");
  const clientShortCode = getString(
    selectedResource,
    "ClientShortCode",
    "clientShortCode",
    "ClientShortName",
    "clientShortName",
    "ShortName",
    "shortName",
    "SystemName",
    "systemName"
  );
  const context = useMemo(() => ({
    wizard: {
      ResourceConfiguration: configuration,
      ResourceConfigurationId: configurationId,
      ClientResourceId: clientResourceId,
      ClientShortCode: clientShortCode,
      LinkedRecords: [{
        ItemName: endpointDefaults.itemName || "SchemaAPI",
        Relationship: "SchemaDestination",
        ItemId: destinationId
      }]
    },
    values: {
      DestinationId: destinationId,
      ResourceConfigurationId: configurationId,
      ClientResourceId: clientResourceId
    },
    productResourceDefaults: endpointDefaults,
    readOnly: false,
    saving
  }), [clientResourceId, clientShortCode, configuration, configurationId, destinationId, endpointDefaults, saving]);

  if (loading) return <ResourceStateControl loading loadingText="Loading Endpoint Authentication..." />;
  if (error && !destination) return <ResourceStateControl tone="warning" title="Endpoint Authentication unavailable" message={error} />;
  if (!destinationId) {
    return (
      <ResourceStateControl
        tone="warning"
        title="Endpoint Authentication unavailable"
        message="No linked Endpoint record is available for this Resource Configuration."
      />
    );
  }

  return (
    <EndpointAuthenticationConfiguratorStep
      context={context}
      onChange={() => undefined}
      managementMode
      fixedDeploymentId={fixedDeploymentId}
      registerStepController={registerStepController}
      onSaved={onSaved}
    />
  );
};

export const ResourceConfigurationEndpointAuthenticationPanel = ({
  open,
  deployment,
  destinationRecord,
  destinationState,
  configuration,
  selectedResource,
  onSaved,
  onClose
}) => {
  const controllerRef = useRef(null);
  const [controllerReady, setControllerReady] = useState(false);
  const [panelSaving, setPanelSaving] = useState(false);
  const deploymentId = getString(deployment, "UniqueId", "uniqueId", "_id", "id") || getDeploymentId(deployment);
  const deploymentName = getString(deployment, "DeploymentName", "deploymentName")
    || getDeploymentDisplayName(deployment, destinationState?.destination);

  const registerStepController = useCallback((controller) => {
    controllerRef.current = controller || null;
    setControllerReady(Boolean(controller));
  }, []);

  const saveAuthentication = useCallback(async () => {
    const controller = controllerRef.current;
    if (!controller?.prepareSave || controller.isBusy || panelSaving) return;

    setPanelSaving(true);
    try {
      await controller.prepareSave();
      onClose?.();
    } catch {
      // The authentication control renders the actionable save error in the panel body.
    } finally {
      setPanelSaving(false);
    }
  }, [onClose, panelSaving]);

  return (
    <PanelControl
      open={Boolean(open && deploymentId)}
      title={`MCP Access: ${deploymentName}`}
      onClose={onClose}
      mapToClass="core-layout"
      width={760}
      minWidth={480}
      maxWidth={960}
      storageKey="resource-workspace-endpoint-authentication-panel-width"
      className="resource-workspace-endpoint-authentication-panel"
      contentClassName="resource-workspace-endpoint-authentication-panel__content"
    >
      <div className="resource-workspace-endpoint-authentication-panel__layout">
        <div className="resource-workspace-endpoint-authentication-panel__body">
          <ResourceConfigurationEndpointAuthenticationScreen
            destinationRecord={destinationRecord}
            destinationState={destinationState}
            configuration={configuration}
            selectedResource={selectedResource}
            fixedDeploymentId={deploymentId}
            saving={panelSaving}
            registerStepController={registerStepController}
            onSaved={onSaved}
          />
        </div>
        <footer className="resource-workspace-endpoint-authentication-panel__footer">
          <ButtonControl
            type="cancel"
            className="resource-workspace-endpoint-authentication-panel__footer-button"
            disabled={panelSaving}
            onClick={onClose}
          >
            Cancel
          </ButtonControl>
          <ButtonControl
            type="ok"
            className="resource-workspace-endpoint-authentication-panel__footer-button"
            disabled={!controllerReady || panelSaving}
            onClick={saveAuthentication}
          >
            {panelSaving ? "Saving..." : "Save"}
          </ButtonControl>
        </footer>
      </div>
    </PanelControl>
  );
};
